/**
 * English message catalog (the base/default locale). English is the COMPLETE catalog:
 * every key the interface uses must exist here, so it is the fallback for every other
 * locale. Keys are namespaced by surface.
 */
import type { Messages } from "../types";

export const en: Messages = {
  // Sidebar navigation - group labels
  "nav.group.build":   "Build",
  "nav.group.ship":    "Ship",
  "nav.group.operate": "Operate",
  "nav.group.account": "Account",
  "nav.group.help":    "Help Center",

  // Sidebar navigation - item titles
  "nav.item.dashboard":     "Dashboard",
  "nav.item.projects":      "Projects",
  "nav.item.deployments":   "Deployments",
  "nav.item.mobileBuilds":  "Mobile Builds",
  "nav.item.marketplace":   "Marketplace",
  "nav.item.plugins":       "Plugins",
  "nav.item.integrations":  "Integrations",
  "nav.item.connectors":    "Integrations",
  "nav.item.creatorStudio": "Creator Studio",
  "nav.item.creativeStudio": "Creative Studio",
  "nav.item.library":       "Library",
  "nav.item.billing":       "Billing",
  "nav.item.settings":      "Settings",
  "nav.item.helpCenter":    "Help Center",
  "nav.item.capabilities":  "Capabilities",
  "nav.item.whatsNew":      "What's New",
  "nav.item.documents":     "Documents",
  "nav.item.engineering":   "Engineering",

  // Sidebar footer - live platform status
  "footer.status.nominal":  "systems nominal",
  "footer.status.degraded": "degraded",
  "footer.status.outage":   "outage detected",

  // User avatar menu
  "menu.billing":              "Billing",
  "menu.settings":             "Settings",
  "menu.platformIntelligence": "Platform Intelligence",
  "menu.signOut":              "Sign Out",
  "menu.admin":                "Admin",
  "menu.userFallback":         "User",
  "menu.language":             "Language",

  // Settings - group titles are NOT a rendered surface (the layout uses flat chips), so they
  // intentionally have no catalog keys - avoids implying coverage that is not rendered.

  // Settings - item labels
  "settings.item.profile":       "Profile",
  "settings.item.security":      "Security",
  "settings.item.notifications": "Notifications",
  "settings.item.billing":       "Billing & Credits",
  "settings.item.apiKeys":       "API Keys",
  "settings.item.connectors":    "Integrations",
  "settings.item.integrations":  "Integrations",
  "settings.item.aiPreferences": "AI Behavior",
  "settings.item.memory":        "Memory",
  "settings.item.export": "Data Export",
  "settings.item.audit": "Audit Log",
  "settings.item.privacy":       "Privacy & Data",
  "settings.item.systemStatus":  "System Status",
  "settings.item.language":      "Interface Language",
  "settings.item.appearance":    "Appearance",
  "settings.item.team":          "Team",
  "settings.item.webhooks":      "Webhooks",
  "settings.item.helpCenter":    "Help Center",
  "settings.item.dangerZone":    "Danger Zone",

  // Settings - header
  "settings.title":    "Settings",
  "settings.subtitle": "Configure your workspace, security, AI, integrations, billing, and developer settings.",

  // Language panel
  "language.title":           "Interface language",
  "language.description":     "Choose the language for the Nuvra interface. This does not change the language of the apps you build.",
  "language.label":           "Language",
  "language.region.global":   "Global",
  "language.region.europe":   "Europe",
  "language.region.africa":   "Africa",
  "language.region.asia":     "Asia",
  "language.region.middle_east": "Middle East",
  "language.region.americas": "Americas",
  "language.search":          "Search languages",
  "language.noMatch":         "No languages match your search.",
  "language.current":          "Current",
  "language.autoSaved":         "Saved automatically",
  "language.clearSearch":       "Clear language search",
  "language.trySearch":         "Try a language name or ISO code.",
  "language.scope":             "Applies to the Nuvra interface only. The language of software you build is controlled separately.",
  
  
  
  
  
  

  // 404 page
  "notFound.kicker":    "Route Not Found",
  "notFound.body":      "This route doesn't exist in the factory. Navigate back to a known system.",
  "notFound.goBack":    "Go back",
  "notFound.dashboard": "Dashboard",

  // Auth - shared
  "auth.backToHome":  "Back to home",
  "auth.backToLogin": "Back to login",
  "auth.email":       "Email",
  "auth.password":    "Password",
  "auth.or":          "or",
  "auth.error":       "Error",
  "auth.signIn":      "Sign In",

  // Auth - login
  "auth.login.welcome":     "Welcome back",
  "auth.login.subtitle":    "Access your AI software factory",
  "auth.login.forgot":      "Forgot password?",
  "auth.login.noAccount":   "Don't have an account?",
  "auth.login.signUpLink":  "Sign up",
  "auth.login.failedTitle": "Login failed",

  // Auth - signup
  "auth.signup.title":           "Create your account",
  "auth.signup.subtitle":        "Your AI software factory awaits",
  "auth.signup.fullName":        "Full Name",
  "auth.signup.namePlaceholder": "Your name",
  "auth.signup.createAccount":   "Create Account",
  "auth.signup.haveAccount":     "Already have an account?",
  "auth.signup.loginLink":       "Login",
  "auth.signup.failedTitle":     "Signup failed",
  "auth.signup.createdTitle":    "Account created!",
  "auth.signup.createdBody":     "You are signed in. Your workspace is ready.",

  // Auth - forgot password
  "auth.forgot.title":          "Forgot password",
  "auth.forgot.subtitleSent":   "Check your email for a reset link",
  "auth.forgot.subtitlePrompt": "Enter your email and we'll send you a reset link",
  "auth.forgot.sentBefore":     "We sent a password reset link to",
  "auth.forgot.sentAfter":      ". The link will expire in 60 minutes.",
  "auth.forgot.tryAnother":     "Try another email",
  "auth.forgot.send":           "Send reset link",
  "auth.forgot.remember":       "Remember your password?",

  // Auth - reset password
  "auth.reset.titleSuccess":    "Password updated",
  "auth.reset.title":           "Set new password",
  "auth.reset.subtitleSuccess": "Redirecting you to login...",
  "auth.reset.subtitle":        "Choose a strong password for your account",
  "auth.reset.successBody":     "Your password has been successfully reset. You'll be redirected to the login page shortly.",
  "auth.reset.verifying":       "Verifying your reset link...",
  "auth.reset.newPassword":     "New password",
  "auth.reset.confirmPassword": "Confirm password",
  "auth.reset.submit":          "Reset password",
  "auth.reset.mismatchTitle":   "Passwords don't match",
  "auth.reset.mismatchBody":    "Please make sure both passwords are identical.",
  "auth.reset.tooShortTitle":   "Password too short",
  "auth.reset.tooShortBody":    "Password must be at least 8 characters.",
  "auth.reset.failedTitle":     "Reset failed",

  // Deploy page (header, project selector, environment strip, tab bar, deploy tab, relative time)
  "deploy.header.kicker":   "Deployment Operations",
  "deploy.header.title":    "Deploy",
  "deploy.header.subtitle": "Preview, promote, publish, observe, rollback.",
  "deploy.header.refresh":  "Refresh",
  "deploy.loadingProjects": "Loading projects...",
  "deploy.noProjects":      "No projects found. Create one from the Dashboard first.",
  "deploy.env.preview":     "Preview",
  "deploy.env.production":  "Production",
  "deploy.env.domain":      "Domain",
  "deploy.env.live":        "Live",
  "deploy.env.none":        "None",
  "deploy.tab.deploy":      "Deploy",
  "deploy.tab.domains":     "Domains",
  "deploy.tab.publishing":  "Publishing",
  "deploy.tab.lineage":     "Lineage",
  "deploy.tab.visibility":  "Visibility",
  "deploy.hosted.liveTitle":         "Your app is live",
  "deploy.hosted.notPublishedTitle": "Not published yet",
  "deploy.hosted.subtitle":          "Nuvra hosts your app on its runtime: instant publish, no build step.",
  "deploy.hosted.manageVisibility":  "Manage visibility",
  "deploy.hosted.liveUrl":           "Live URL",
  "deploy.hosted.vanityUrl":         "Vanity URL",
  "deploy.hosted.liveHint":          "Re-build in the project workspace to update the live app instantly. Use the Visibility tab to manage the vanity URL or make the app private.",
  "deploy.hosted.notPublishedHint":  "Build your app in the project workspace, then publish it from the Publish toggle to make it publicly accessible at a shareable URL.",
  "deploy.time.justNow":    "just now",
  "deploy.time.minutesAgo": "{n}m ago",
  "deploy.time.hoursAgo":   "{n}h ago",

  // Projects page (the founder's project list)
  "projects.title":             "Projects",
  "projects.loading":           "Loading your projects",
  "projects.countOne":          "1 project",
  "projects.countOther":        "{count} projects",
  "projects.new":               "New Project",
  "projects.search":            "Search projects",
  "projects.loadingList":       "Loading projects",
  "projects.emptyTitle":           "Start your first Nuvra project.",
  "projects.emptyBody":            "Describe what you want to build and Nuvra will take it through the real engineering pipeline. Your project becomes the durable workspace for it.",
  "projects.page.eyebrow":             "Workspace",
  "projects.page.subtitle":            "Your projects, live deployments, and engineering activity in one place.",
  "projects.page.searchPlaceholder":   "Search projects by name or description",
  "projects.page.sort":                "Sort",
  "projects.page.sortRecent":          "Recent",
  "projects.page.sortName":            "Name",
  "projects.page.sortStatus":          "Status",
  "projects.page.gridView":            "Grid view",
  "projects.page.listView":            "List view",
  "projects.page.readyBadge":          "Ready to build",
  "projects.page.createProject":       "Create project",
  "projects.page.mobileBuild":         "Mobile build",
  "projects.page.yourProjects":        "Your projects",
  "projects.page.yourProjectsBody":    "Open a workspace to continue engineering where you left off.",
  "projects.page.clearSearch":         "Clear search",
  "projects.page.tabAll":              "All",
  "projects.page.tabActive":           "Building",
  "projects.page.tabDeployed":         "Live",
  "projects.page.tabDraft":            "Draft / Attention",
  "projects.page.noMatchTitle":        "No matching projects",
  "projects.page.noMatchBody":         "Try a different name, description, or filter.",
  "projects.page.usage":               "Usage",
  "projects.page.usageBody":           "Account-level usage at a glance. Individual build costs are available from the corresponding build.",
  "projects.page.currentAccount":      "Current account",
  "projects.page.creditsRemaining":    "Credits remaining",
  "projects.page.liveProjects":        "Live projects",
  "projects.page.autoTopUp":           "Auto top-up",
  "projects.page.on":                  "On",
  "projects.page.off":                 "Off",
  "projects.page.plan":                "Plan",
  "projects.page.manage":              "Manage",
  "projects.quick.newLabel":           "New Project",
  "projects.quick.newDesc":            "Start from scratch",
  "projects.quick.mobileLabel":        "Mobile Build",
  "projects.quick.mobileDesc":         "iOS and Android",
  "projects.quick.marketLabel":        "Marketplace",
  "projects.quick.marketDesc":         "Browse extensions",
  "projects.quick.deployLabel":        "Deploy",
  "projects.quick.deployDesc":         "Publish your project",
  "projects.page.statProjects":        "Projects",
  "projects.page.statLive":            "Live",
  "projects.page.statBuilding":        "Building",
  "projects.page.statAttention":       "Needs attention",
  "projects.page.statCredits":         "Credits",
  "projects.page.footerNav":           "Footer navigation",
  "projects.page.footerDocs":          "Docs",
  "projects.page.footerSupport":       "Support",
  "projects.page.footerStatus":        "Status",
  "projects.page.footerPricing":       "Pricing",
  "projects.noMatch":           "No projects match \"{query}\".",
  "projects.openLive":          "Open live",
  "projects.actions":           "Project actions",
  "projects.openWorkspace":     "Open workspace",
  "projects.shareProject":      "Share project",
  "projects.deleteProject":     "Delete project",
  "projects.deleteTitle":       "Delete this project?",
  "projects.deleteBody":        "\"{name}\" will be permanently removed. This cannot be undone.",
  "projects.cancel":            "Cancel",
  "projects.delete":            "Delete",
  "projects.deleting":          "Deleting...",
  "projects.status.building":   "Building",
  "projects.status.live":       "Live",
  "projects.status.error":      "Build failed",
  "projects.status.draft":      "Draft",
  "projects.error.title":   "We could not load your projects",
  "projects.error.body":    "This is usually temporary. Your projects are safe. Retry now, or reload the page.",
  "projects.error.details": "Technical details",
  "projects.error.retry":   "Retry",
  "projects.time.justNow":      "Just now",
  "projects.time.minAgo":       "{min} min ago",
  "projects.time.hourAgo":      "{h} hour ago",
  "projects.time.hoursAgo":     "{h} hours ago",
  "projects.time.yesterday":    "Yesterday",
  "projects.time.daysAgo":      "{d} days ago",
  "projects.toast.deleteFail":  "Could not delete project",
  "projects.toast.deleted":     "Project deleted",
  "projects.toast.deletedBody": "\"{name}\" was removed.",

  // Billing page (plan, usage, payments, auto top-up)
  "billing.title":            "Billing",
  "billing.subtitle": "Your plan, your credits, and every payment, tracked in real time.",
  "billing.transparency":     "Payments, credits, and usage are tracked in real-time with full transparency.",
  "billing.tab.plan":         "Plan",
  "billing.tab.usage":        "Usage",
  "billing.tab.payments":     "Payments",
  "billing.tab.settings":     "Settings",
  "billing.planBadge":        "{tier} Plan",
  "billing.creditsAvailable": "{balance} credits available",
  "billing.viewPlans":        "View Plans",
  "billing.creditsUsedUp":    "Your credits are used up. Upgrade to continue building.",
  "billing.upgradeTitle":     "Upgrade your plan",
  "billing.perMonth":         "/mo",
  "billing.paymentsInstant":  "Payments process instantly once the gateway keys are configured in your deployment.",
  "billing.creditBalance":    "Credit Balance",
  "billing.live":             "Live",
  "billing.creditsUsed":      "{used} credits used",
  "billing.paidBillingOptionsTitle": "Extra credits are available on paid plans",
  "billing.paidBillingOptionsBody": "Upgrade to a paid plan to purchase additional credits when you need them.",
  "billing.paymentPreferences": "Payment preferences",
  "billing.paymentPreferencesBody": "Payment options become available after you choose a paid plan.",
  "billing.autoTopupBody": "Automatically purchase the selected credit package when your balance reaches the threshold.",
  "billing.acuUpgrade": "Your included credits are exhausted. Upgrade to a paid plan to continue building.",
  "billing.lowBalance":       "Your credit balance is low. Top up or enable auto top-up.",
  "billing.txHistory":        "Transaction History",
  "billing.securePayments":   "All payments are securely processed. Credits are added only after payment confirmation.",
  "billing.topupPaidTitle":   "Top-ups are a paid-plan feature",
  "billing.topupPaidBody":    "Upgrade to a paid plan to buy extra credits anytime during your billing cycle.",
  "billing.seePlans":         "See Plans",
  "billing.autoTopup":        "Auto Top-Up",
  "billing.autoPaidTitle":    "Auto top-up is a paid-plan feature",
  "billing.autoPaidBody":     "Upgrade to a paid plan to enable automatic credit top-ups.",
  "billing.enableAuto":       "Enable auto top-up",
  "billing.thresholdZero":    "When balance hits 0",
  "billing.thresholdBelow":   "When below {n} credits",
  "billing.choosePackage":    "Choose a top-up package",
  "billing.packageOption":    "{amount} credits · {price}",
  "billing.autoTriggerNote":  "Auto top-up only triggers after successful payment confirmation.",
  "billing.loading":          "Loading...",
  "billing.pastDueTitle": "Your payment did not go through",
  "billing.pastDueBody": "Your plan ends in {days} unless the payment clears. Nothing you have built will be deleted.",
  "billing.pastDueToday": "Your plan ends today unless the payment clears. Nothing you have built will be deleted.",
  "billing.pastDueDays": "{n} days",
  "billing.pastDueOneDay": "1 day",
  "billing.updateCard": "Update payment method",
  "billing.cancelPlan": "Cancel plan",
  "billing.cancelling": "Cancelling...",
  "billing.creditsLabel": "Credits available",
  "billing.usedOfTotal": "{used} used of {total}",
  "billing.planPrice": "${price}/month",
  "billing.planFree": "Free",

  // Marketplace page (buyer/discovery storefront)
  "marketplace.eyebrow":            "Marketplace",
  "marketplace.heroTitle":          "Production software, ready to ship",
  "marketplace.heroSubtitle":       "Templates, plugins, components, and AI workflows built on Nuvra. Install in one step and make them your own.",
  "marketplace.sell":               "Sell on Nuvra",
  "marketplace.trust.verifiedLabel":  "Verified creators",
  "marketplace.trust.verifiedSub":    "Identity-checked sellers",
  "marketplace.trust.secureLabel":    "Secure checkout",
  "marketplace.trust.secureSub":      "Payments handled by the gateway",
  "marketplace.trust.licensingLabel": "Clear licensing",
  "marketplace.trust.licensingSub":   "Every listing states its license",
  "marketplace.prefill.loading":    "Preparing listing metadata from your project.",
  "marketplace.prefill.error":      "Metadata prefill is unavailable. Fill in listing details manually.",
  "marketplace.prefill.ready":      "Listing metadata ready",
  "marketplace.prefill.issueOne":   "{n} issue",
  "marketplace.prefill.issueOther": "{n} issues",
  "marketplace.prefill.readyBadge": "ready",
  "marketplace.prefill.dismiss":    "dismiss",
  "marketplace.prefill.titleLabel": "Title",
  "marketplace.prefill.shortDesc":  "Short description",
  "marketplace.prefill.continue":   "Continue in Creator Studio",
  "marketplace.featured":           "Featured",
  "marketplace.trending":           "Trending",
  "marketplace.trendingTitle":      "What is gaining momentum",
  "marketplace.trendingNote":       "Ranked from real published marketplace activity over the last 7 days.",
  "marketplace.mostUsed":           "Most adopted",
  "marketplace.mostUsedTitle":      "Used by real buyers",
  "marketplace.mostUsedNote":       "Ordered by completed marketplace adoptions. No empty or invented counts.",

  "marketplace.searchPlaceholder":  "Search the marketplace",
  "marketplace.sort.newest":        "Newest",
  "marketplace.sort.popular":       "Most popular",
  "marketplace.sort.rating":        "Top rated",
  "marketplace.sort.priceAsc":      "Price: low",
  "marketplace.sort.priceDesc":     "Price: high",
  "marketplace.filterAll":          "All",
  "marketplace.error":              "The catalog could not be loaded. Try again in a moment.",
  "marketplace.retry":              "Retry",
  "marketplace.resultOne":          "{total} result",
  "marketplace.resultOther":        "{total} results",
  "marketplace.resultsFor":         " for \"{search}\"",
  "marketplace.free":               "Free",
  "marketplace.perMonth":           "/mo",
  "marketplace.license.standard":   "Standard license",
  "marketplace.license.extended":   "Extended license",
  "marketplace.license.enterprise": "Enterprise license",
  "marketplace.aria.verified":      "Verified creator",
  "marketplace.aria.sort":          "Sort listings",
  "marketplace.aria.featured":      "Featured listing",
  "marketplace.aria.close":         "Close",
  "marketplace.by":                 "by {name}",
  "marketplace.installOne":         "{n} install",
  "marketplace.installOther":       "{n} installs",
  "marketplace.reviews.loading":    "Loading reviews.",
  "marketplace.reviews.none":       "No reviews yet.",
  "marketplace.reviews.title":      "Reviews",
  "marketplace.toast.added":        "Added to your library",
  "marketplace.toast.addedBody":    "{title} is ready to use.",
  "marketplace.toast.failed":       "Could not complete",
  "marketplace.toast.failedBody":   "Please try again.",
  "marketplace.toast.signInBody":   "Please sign in and try again.",
  "marketplace.getFree":            "Get for free",
  "marketplace.buy":                "Buy {price}",
  "marketplace.empty.noResults":    "No results for \"{search}\"",
  "marketplace.empty.open":         "The marketplace is open",
  "marketplace.empty.tryDifferent": "Try a different search term or clear the filter.",
  "marketplace.empty.beFirst":      "Be the first to publish. Build a project and list it in Creator Studio.",
  "marketplace.empty.clear":        "Clear search",
  "marketplace.empty.build":        "Build something",

  // Mobile Builds page (native packaging dashboard)
  "mobile.eyebrow":              "Native Builds",
  "mobile.title":                "Mobile Builds",
  "mobile.subtitle":             "Produce native Android and iOS artifacts from your project.",
  "mobile.projectAria":          "Project",
  "mobile.target.aab.label":     "Android App Bundle",
  "mobile.target.aab.desc":      "Signed bundle for the Google Play Store.",
  "mobile.target.apk.label":     "Android APK",
  "mobile.target.apk.desc":      "Signed APK for direct install and sideloading.",
  "mobile.target.ipa.label":     "iOS App Archive",
  "mobile.target.ipa.desc":      "Signed archive for App Store submission.",
  "mobile.target.twa.label":     "Trusted Web Activity",
  "mobile.target.twa.desc":      "Your web app as a Play Store package.",
  "mobile.github.connectedAs":   "Connected as",
  "mobile.github.connectedTail": ". Builds are saved to your repository and packaged on CI.",
  "mobile.github.connect":       "Connect GitHub to package signed native builds from your own repository.",
  "mobile.build":                "Build",
  "mobile.buildAgain":           "Build again",
  "mobile.buildStarted":         "Build started",
  "mobile.track":                "Track",
  "mobile.err.selectProject":    "Select a project first.",
  "mobile.err.connectGithub":    "Connect GitHub to build.",
  "mobile.err.buildFirst":       "Build the project before producing artifacts.",
  "mobile.err.couldNotStart":    "Could not start the build.",
  "mobile.noProjectsHint":       "Build a project first, then return here to produce native artifacts.",
  "mobile.historyTitle":         "Build History",
  "mobile.succeeded":            "{n} succeeded",
  "mobile.failed":               "{n} failed",
  "mobile.running":              "{n} running",
  "mobile.loadingHistory":       "Loading build history",
  "mobile.noBuilds":             "No builds yet.",
  "mobile.noBuildsHint":         "Pick a target above to produce your first signed artifact.",
  "mobile.startProject":         "Start a project",
  "mobile.time.dayAgo":          "{d}d ago",
  "mobile.time.hourAgo":         "{h}h ago",
  "mobile.time.minAgo":          "{m}m ago",
  "mobile.time.justNow":         "just now",
  "mobile.comingSoon":           "Coming soon",
  "mobile.openProject": "Open project",
  "mobile.projectContext": "Build from project",
  "mobile.noProjectSelected": "Select a project to continue",
  "mobile.stat.builds": "Builds",
  "mobile.stat.successRate": "Success rate",
  "mobile.stat.lastBuild": "Last build",
  "mobile.readinessTitle": "Build readiness",
  "mobile.readinessSubtitle": "Nuvra will validate your project and account before a native build is admitted. No build state is shown until the real backend records it.",
  "mobile.github.ready": "GitHub connected",
  "mobile.github.required": "GitHub connection required",
  "mobile.projectSelected": "Project selected",
  "mobile.buildCenterEyebrow": "Native delivery",
  "mobile.buildCenterTitle": "Choose a build target",
  "mobile.buildCenterSubtitle": "Create the native artifact your project needs. Each action uses the existing governed build and billing path.",
  "mobile.filter.all": "All",
  "mobile.filter.android": "Android",
  "mobile.filter.ios": "iOS",
  "mobile.refresh": "Refresh build history",
  "mobile.recentBuildsTitle": "Recent builds",
  "mobile.artifactReady": "Artifact recorded",
  "mobile.intelligenceTitle": "Build intelligence & governance",
  "mobile.intelligenceSubtitle": "Real observability, version history, signing provenance, and build governance for this project.",

  // Platform Intelligence page (systems visibility)
  "platform.title":                    "Platform Intelligence",
  "platform.subtitle":                 "Live visibility into orchestration, release, build, and audit systems",
  "platform.live":                     "Live",
  "platform.eyebrow":                  "Operator console",
  "platform.adminOnly":                "Operator visibility. This console is not part of the customer product.",
  "platform.verifying":                "Verifying access...",
  "platform.loading":                  "Loading...",
  "platform.stat.projects":            "Projects",
  "platform.stat.releases":            "Releases",
  "platform.stat.builds":              "Mobile Builds",
  "platform.stat.audit":               "Audit Entries",
  "platform.releaseEvents.title":      "Release Pipeline Events",
  "platform.releaseEvents.empty":      "No release events recorded yet.",
  "platform.releaseEvents.emptyHint":  "Events appear here when releases are triggered.",
  "platform.audit.title":              "Release Audit Log",
  "platform.audit.empty":              "No audit entries yet.",
  "platform.audit.emptyHint":          "Every release decision, credential use, and submission is recorded here.",
  "platform.mobile.title":             "Mobile Build Queue",
  "platform.mobile.empty":             "No mobile builds recorded yet.",
  "platform.mobile.emptyHint":         "AAB, APK, and IPA builds appear here when triggered from Deploy.",
  "platform.infra.title":              "Infrastructure Systems",
  "platform.infra.active":             "active",

  // What's New page (customer product updates; no operational telemetry)
  "whatsNew.eyebrow":         "Product updates",
  "whatsNew.title":           "What's New",
  "whatsNew.subtitle":        "See what we've shipped, improved, and made safer across Nuvra.",
  "whatsNew.shippedOnly":     "Shipped updates only",
  "whatsNew.latest":          "Latest",
  "whatsNew.seeIncluded":     "See what's included",
  "whatsNew.hideDetails":     "Hide details",
  "whatsNew.customerValue":   "Product changes are described by the outcome they give you, not by Nuvra's internal implementation.",
  "whatsNew.search":          "Search updates…",
  "whatsNew.filter":          "Filter",
  "whatsNew.all":             "All updates",
  "whatsNew.releases":        "New releases",
  "whatsNew.improvements":    "Improvements",
  "whatsNew.security":        "Security",
  "whatsNew.history":         "Product history",
  "whatsNew.earlier":         "Earlier updates",
  "whatsNew.matching":        "Matching updates",
  "whatsNew.update":          "update",
  "whatsNew.updates":         "updates",
  "whatsNew.noMatches":       "No updates match these filters.",
  "whatsNew.clearFilters":    "Clear filters",
  "whatsNew.helpPitch":       "Looking for help using a feature? Open the Help Center for guides, or contact Nuvra Support when you need a person.",
  "whatsNew.helpCenter":      "Help Center",
  "whatsNew.support":         "Support",

  "whatsNew.customerOutcome": "Built for your workflow",
  "whatsNew.filterUpdates":  "Filter product updates",

  // Command Center (dashboard creation surface)
  "cc.prompt":      "What should we build?",
  "cc.eyebrow":     "New project",
  "cc.chipsLabel":  "Start from a direction",
  "cc.flow.describe": "Describe",
  "cc.flow.plan":    "Plan",
  "cc.flow.build":   "Build",
  "cc.flow.ship":    "Ship",
  "cc.promptNamed": "What should we build, {name}?",
  "cc.subtitle":    "Describe the product. Nuvra engineers the system.",
  "cc.degraded":    "Some services degraded",
  "cc.outage":      "Service disruption in progress",
  "cc.viewStatus":  "View status →",
  "cc.recentProjects": "Recent projects",
  "cc.recentProjectsHint": "Continue where you left off.",
  "cc.viewAllProjects": "View all",
  "cc.recentProjectsLoading": "Loading recent projects",
  "cc.projectsLoadErrorTitle": "Your projects could not be loaded",
  "cc.projectsLoadErrorBody": "Your projects are safe. Check your connection and try again.",
  "cc.retryProjects": "Try again",

  // Connectors page (integration catalog)
  "connectors.page.title":               "Integrations",
  "connectors.page.subtitle":            "Connect the services your project actually uses. Credentials stay protected by Nuvra's existing encrypted connector vault and every connection can be tested before use.",
  "connectors.page.project":             "Project",
  "connectors.page.selectProject":       "Select project",
  "connectors.page.noProject":           "No project selected",
  "connectors.page.statConnected":       "Connected",
  "connectors.page.statHealthy":         "Healthy",
  "connectors.page.statAttention":       "Needs attention",
  "connectors.page.search":              "Search integrations, capabilities, or providers",
  "connectors.page.tabAll":              "All",
  "connectors.page.tabConnected":        "Connected",
  "connectors.page.tabAttention":        "Needs attention",
  "connectors.page.clearFilters":        "Clear filters",
  "connectors.page.pickProjectTitle":    "Select a project to connect services",
  "connectors.page.pickProjectBody":     "You can browse the integration catalog now. Connection changes are always scoped to a real Nuvra project.",
  "connectors.page.suggestedTitle":      "Suggested for this project",
  "connectors.page.suggestedBody":       "Derived from capabilities already present in the project's Nuvra schema.",
  "connectors.page.notConnectedYet":     "Not connected yet",
  "connectors.page.noMatchTitle":        "No matching integrations",
  "connectors.page.noMatchBody":         "Try another provider, capability, or category. Nuvra will never invent a connector just to fill this catalog.",
  "connectors.page.securityTitle":       "Connection security",
  "connectors.page.securityBody":        "Nuvra stores connector credentials through its existing protected vault, keeps secrets server-side, scopes connector management to the owning project, and provides a real connectivity test before a service is used.",
  "connectors.page.suggestedBadge":      "Suggested",

  // Collaboration page (live session environment)
  "collab.title":              "Collaboration",
  "collab.conn.connected":     "Connected",
  "collab.conn.reconnecting":  "Reconnecting",
  "collab.conn.disconnected":  "Disconnected",
  "collab.noProjectTitle":     "No project selected",
  "collab.noProjectBody":      "Open a project and navigate to Collaboration to see live session state.",
  "collab.urlParam":           "URL param:",
  "collab.security":           "Security Observability",
  "collab.active":             "active",
  "collab.participants.aria":  "Session participants",
  "collab.participants":       "Participants",
  "collab.activity.aria":      "Activity stream",
  "collab.activity":           "Live Activity",
  "collab.ai.aria":            "AI orchestration",
  "collab.ai":                 "AI Execution",

  // Help Center page (support + documentation)
  "help.eyebrow":          "Support",
  "help.title":            "Help Center",
  "help.subtitle":         "Guides for building, deploying, and operating your products on Nuvra.",
  "help.searchPlaceholder":"Search help articles...",
  "help.noResults":        "No articles match \"{query}\". Try a different term, or email us at",
  "help.stillNeedHelp":    "Still need help?",
  "help.emailPrefix":      "Email our team at",
  "help.emailSuffix":      ". We read every message.",
  "help.systemStatus":     "System Status",
  "help.whatsNew":         "What's New",
  "help.startHere":         "Start here",
  "help.documentation":     "Documentation",
  "help.guides":            "Guides",
  "help.searchResults":     "Search results",
  "help.clearSearch":       "Clear search",
  "help.resources":         "Resources",
  "help.searchHint":        "Search",
  "help.gettingStarted.title": "Getting Started",
  "help.gettingStarted.q1": "How Nuvra works",
  "help.gettingStarted.a1": "Describe the product you want to build. Nuvra creates a project and engineers a real, working application with data models, pages, authentication, and persistent storage. Each further build request refines that same product.",
  "help.gettingStarted.q2": "Projects are the core object",
  "help.gettingStarted.a2": "Every product you build is a project with its own workspace: Overview, Requirements, Architecture, Build Requests, Builds, Files, Environments, and Integrations. Submitting a new prompt from the dashboard starts a new, separate project.",
  "help.building.title": "Building & Projects",
  "help.building.q1": "Submitting a build request",
  "help.building.a1": "Open a project and submit a build request describing the change, feature, fix, or architecture update you want. Nuvra performs the work and records it as a build record showing the analysis, work performed, and the files and results it produced.",
  "help.building.q2": "Reviewing what was built",
  "help.building.a2": "The Overview, Architecture, and Builds sections show your project's pages, data models, routes, and full build history, generated from your real application, not samples.",
  "help.deploying.title": "Deploying & Environments",
  "help.deploying.q1": "Staging and Production",
  "help.deploying.a1": "Each project has Staging and Production environments. New builds land in Staging automatically; you promote to Production when you are ready, and you can roll back to any previous verified release.",
  "help.deploying.q2": "Publishing to the app stores",
  "help.deploying.a2": "Mobile projects can be built natively for Android and iOS and published to Google Play and the Apple App Store, with staged rollouts you control and encrypted handling of your signing credentials.",
  "help.integrations.title": "Integrations",
  "help.integrations.q1": "Connecting services to a project",
  "help.integrations.a1": "Connect integrations such as GitHub and your database from inside a project workspace. Integrations are scoped to that single project, so each product manages its own connections.",
  "help.billing.title": "Billing & Credits",
  "help.billing.q1": "How credits work",
  "help.billing.a1": "Building consumes credits. Your current balance is shown in the workspace header and on the Billing page. When you run low, top up or upgrade your plan to keep building.",
  "help.billing.q2": "Plans and top-ups",
  "help.billing.a2": "Choose a plan that matches your usage, or buy a one-time top-up. Credits are applied as soon as your payment is confirmed.",
  "help.account.title": "Account & Security",
  "help.account.q1": "Two-factor authentication",
  "help.account.a1": "Turn on authenticator-app (TOTP) two-factor authentication under Settings → Security to require a second step at sign-in.",
  "help.account.q2": "API keys",
  "help.account.a2": "Create scoped, read-only API keys under Settings → API Keys to read your projects, builds, and deployments programmatically. The secret is shown once at creation.",
  "help.account.q3": "Exporting your data",
  "help.account.a3": "Download a JSON copy of your account data at any time under Settings → Export Center.",
  "help.account.q4": "Audit logs",
  "help.account.a4": "Review an immutable record of actions taken on your account and projects under Settings → Audit Logs.",

  // Magic-link sign-in landing page
  "magicLink.missingToken": "This sign-in link is missing its token. Request a new one.",
  "magicLink.invalid":      "This sign-in link is invalid or has expired.",
  "magicLink.signingIn":    "Signing you in...",
  "magicLink.backToSignIn": "Back to sign in",

  // Share-link join page
  "join.missingToken":   "This share link is missing its token.",
  "join.accessGranted":  "You now have {role} access.",
  "join.invalid":        "This link is invalid or has expired.",
  "join.joining":        "Joining the project...",
  "join.goToDashboard":  "Go to dashboard",

  // Creator Studio (seller operating system)
  "cs.loading":            "Loading Creator Studio.",
  "cs.loadError":          "Creator Studio could not be loaded. Try again in a moment.",
  "cs.retry":              "Retry",
  "cs.eyebrow":            "Creator Studio",
  "cs.sellBlocked":        "Selling is not available on your account right now.",
  "cs.planAndAbove":       "{tier} plan and above",
  "cs.upsellTitle":        "Turn what you build into revenue.",
  "cs.upsellBody":         "Publish to the Nuvra Marketplace, keep 80% of every sale, and get paid on a fixed, automatic schedule.",
  "cs.becomeCreator":      "Become a creator",
  "cs.noFees":             "No upfront fees. No listing fees.",
  "cs.keep80Title":        "Keep 80% of every sale",
  "cs.keep80Sub":          "Your share of every transaction",
  "cs.keep80Body":         "You keep the majority of every sale. No upfront costs and no listing fees, ever.",
  "cs.paidTitle":          "Paid on a fixed schedule",
  "cs.paidSub":            "Automatic, twice a month",
  "cs.scheduleWithPolicy": "Issued automatically on the {dates}. Minimum payout {amount}.",
  "cs.scheduleDefault":    "Issued automatically twice a month.",
  "cs.earnings":           "Earnings",
  "cs.payouts":            "Payouts",
  "cs.yourListings":       "Your listings",
  "cs.affiliate":          "Affiliate",
  "cs.analytics":          "Analytics",
  "cs.verification":       "Verification",
  "cs.noEarningsYet":      "No earnings yet",
  "cs.next":               "Next {date}",
  "cs.automaticSchedule":  "Automatic schedule",
  "cs.nPublished":         "{n} published",
  "cs.nothingPublished":   "Nothing published yet",
  "cs.active":             "Active",
  "cs.notEnrolled":        "Not enrolled",
  "cs.available":          "Available",
  "cs.noAnalytics":        "No analytics yet",
  "cs.verified":           "Verified",
  "cs.inReview":           "In review",
  "cs.notVerified":        "Not verified",
  "cs.insideEyebrow":      "Inside Creator Studio",
  "cs.whatYouUnlock":      "What you unlock with Pro",
  "cs.locked":             "Locked",
  "cs.nProjectsOne":       "{n} project in your workspace",
  "cs.nProjectsOther":     "{n} projects in your workspace",
  "cs.becomeToPublish":    "Become a creator to publish your work on the marketplace.",
  "cs.unlockPublishing":   "Unlock publishing",
  "cs.openOnUpgrade":      "These open the moment you upgrade. Until then, nothing here is active.",
  "cs.controlPlaneTitle":  "Your creator control plane",
  "cs.controlPlaneSub":    "Create, publish, grow, and earn, in one place.",
  "cs.newProject":         "New project",
  "cs.seeYourWork":        "See your work in the marketplace",
  "cs.browseCatalog":      "Browse the public catalog the way buyers do.",
  "cs.openMarketplace":    "Open Marketplace",
  "cs.yourPath":           "Your path",
  "cs.journeyTitle":       "Create, publish, grow, earn",
  "cs.step.create":        "Create",
  "cs.step.publish":       "Publish",
  "cs.step.verify":        "Verify",
  "cs.step.earn":          "Earn",
  "cs.step.createDesc":    "Build a project in your workspace.",
  "cs.step.publishDesc":   "List a build on the marketplace.",
  "cs.step.verifyDesc":    "Verify to enable payouts.",
  "cs.step.earnDesc":      "Earnings arrive after your first sale.",
  "cs.toast.published":      "Published",
  "cs.toast.publishedBody":  "Your listing is now live.",
  "cs.toast.publishFail":    "Could not publish",
  "cs.toast.publishFailBody":"Check the listing details and try again.",
  "cs.toast.removed":        "Removed",
  "cs.toast.removedBody":    "The listing has been removed.",
  "cs.toast.removeFail":     "Could not remove",
  "cs.toast.tryAgain":       "Please try again.",
  "cs.listings":           "Listings",
  "cs.publishFirstBody":   "Publish your first creation to start selling on the marketplace.",
  "cs.publishFirst":       "Publish your first creation",
  "cs.nSold":              "{n} sold",
  "cs.publish":            "Publish",
  "cs.remove":             "Remove",
  "cs.totalInstalls":      "Total installs",
  "cs.totalRevenue":       "Total revenue",
  "cs.totalViews":         "Total views",
  "cs.avgRating":          "Avg rating",
  "cs.noRatings":          "No ratings yet",
  "cs.earningsAppear":     "Earnings will appear here after your first sale.",
  "cs.totalEarned":        "Total earned",
  "cs.pending":            "Pending",
  "cs.paidOut":            "Paid out",
  "cs.nextPayout":         "Next payout",
  "cs.onSchedule":         "On schedule",
  "cs.minimumPayout":      "Minimum payout",
  "cs.issuedOn":           "Issued automatically on the {dates}",
  "cs.issuedDefault":      "Issued automatically on the 15th and the last day of each month",
  "cs.history":            "History",
  "cs.loadingDot":         "Loading.",
  "cs.payoutHistoryEmpty": "Payout history will appear here.",
  "cs.scheduled":          "Scheduled",
  "cs.toast.refRequired":    "Reference required",
  "cs.toast.refRequiredBody":"Add a provider session id or a link you control.",
  "cs.toast.submitted":      "Submitted for review",
  "cs.toast.submittedBody":  "A specialist will confirm.",
  "cs.toast.submitFail":     "Could not submit",
  "cs.verifiedCreator":    "Verified creator",
  "cs.payoutsEnabled":     "Payouts are enabled on your account.",
  "cs.verifInReview":      "Verification in review",
  "cs.confirmShortly":     "We will confirm shortly.",
  "cs.verifyToReceive":    "Verify to receive payouts",
  "cs.completeVerif":      "Complete verification to enable payouts to your account.",
  "cs.refPlaceholder":     "Provider session id or a link you control",
  "cs.aria.kind":          "Provenance kind",
  "cs.aria.project":       "Select a project",
  "cs.submit":             "Submit",
  "cs.toast.affJoined":      "Affiliate program joined",
  "cs.toast.joinFail":       "Could not join",
  "cs.toast.genFail":        "Could not generate link",
  "cs.toast.linkCopied":     "Link copied",
  "cs.affEarnTitle":       "Earn by referring marketplace sales",
  "cs.affEarnBody":        "Join the affiliate program to generate tracked referral links.",
  "cs.joinProgram":        "Join program",
  "cs.clicks":             "Clicks",
  "cs.conversions":        "Conversions",
  "cs.approved":           "Approved",
  "cs.genLink":            "Generate referral link",
  "cs.copyLink":           "Copy link",
  "cs.commissions":        "Commissions",
  "cs.publishingReadiness":"Publishing readiness",

  // Project workspace - build stage labels (the real backend stages over SSE)
  "pw.stage.queued":        "Product",
  "pw.stage.blueprint":     "Architecture",
  "pw.stage.schema":        "Data",
  "pw.stage.generate":      "Engineering",
  "pw.stage.security_scan": "Security",
  "pw.stage.write_files":   "Engineering",
  "pw.stage.validate":      "QA",
  "pw.stage.deploy_pages":  "Release",
  "pw.stage.complete":      "Live",
  "pw.stage.intent":        "Contemplating",
  "pw.stage.constraints":   "Checking",
  "pw.stage.agents":        "Designing",
  "pw.stage.planning":      "Scheming",
  "pw.stage.validation":    "Verifying",
  "pw.stage.explanation":   "Reasoning",
  "pw.stage.enriching":     "Synthesizing",

  // Project workspace - navigation
  "pw.nav.conversation": "Build",
  "pw.nav.overview":     "Overview",
  "pw.nav.requirements": "Requirements",
  "pw.nav.architecture": "Architecture",
  "pw.nav.builds":       "Builds",
  "pw.nav.code":         "Files",
  "pw.nav.analytics":                  "Analytics",
  "pw.analytics.window.today":         "Today",
  "pw.analytics.window.7d":            "7 days",
  "pw.analytics.window.30d":           "30 days",
  "pw.analytics.window.all":           "All time",
  "pw.analytics.visitors":             "Visitors",
  "pw.analytics.pageViews":            "Page views",
  "pw.analytics.deployments":          "Deployments",
  "pw.analytics.builds":               "Builds completed",
  "pw.analytics.successRate":          "Build success",
  "pw.analytics.previous":             "was {n}",
  "pw.analytics.trend.new":            "new",
  "pw.analytics.unavailable":          "Could not be read",
  "pw.analytics.noBuildsYet":          "No builds finished yet",
  "pw.analytics.none":                 "No activity yet",
  "pw.analytics.noneHint":             "Once your app is deployed and people visit it, their visits appear here.",
  "pw.analytics.failed":               "Analytics could not be loaded",
  "pw.analytics.failedHint":           "This is a display problem, not a problem with your app. Try again shortly.",
  "pw.analytics.degraded":             "Some figures could not be read just now, so this view is incomplete. The numbers shown are real; the missing ones are marked.",
  "pw.analytics.computedAt":           "Measured at {time}",
  "pw.nav.environments": "Environments",
  "pw.nav.memory":       "Memory",
  "pw.nav.agent":        "Agent",
  "pw.nav.deployments":  "Deployments",
  "pw.settings.title": "Project settings",
  "pw.settings.subtitle": "Manage the identity and visibility of this project without leaving its workspace.",
  "pw.settings.nameLabel": "Project name",
  "pw.settings.nameHint": "This name appears across your project workspace and project list.",
  "pw.settings.publicLabel": "Make project public",
  "pw.settings.publicHint": "Public projects can be opened at their shared runtime URL. Private projects remain owner-controlled.",
  "pw.settings.identityTitle": "Project identity",
  "pw.settings.identityBody": "Canonical project ID: {id}",
  "pw.settings.save": "Save changes",
  "pw.settings.saving": "Saving…",
  "pw.settings.saved": "Project settings saved",
  "pw.settings.savedBody": "Your project identity and visibility are up to date.",
  "pw.settings.saveFailed": "Could not save project settings",
  "pw.settings.saveFailedBody": "Your changes were not saved. Please try again.",
  "pw.nav.integrations": "Integrations",
  "pw.nav.settings":     "Settings",
  "pw.nav.group.product":  "Your product",
  "pw.nav.group.changes":  "Changes",
  "pw.nav.group.ship":     "Ship",
  "pw.mode.stream":            "Stream",
  "pw.mode.preview":           "Preview",
  "pw.mode.files":             "Files",
  "pw.nav.preview":        "Preview",
  "pw.nav.previewSoon":    "after first build",
  "pw.nav.previewAfterBuild": "Available after your first build",
  "pw.nav.allProjects":  "All projects",
  "pw.nav.buildsOne":    "{n} build",
  "pw.nav.buildsOther":  "{n} builds",
  "pw.nav.originalIntent": "Your brief",
  "pw.plan.button":      "Plan",
  "pw.plan.buttonTitle": "See what this prompt will engineer and its cost, free, before building",
  "pw.plan.cost":        "Build uses {n} credits",
  "pw.plan.buildThis":   "Build this",
  "pw.plan.dismiss":     "Dismiss plan",
  "pw.plan.failed":      "Could not derive the plan. Try again.",
  "pw.plan.entities":    "Data",
  "pw.plan.workflows":   "Workflows",
  "pw.plan.security.standard": "Per-user data isolation with row-level security",
  "pw.plan.security.elevated": "Row-level security, audit trail, and role gates",
  "pw.plan.security.critical": "Strict isolation, full audit trail, and role gates",
  "pw.defaultName":      "New Project",
  "pw.projectLoading": "Opening your project",
  "pw.projectLoadingBody": "Loading the project and restoring its workspace context.",
  "pw.projectNotFoundTitle": "Project not found",
  "pw.projectNotFoundBody": "This project may have been moved, deleted, or you may not have access to it.",
  "pw.projectLoadErrorTitle": "Project could not be loaded",
  "pw.projectLoadErrorBody": "Your project was not changed. Check your connection and try again.",
  "pw.backToProjects": "Back to projects",
  "pw.retryProject": "Try again",

  // Project workspace - files panel
  "pw.files.noBuild":      "No build yet",
  "pw.files.noBuildHint":  "Files will appear here after the first build completes.",
  "pw.files.pagesN":       "Pages ({n})",
  "pw.files.componentsN":  "Components ({n})",
  "pw.files.dataModelsN":  "Data Models ({n})",
  "pw.files.moreComponents": "+{n} more components",

  // Project workspace - overview panel
  "pw.overview.statusBuilding": "Building",
  "pw.overview.statusFailed":   "Last build failed",
  "pw.overview.statusBuilt":    "Built",
  "pw.overview.statusDeployed": "Deployed",
  "pw.overview.statusNotBuilt": "Not built yet",
  "pw.overview.stat.pages":      "Pages",
  "pw.overview.stat.components": "Components",
  "pw.overview.stat.dataModels": "Data models",
  "pw.overview.stat.builds":     "Builds",
  "pw.overview.openLiveApp":     "Open live app",
  "pw.overview.originalRequirement": "Original requirement",

  // Project workspace - architecture panel
  "pw.arch.none":          "No architecture yet",
  "pw.arch.noneHint":      "It appears after the first build completes.",
  "pw.arch.whyBuilt":      "Why Nuvra built this",
  "pw.arch.fieldsN":       "({n} fields)",
  "pw.arch.assumptions":   "Assumptions Nuvra made",
  "pw.arch.youCanChange":  "You can change: {areas}.",
  "pw.arch.whatChanged":   "What changed in this build",
  "pw.arch.routesN":       "Routes ({n})",
  "pw.arch.dataLayerN":    "Data layer ({n} models)",
  "pw.arch.capabilitiesN": "Capabilities ({n})",
  "pw.arch.componentsGeneratedOne":   "{n} component generated.",
  "pw.arch.componentsGeneratedOther": "{n} components generated.",
  "pw.arch.showComponentsOnPage": "Show components on this page",
  "pw.arch.noComponentsOnPage":   "No components on this page",
  "pw.arch.toggleComponents":     "Toggle components",
  "pw.arch.renamed":       "renamed",
  "pw.arch.hiddenNextBuild": "hidden next build",
  "pw.arch.hidden":        "hidden",
  "pw.arch.saveName":      "Save name",
  "pw.arch.savePageName":  "Save page name",
  "pw.arch.renameThisPage": "Rename this page",
  "pw.arch.renamePage":    "Rename page",
  "pw.arch.hiddenRestore": "Hidden: removed from the app on the next build. Click to restore.",
  "pw.arch.hidePage":      "Hide this page so the next build removes it from the app",
  "pw.arch.showPage":      "Show page",
  "pw.arch.hidePageLabel": "Hide page",
  "pw.arch.lockedKeep":    "Locked: future builds keep this page as-is",
  "pw.arch.lockPage":      "Lock this page so future builds do not change it",
  "pw.arch.unlockPage":    "Unlock page",
  "pw.arch.lockPageLabel": "Lock page",
  "pw.arch.compHiddenRestore": "Hidden: removed on the next build. Click to restore.",
  "pw.arch.hideComponent": "Hide this component from the app",
  "pw.arch.showComponent": "Show component",
  "pw.arch.hideComponentLabel": "Hide component",

  // Project workspace - requirements panel
  "pw.req.originalRequirement": "Original requirement",
  "pw.req.noOriginal":     "No original requirement recorded for this project.",
  "pw.req.engineeringN":   "Engineering requests ({n})",
  "pw.req.noRequests":     "No change requests yet. Submit one under Build Requests.",
  "pw.req.showingRecent":  "Showing the {shown} most recent",
  "pw.req.constraintsN":   "Constraints ({n})",
  "pw.req.decisionsN":     "Decisions ({n})",

  // Project workspace - environments panel
  "pw.env.staging":        "Staging",
  "pw.env.production":     "Production",
  "pw.env.deployed":       "Deployed",
  "pw.env.notDeployed":    "Not deployed",
  "pw.env.noUrl":          "No URL",
  "pw.env.none":           "No environments yet",
  "pw.env.noneHint":       "Environments appear after your first build deploys.",
  "pw.env.build":          "Build:",
  "pw.env.lastDeployment": "Last deployment:",
  "pw.env.envVars":        "Env vars:",
  "pw.env.buildNone":      "none",
  "pw.env.never":          "never",

  // Project workspace - activity panel
  "pw.activity.none":      "No activity yet",
  "pw.activity.noneHint":  "Build history will appear here.",
  "pw.activity.buildFailed": "Build failed",

  // Project workspace - memory panel
  "pw.mem.brain.architecture": "Architecture",
  "pw.mem.brain.decision":     "Decisions",
  "pw.mem.brain.constraint":   "Constraints",
  "pw.mem.brain.naming":       "Naming",
  "pw.mem.brain.rationale":    "Rationale",
  "pw.mem.brain.history":      "History",
  "pw.mem.title":          "Project Memory",
  "pw.mem.subtitle":       "Everything Nuvra knows about this project, accumulated across all builds.",
  "pw.mem.originalIntent": "Original intent",
  "pw.mem.noPrompt":       "No prompt recorded",
  "pw.mem.builds":         "Builds",
  "pw.mem.pages":          "Pages",
  "pw.mem.applicationRoutes": "Application routes",
  "pw.mem.detectedCapabilities": "Detected capabilities",
  "pw.mem.emptyBrain":     "As you build, Nuvra remembers this product's decisions, architecture, naming, and constraints here, and feeds them into every future build.",

  // Project workspace - header and status
  "pw.header.clickToRename": "Click to rename",
  "pw.header.projectName": "Project name",
  "pw.identity.edit":               "Edit product identity",
  "pw.identity.title":              "Product identity",
  "pw.identity.productName":        "Product name",
  "pw.identity.saveName":           "Save name",
  "pw.identity.regenerateLogo":     "New logo",
  "pw.identity.confirmedNote":      "You chose this name. Nuvra will never overwrite it.",
  "pw.identity.generatedNote":      "Generated from your product. Edit it and it becomes permanent.",
  "pw.identity.saveFailed":         "Could not save the product identity. Please try again.",
  "pw.status.building":    "Building...",
  "pw.status.buildFailed": "Build failed",
  "pw.status.built":       "Built",
  "pw.status.latestFailed": "Latest build failed",
  "pw.status.draft":       "Draft",
  "pw.status.ready":       "Ready",
  "pw.projectMenu.open": "Open project menu",
  "pw.projectMenu.project": "Project workspace",
  "pw.projectMenu.overview": "Overview",
  "pw.projectMenu.tools": "Project tools",
  "pw.projectMenu.rename": "Rename project",
  "pw.projectMenu.allProjects": "All projects",
  "pw.projectMenu.accountSettings": "Account settings",
  "pw.header.openLiveApp": "Open live app",
  "pw.header.manage":      "Manage",
  "pw.header.manageTitle": "Manage this product",
  "pw.header.codeTitle":   "Browse and download your project's code",
  "pw.header.projectCode": "Project code",
  "pw.header.code":        "Code",
  "pw.header.manageLess":  "Less",
  "pw.header.forkTitle":   "Fork this product into a new project",
  "pw.header.fork":        "Fork",
  "pw.header.notesTitle":  "Engineering Notes",
  "pw.header.notes":       "Notes",
  "pw.header.notesPreset.explain":  "Explain this project",
  "pw.header.notesPreset.next":     "What should I build next?",
  "pw.header.notesPreset.fail":     "Why did my last build fail?",
  "pw.header.notesPreset.analyze":  "Analyze my architecture",
  "pw.header.notesPreset.missing":  "What features am I missing?",
  "pw.header.askPlaceholder": "Ask about this project's architecture, builds, or status...",
  "pw.header.send":        "Send",
  "pw.header.goLive":      "Go live",
  "pw.header.clearNotes":  "Clear engineering notes",

  // Project workspace - empty state and composer
  "pw.empty.title":        "What should we build next?",
  "pw.empty.body":         "Describe a feature, a fix, or a change to the design. Nuvra engineers it into your app.",
  "pw.mobile.readyTap":    "Your product is ready, tap to open",
  "pw.mobile.previousTap": "Latest build failed. Open last working version",
  "pw.mobile.tapToOpen":   "Tap to open your product",
  "pw.mobile.draftTap":    "Open draft, add credit to build it for real",
  "pw.dup.notice":         "You have already built this. Open the existing version or build it again.",
  "pw.dup.openExisting":   "Open existing",
  "pw.dup.buildAgain":     "Build again",
  "pw.dup.dismiss":        "Dismiss",
  "pw.composer.placeholderComplete": "Ask Nuvra to build, change, or improve...",
  "pw.composer.placeholderBuilding": "Nuvra is engineering. Queue your next change while it works...",
  "pw.composer.placeholderNew":      "Describe the product to engineer...",
  "pw.composer.sessionActive": "Building",
  "pw.composer.ariaLabel": "Workspace message composer",
  "pw.composer.dropToAttach": "Drop file to attach",
  "pw.composer.removeImage": "Remove image",
  "pw.composer.hint":      "Enter to send",

  // Project workspace - preview panel
  "pw.preview.appDeployed": "App deployed",
  "pw.preview.builtRunnableTitle": "Your project is built",
  "pw.preview.builtRunnableBody": "This is a playable build that runs as its own page. Deploy it to play live, or open the code to inspect, edit, and download it.",
  "pw.preview.appDeployedBody": "Your app is live. Open it to interact, share, and request edits.",
  "pw.preview.openLiveApp": "Open Live App",
  "pw.preview.prevBuildTitle": "Previous build",
  "pw.preview.prevBuildBody": "Latest build failed. This link opens your last successful version, not the failed one.",
  "pw.preview.live":       "Live",
  "pw.preview.previousVersion": "Previous working version",
  "pw.preview.draft":      "Draft",
  "pw.preview.switchToMobile":  "Switch to mobile view",
  "pw.preview.switchToDesktop": "Switch to desktop view",
  "pw.preview.mobileView":  "Mobile view",
  "pw.preview.desktopView": "Desktop view",
  "pw.preview.copyShareable": "Copy shareable link",
  "pw.preview.copied":     "Copied",
  "pw.preview.share":      "Share",
  "pw.preview.openProduct": "Open your product",
  "pw.preview.open":       "Open",
  "pw.preview.takeLive":   "Take your product live",
  "pw.preview.goLive":     "Go live",
  "pw.preview.reloadProduct": "Reload your product",
  "pw.preview.reload":     "Reload",
  "pw.preview.close":      "Close",
  "pw.preview.lastBuildFailed": "Last build failed. Showing prior version.",
  "pw.preview.rebuild":    "Rebuild",
  "pw.preview.hidePreview": "Hide preview panel",
  "pw.preview.showPreview": "Show preview panel",
  "pw.preview.preview":    "Preview",

  // Project workspace - status text and toasts
  "pw.build.understanding": "Understanding your request...",
  "pw.build.resumeRequest": "Continue building this project from where the previous build stopped.",
  "pw.build.sending":       "Sending to Nuvra AI...",
  "pw.build.designing":     "Planning the build...",
  "pw.build.acuExhausted":  "Credits exhausted.",
  "pw.build.acuTopUp":      "Your free credits are used up. Top up to continue building.",
  "pw.build.noLlm":         "Engineering engine unavailable.",
  "pw.build.noLlmHint":     "No AI provider is configured. Builds are paused until the AI engine is online. No credits were used.",
  "pw.build.planLimit":     "Monthly build limit reached.",
  "pw.build.planLimitHint": "Upgrade your plan to continue building.",
  "pw.build.notAuthed":     "Not authenticated. Please sign in.",
  "pw.build.notAuthedShort": "Not authenticated",
  "pw.build.productLive":   "Your product is live",
  "pw.build.productReady":  "Your product is ready",
  "pw.build.engineeredLive": "Engineering report ready",
  "pw.build.engineered":    "Your app is engineered.",
  "pw.build.scaffoldReady":  "A starting draft is ready. Add credit to engineer the real product.",
  "pw.build.scaffoldTitle":  "This is a draft, not your finished product",
  "pw.build.scaffoldBody":   "Real generation for your prompt needs AI credit. Add credit and Nuvra will engineer the full data model, pages, and logic for what you described.",
  "pw.build.scaffoldCta":    "Add credit to build for real",
  "bfr.failed": "Build failed",
  "bfr.creditBlocked": "This build needs more credits before it can run",
  "bfr.stalled": "Nuvra could not confirm the final build result",
  "bfr.running": "Your build is still running",
  "bfr.completed": "Build completed",
  "bfr.retry": "Try again",
  "bfr.topUp": "Add credits",
  "bfr.refresh": "Check status",
  "bfr.refunded": "Reserved credits were refunded for this failed attempt.",
  "bfr.notUsed": "No credits were used for this attempt.",
  "pw.build.failed":        "Build failed",
  "pw.build.stillBuilding": "Still building. Your build is continuing on the server and will appear here shortly.",
  "pw.build.stalled": "This build did not finish in time.",
  "pw.build.stalledHint": "Nuvra could not confirm the final build result yet. Your work is preserved. Check the status before starting another build.",
  "pw.ask.analyzing":       "Analyzing...",
  "pw.ask.unableNow":       "I'm unable to respond right now. Please try again.",
  "pw.ask.unableConn":      "Unable to respond. Please check your connection and try again.",

  /* Settings - two-factor authentication panel (TOTP via Supabase Auth) */
  "settings.twofactor.title":                  "Two-Factor Authentication",
  "settings.twofactor.loading":                "Loading",
  "settings.twofactor.scanInstructions":       "Scan this QR code with an authenticator app (Google Authenticator, 1Password, Authy), then enter the 6-digit code to confirm.",
  "settings.twofactor.qrAlt":                  "Two-factor QR code",
  "settings.twofactor.manualKey":              "Or enter this key manually",
  "settings.twofactor.codeLabel":              "6-digit code",
  "settings.twofactor.verifying":              "Verifying",
  "settings.twofactor.confirmOn":              "Confirm and turn on",
  "settings.twofactor.cancel":                 "Cancel",
  "settings.twofactor.onTitle":                "Two-factor authentication is on",
  "settings.twofactor.onDesc":                 "A code from your authenticator app is required at sign in.",
  "settings.twofactor.disable":                "Disable",
  "settings.twofactor.addStep":                "Add a second step at sign in",
  "settings.twofactor.addStepDesc":            "Use an authenticator app to generate one-time codes.",
  "settings.twofactor.starting":               "Starting",
  "settings.twofactor.enable":                 "Enable",
  "settings.twofactor.toast.loadFailTitle":    "Could not load 2FA status",
  "settings.twofactor.toast.startFailTitle":   "Could not start 2FA setup",
  "settings.twofactor.toast.tryAgain":         "Try again.",
  "settings.twofactor.toast.verifyFailTitle":  "Verification failed",
  "settings.twofactor.toast.incorrectTitle":   "Incorrect code",
  "settings.twofactor.toast.onTitle":          "Two-factor authentication on",
  "settings.twofactor.toast.onBody":           "Your account now requires a code at sign in.",
  "settings.twofactor.toast.offTitle":         "Two-factor authentication off",
  "settings.twofactor.toast.offBody":          "Codes are no longer required at sign in.",
  "settings.twofactor.toast.disableFailTitle": "Could not disable 2FA",
  "settings.twofactor.toast.secretCopiedTitle":"Secret copied",
  "settings.twofactor.toast.secretCopiedBody": "Paste it into your authenticator app.",

  /* Project workspace - marketplace "recommended to complete this product" block */
  "pw.rec.title":             "Recommended to complete this product",
  "pw.rec.subtitle":          "Gaps Nuvra detected from what you built. These are suggestions, not changes.",
  "pw.rec.noMatches":         "No matching assets yet",
  "pw.rec.assetSingular":     "asset",
  "pw.rec.assetsPlural":      "assets",
  "pw.rec.fromFree":          "from free",
  "pw.rec.fromPrice":         "from ${price}",
  "pw.rec.findInMarketplace": "Find in Marketplace",
  "pw.rec.free":              "Free",
  "pw.rec.compatible":        "compatible",
  "pw.rec.checkCompat":       "check compat",

  /* Public preview pages (SharedPreview, SlugPreview) and the auth callback */
  "preview.notAvailable":   "This app isn't available for public preview.",
  "preview.linkProblem":    "The link may be private, expired, or incorrect.",
  "auth.completingSignIn":  "Completing sign in…",

  // Settings - Profile panel
  "settings.profile.infoTitle":      "Profile Information",
  "settings.profile.avatarAlt":      "Avatar",
  "settings.profile.name":           "Name",
  "settings.profile.email":          "Email",
  "settings.profile.saving":         "Saving",
  "settings.profile.save":           "Save Changes",
  "settings.profile.onboardingTitle":"Onboarding",
  "settings.profile.onboardingBody": "Replay the welcome walkthrough to rediscover key features or explore the platform again.",
  "settings.profile.restartTour":    "Restart Onboarding Tour",
  "settings.profile.toast.errorTitle":   "Error",
  "settings.profile.toast.updatedTitle": "Profile updated",
  "settings.profile.toast.updatedBody":  "Your name has been saved.",

  // Settings - Security panel
  "settings.security.changeTitle":     "Change Password",
  "settings.security.currentPassword": "Current Password",
  "settings.security.newPassword":     "New Password",
  "settings.security.confirm":         "Confirm",
  "settings.security.mismatchInline":  "Passwords do not match.",
  "settings.security.updating":        "Updating",
  "settings.security.update":          "Update Password",
  "settings.security.toast.mismatchTitle": "Password mismatch",
  "settings.security.toast.mismatchBody":  "New passwords do not match.",
  "settings.security.toast.shortTitle":    "Too short",
  "settings.security.toast.shortBody":     "Password must be at least 8 characters.",
  "settings.security.toast.errorTitle":    "Error",
  "settings.security.toast.updatedTitle":  "Password updated",
  "settings.security.toast.updatedBody":   "Your password has been changed.",

  "settings.notifications.important": "Important",
  "settings.notifications.summary.categories": "Categories enabled",
  "settings.notifications.summary.channels": "Active channels",
  "settings.notifications.summary.behavior": "Delivery model",
  "settings.notifications.summary.behaviorBody": "Preferences apply per notification type and delivery channel. Changes are saved to your account as you make them.",
  "settings.notifications.saveError": "This change could not be saved.",
  "settings.notifications.dismiss": "Dismiss",
  "language.summary.available": "Available now",
  "language.summary.note": "Translation coverage",
  "language.summary.noteBody": "Only interface languages with a complete Nuvra catalog are selectable. This prevents an apparent language change from silently falling back to English.",
  "language.unavailableTitle": "Additional interface languages",
  "language.unavailableBody": "These locales are defined in Nuvra but do not yet have complete interface catalogs, so they remain unavailable until their translations are ready.",
  // Settings - Notifications panel
  "settings.notifications.title":     "Notification Preferences",
  "settings.notifications.loading":   "Loading",
  "settings.notifications.description": "Choose where Nuvra sends each type of account and product update. Changes are saved automatically.",
  "settings.notifications.colName":   "Notification",
  "settings.notifications.colInApp":  "In-app",
  "settings.notifications.colEmail":  "Email",
  "settings.notifications.saving":    "Saving",
  "settings.notifications.saved":     "Saved",
  "settings.notifications.recommended": "Recommended",
  "settings.notifications.securityNote": "Security and billing messages may be important to protecting your account or completing a transaction. Notification preferences control available delivery channels; they do not disable required account operations.",
  "settings.notifications.type.product_updates":     "Product updates",
  "settings.notifications.type.deployment_alerts":   "Deployment alerts",
  "settings.notifications.type.marketplace_updates": "Marketplace updates",
  "settings.notifications.type.security_alerts":     "Security alerts",
  "settings.notifications.type.weekly_digest":       "Weekly digest",
  "settings.notifications.type.team_mentions":       "Team mentions",
  "settings.notifications.type.support_alerts":       "Support updates",
  "settings.notifications.type.billing_alerts":       "Billing alerts",
  "settings.notifications.desc.product_updates":     "New Nuvra releases, improvements, and product announcements.",
  "settings.notifications.desc.deployment_alerts":   "Deployment failures, completion notices, and production events.",
  "settings.notifications.desc.marketplace_updates": "Marketplace releases, template updates, and availability changes.",
  "settings.notifications.desc.security_alerts":     "Sign-in, authentication, recovery, and other account-security events.",
  "settings.notifications.desc.weekly_digest":       "A periodic summary of important activity and product updates.",
  "settings.notifications.desc.team_mentions":       "When teammates mention or directly involve you in a workspace.",
  "settings.notifications.desc.support_alerts":       "Replies and status changes from Nuvra Support.",
  "settings.notifications.desc.billing_alerts":       "Payment, subscription, credit, and billing events.",

  // Settings - API Keys panel
  "settings.apiKeys.createdBanner":  "Key created. Copy it now, it won't be shown again.",
  "settings.apiKeys.dismiss":        "Dismiss",
  "settings.apiKeys.createTitle":    "Create API Key",
  "settings.apiKeys.keyName":        "Key Name",
  "settings.apiKeys.keyNamePlaceholder": "e.g. CI/CD Pipeline",
  "settings.apiKeys.scopes":         "Scopes",
  "settings.apiKeys.creating":       "Creating",
  "settings.apiKeys.create":         "Create Key",
  "settings.apiKeys.activeTitle":    "Active Keys",
  "settings.apiKeys.loading":        "Loading",
  "settings.apiKeys.empty":          "No API keys yet. Create one above.",
  "settings.apiKeys.revoked":        "Revoked",
  "settings.apiKeys.revoke":         "Revoke",
  "settings.apiKeys.usingTitle":     "Using your keys",
  "settings.apiKeys.usingBodyBefore":"Send the key as a Bearer token or an",
  "settings.apiKeys.usingBodyAfter": "header. Keys are read-only and each call is checked against the key's scopes.",
  "settings.apiKeys.usingNote":      "Write scopes are reserved. Write endpoints accept your signed-in session only, so a leaked key cannot change a project.",
  "settings.apiKeys.toast.createdTitle": "API key created",
  "settings.apiKeys.toast.createdBody":  "Copy it now, it will not be shown again.",
  "settings.apiKeys.toast.errorTitle":   "Error",
  "settings.apiKeys.toast.copiedTitle":  "Copied",
  "settings.apiKeys.toast.copiedBody":   "Copied to clipboard.",

  // Settings - Privacy panel
  "settings.privacy.title": "Privacy & Compliance",

  // Settings - System Status panel
  "settings.systemStatus.title":       "System Status",
  "settings.systemStatus.checking":    "Checking services",
  "settings.systemStatus.loadError":   "Could not load system status.",
  "settings.systemStatus.allOperational": "All systems operational",
  "settings.systemStatus.noChecks":    "No service checks are reporting right now.",
  "settings.systemStatus.incidents":   "Incidents",
  "settings.systemStatus.noIncidents": "No incidents reported.",
  "settings.systemStatus.resolved":    "Resolved",
  "settings.systemStatus.startedAt":   "Started {time}",
  "settings.systemStatus.resolvedAt":  "Resolved {time}",
  "settings.systemStatus.state.operational": "Operational",
  "settings.systemStatus.state.degraded":    "Degraded",
  "settings.systemStatus.state.outage":      "Outage",

  // Settings - Memory panel
  "settings.memory.title":     "Memory Management",
  "settings.memory.clearAll":  "Clear all",
  "settings.memory.body":      "What Nuvra remembers across your sessions, by agent. Content is never displayed here, only what is stored and how large it is. Deleting removes it permanently.",
  "settings.memory.loading":   "Loading memory",
  "settings.memory.loadError": "Could not load memory.",
  "settings.memory.emptyTitle":"No memory stored yet.",
  "settings.memory.emptyBody": "As you work with Nuvra's agents, what they learn will appear here.",
  "settings.memory.itemOne":   "{n} item",
  "settings.memory.itemOther": "{n} items",
  "settings.memory.updated":   "updated {date}",
  "settings.memory.toast.deletedTitle":   "Memory deleted",
  "settings.memory.toast.deleteFailTitle":"Delete failed",
  "settings.memory.toast.clearedTitle":   "Memory cleared",
  "settings.memory.toast.clearedOne":     "{n} item removed.",
  "settings.memory.toast.clearedOther":   "{n} items removed.",
  "settings.memory.toast.clearFailTitle": "Clear failed",

  // Settings - AI Preferences panel
  "settings.aiPreferences.title":        "AI Preferences",
  "settings.aiPreferences.loading":      "Loading",
  "settings.aiPreferences.intro":        "How Nuvra's agents work for you. These persist to your account and apply across sessions.",
  "settings.aiPreferences.preferredModel":"Preferred model",
  "settings.aiPreferences.automatic":    "Automatic (Nuvra picks the best model per task)",
  "settings.aiPreferences.reasoningDepth":"Reasoning depth",
  "settings.aiPreferences.creativity":   "Creativity",
  "settings.aiPreferences.responseStyle":"Response style",
  "settings.aiPreferences.longTermMemory":"Use long-term memory",
  "settings.aiPreferences.longTermMemoryNote":"Let agents remember context across sessions. Manage stored memory under Memory.",
  "settings.aiPreferences.saving":       "Saving",
  "settings.aiPreferences.save":         "Save preferences",
  "settings.aiPreferences.depth.concise":  "Concise",
  "settings.aiPreferences.depth.balanced": "Balanced",
  "settings.aiPreferences.depth.deep":     "Deep",
  "settings.aiPreferences.creativity.precise":  "Precise",
  "settings.aiPreferences.creativity.balanced": "Balanced",
  "settings.aiPreferences.creativity.creative": "Creative",
  "settings.aiPreferences.style.concise":  "Concise",
  "settings.aiPreferences.style.balanced": "Balanced",
  "settings.aiPreferences.style.detailed": "Detailed",
  "settings.aiPreferences.toast.savedTitle":    "Preferences saved",
  "settings.aiPreferences.toast.saveFailTitle": "Save failed",


  // Settings - AI Behavior / Memory & Context enterprise surfaces
  "settings.aiBehavior.loadError": "Could not load AI behavior settings.",
  "settings.aiBehavior.summaryTitle": "Nuvra AI",
  "settings.aiBehavior.accountBadge": "Account defaults",
  "settings.aiBehavior.summaryBody": "These defaults guide interactive Nuvra intelligence while task-specific routing and platform governance remain authoritative where required.",
  "settings.aiBehavior.summary.routing": "Routing",
  "settings.aiBehavior.summary.reasoning": "Reasoning",
  "settings.aiBehavior.summary.style": "Style",
  "settings.aiBehavior.summary.memory": "Memory",
  "settings.aiBehavior.enabled": "Enabled",
  "settings.aiBehavior.disabled": "Disabled",
  "settings.aiBehavior.routingTitle": "Model routing",
  "settings.aiBehavior.routingDescription": "Choose how Nuvra selects the model for interactive AI work. Automatic routing uses Nuvra's server-side model and availability rules.",
  "settings.aiBehavior.modelLabel": "Preferred model",
  "settings.aiBehavior.modelHelp": "A preference, not a billing or governance override. Explicit task routing remains authoritative.",
  "settings.aiBehavior.automatic": "Automatic",
  "settings.aiBehavior.modelPinnedNote": "Nuvra will use this model for eligible interactive requests while it remains available.",
  "settings.aiBehavior.modelAutomaticNote": "Nuvra chooses the appropriate available model for each eligible interactive request.",
  "settings.aiBehavior.reasoningTitle": "Reasoning",
  "settings.aiBehavior.reasoningDescription": "Set the amount of reasoning Nuvra should prefer for interactive work.",
  "settings.aiBehavior.reasoning.concise.label": "Concise",
  "settings.aiBehavior.reasoning.concise.description": "Focused reasoning for faster, direct responses.",
  "settings.aiBehavior.reasoning.balanced.label": "Balanced",
  "settings.aiBehavior.reasoning.balanced.description": "A practical balance of depth, responsiveness, and resource use.",
  "settings.aiBehavior.reasoning.deep.label": "Deep",
  "settings.aiBehavior.reasoning.deep.description": "More extensive reasoning for complex questions and decisions.",
  "settings.aiBehavior.creativityTitle": "Creativity",
  "settings.aiBehavior.creativityDescription": "Control how much variation Nuvra should introduce in interactive responses.",
  "settings.aiBehavior.creativity.precise.label": "Precise",
  "settings.aiBehavior.creativity.precise.description": "Prefer predictable, specification-driven results.",
  "settings.aiBehavior.creativity.balanced.label": "Balanced",
  "settings.aiBehavior.creativity.balanced.description": "Balance consistency with useful exploration.",
  "settings.aiBehavior.creativity.creative.label": "Creative",
  "settings.aiBehavior.creativity.creative.description": "Allow more variation when exploring ideas and solutions.",
  "settings.aiBehavior.responseTitle": "Response style",
  "settings.aiBehavior.responseDescription": "Choose how much explanation and context Nuvra should provide.",
  "settings.aiBehavior.style.concise.label": "Concise",
  "settings.aiBehavior.style.concise.description": "Direct answers with minimal extra exposition.",
  "settings.aiBehavior.style.balanced.label": "Balanced",
  "settings.aiBehavior.style.balanced.description": "Clear explanations with the context needed to act.",
  "settings.aiBehavior.style.detailed.label": "Detailed",
  "settings.aiBehavior.style.detailed.description": "Complete explanations, assumptions, and actionable context.",
  "settings.aiBehavior.recommended": "Recommended",
  "settings.aiBehavior.memoryTitle": "Long-term memory",
  "settings.aiBehavior.memoryDescription": "Allow Nuvra's agents to retain permitted context across sessions. Stored memory is managed separately.",
  "settings.aiBehavior.memoryToggle": "Enable long-term memory",
  "settings.aiBehavior.memoryOnTitle": "Memory is enabled",
  "settings.aiBehavior.memoryOnBody": "Nuvra may use permitted long-term memory to maintain continuity across eligible sessions.",
  "settings.aiBehavior.memoryOffTitle": "Memory is disabled",
  "settings.aiBehavior.memoryOffBody": "Nuvra will not use long-term agent memory operations.",
  "settings.aiBehavior.manageMemory": "Manage stored memory",
  "settings.aiBehavior.saved": "Saved",
  "settings.aiBehavior.unsaved": "Unsaved changes",
  "settings.aiBehavior.saveNote": "Account defaults apply to eligible interactive AI requests.",
  "settings.aiBehavior.reset": "Reset",
  "settings.aiBehavior.discard": "Discard",
  "settings.aiBehavior.saving": "Saving",
  "settings.aiBehavior.save": "Save changes",
  "settings.aiBehavior.toast.savedTitle": "AI behavior saved",
  "settings.aiBehavior.toast.saveFailTitle": "Could not save AI behavior",

  "settings.memory.managementTitle": "Memory & Context",
  "settings.memory.managementDescription": "Review the account-level memory metadata Nuvra stores for continuity. Stored content is never exposed in this settings surface.",
  "settings.memory.active": "Active",
  "settings.memory.paused": "Paused",
  "settings.memory.manageBehavior": "AI Behavior",
  "settings.memory.stats.items": "Memory items",
  "settings.memory.stats.storage": "Storage used",
  "settings.memory.stats.updated": "Latest update",
  "settings.memory.stats.expiring": "With expiration",
  "settings.memory.privacyTitle": "Privacy & control",
  "settings.memory.longTermTitle": "Long-term memory",
  "settings.memory.longTermOn": "Enabled for eligible agent memory operations.",
  "settings.memory.longTermOff": "Disabled. The account preference blocks agent memory operations.",
  "settings.memory.toggleManagedElsewhere": "This control is managed from AI Behavior so there is one source of truth.",
  "settings.memory.recordsTitle": "Stored memory",
  "settings.memory.recordsDescription": "Metadata only: key, agent, size, timestamps, and expiration. Memory content remains server-side.",
  "settings.memory.searchPlaceholder": "Search keys or agents",
  "settings.memory.clearSearch": "Clear search",
  "settings.memory.agentFilter": "Filter by agent",
  "settings.memory.allAgents": "All agents",
  "settings.memory.expires": "Expires",
  
  "settings.memory.delete": "Delete memory",
  "settings.memory.noMatches": "No memory matches your search",
  "settings.memory.noMatchesBody": "Try a different key, agent, or filter.",
  "settings.memory.reviewBehavior": "Review AI Behavior",
  "settings.memory.securityNote": "Nuvra keeps memory content behind the server boundary. This page exposes metadata only and every account operation is scoped to the authenticated owner.",
  "settings.memory.detailDescription": "Metadata for this stored memory. The underlying content is intentionally not displayed here.",
  "settings.memory.detail.key": "Memory key",
  "settings.memory.detail.agent": "Agent",
  "settings.memory.detail.size": "Size",
  "settings.memory.detail.created": "Created",
  "settings.memory.detail.updated": "Updated",
  "settings.memory.detail.expiration": "Expiration",
  "settings.memory.noExpiration": "No expiration",
  "settings.memory.contentHidden": "Stored memory content is not returned to the settings client.",
  "settings.memory.deleteConfirmTitle": "Delete this memory?",
  "settings.memory.deleteConfirmDescription": "This permanently removes the selected account memory. Nuvra will no longer use it in future sessions.",
  "settings.memory.clearConfirmTitle": "Clear all account memory?",
  "settings.memory.clearConfirmDescription": "This permanently removes all memory records managed by this account-level settings surface. Project and organization context are governed separately.",
  "settings.memory.clearing": "Clearing",
  "settings.memory.cancel": "Cancel",

  "settings.aiBehavior.pageDescription": "Control how Nuvra reasons, responds, routes eligible interactive work, and uses long-term memory.",
  "settings.memory.pageDescription": "Review and control the account-level memory Nuvra stores for continuity.",
  "settings.memory.scopeNote": "This settings surface manages account-level memory metadata. Project and organization context are governed by their existing Nuvra memory surfaces.",
  // Settings - Billing panel
  "settings.billing.planTitle":      "Plan",
  "settings.billing.loading":        "Loading",
  "settings.billing.loadError":      "Could not load billing.",
  "settings.billing.free":           "Free",
  "settings.billing.noSubscription": "No active paid subscription.",
  "settings.billing.renews":         "renews {date}",
  "settings.billing.cancelNote":     "Your plan is set to cancel at the end of the current period.",
  "settings.billing.managePlan":     "Manage plan",
  "settings.billing.upgrade":        "Upgrade",
  "settings.billing.creditsTitle":   "Credits",
  "settings.billing.actionsTitle":   "What actions cost",
  "settings.billing.actionsNote":    "Costs shown before you run an action. Complex builds can scale higher; failed actions are refunded.",
  "settings.billing.usageTitle":     "Recent usage",
  "settings.billing.usage.refund":   "Refund: {reason}",
  "settings.billing.usage.grant":    "Credits granted",
  "settings.billing.usage.topup":    "Credits purchased",
  "settings.billing.usage.refunded": "refunded",
  "settings.billing.noUsage":        "No usage recorded yet.",

  // Settings - Audit panel
  "settings.audit.title":        "Audit Logs",
  "settings.audit.intro":        "An immutable record of actions taken on your account and projects. Filter by event type or search by scope.",
  "settings.audit.allTypes":     "All event types",
  "settings.audit.searchPlaceholder": "Search scope or table",
  "settings.audit.loading":      "Loading audit log",
  "settings.audit.emptyTitle":   "No audited actions yet.",
  "settings.audit.noMatch":      "No events match your filters.",
  "settings.audit.account":      "Account",
  "settings.audit.project":      "Project {id}",
  "settings.audit.scope.account":"account",
  "settings.audit.scope.project":"project",

  // Settings - Export center panel
  "settings.export.privateBadge": "Private to your account",
  "settings.export.sourceGroups": "Data groups",
  "settings.export.format": "Download format",
  "settings.export.rateLimit": "Maximum downloads",
  "settings.export.privacyTitle": "Privacy boundary",
  "settings.export.privacyBody": "The export is scoped to data Nuvra records as yours. Public support messages are included; internal support notes are excluded.",
  "settings.export.successTitle": "Export ready",
  "settings.export.successBody": "Downloaded {records} records across {sources} data groups.",
  "settings.export.source.support_messages": "Support conversations",
  "settings.audit.refresh": "Refresh",
  "settings.audit.export": "Export filtered CSV",
  "settings.audit.filters": "Filters",
  "settings.audit.clear": "Clear filters",
  "settings.audit.allScopes": "All scopes",
  "settings.audit.allTime": "All time",
  "settings.audit.last24h": "Last 24 hours",
  "settings.audit.last7d": "Last 7 days",
  "settings.audit.last30d": "Last 30 days",
  "settings.audit.stat.events": "Recorded events",
  "settings.audit.stat.account": "Account events",
  "settings.audit.stat.projects": "Project events",
  "settings.audit.col.event": "Event",
  "settings.audit.col.scope": "Scope",
  "settings.audit.col.time": "Time",
  "settings.audit.detail.action": "Action",
  "settings.audit.detail.time": "Recorded",
  "settings.audit.detail.payload": "Recorded detail",
  "settings.audit.retentionNote": "This account view shows the latest audit records returned by Nuvra for your account and projects. Filtering and CSV export operate on the records currently loaded.",
  "settings.export.title":       "Export your data",
  "settings.export.body":        "Download a JSON copy of the data tied to your account, for your own records or for compliance. The export includes only your own data and is limited to 3 downloads per hour.",
  "settings.export.included":    "Included in your export",
  "settings.export.preparing":   "Preparing export",
  "settings.export.exportNow":   "Export my data",
  "settings.export.generated":   "Generated {time}",
  "settings.export.toast.doneTitle": "Export downloaded",
  "settings.export.toast.doneBody":  "Your data was saved as nuvra-data-export.json.",
  "settings.export.toast.failTitle": "Export failed",
  "settings.export.toast.failBody":  "Export failed. Please try again.",
  "settings.export.source.projects":            "Projects",
  "settings.export.source.builds":              "Build history",
  "settings.export.source.support_tickets":     "Support tickets",
  "settings.export.source.creator_standing":    "Creator standing",
  "settings.export.source.creator_violations":  "Creator violations",
  "settings.export.source.creator_appeals":     "Creator appeals",
  "settings.export.source.enforcement_actions": "Enforcement actions",
  "settings.export.source.published_templates": "Published templates",
  "settings.export.source.template_installs":   "Template installs",
  "settings.export.source.purchases":           "Purchases",
  "settings.export.source.payments":            "Payment history",
  "settings.export.source.data_audit":          "Account audit log",

  /* Admin operator console - shell (tab bar, access gate, header) */
  "admin.shell.nav.overview":       "Overview",
  "admin.shell.nav.status":         "Status",
  "admin.shell.nav.trust":          "Trust & Safety",
  "admin.shell.nav.users":          "Users",
  "admin.shell.nav.projects":       "Projects",
  "admin.shell.nav.builds":         "AI Builds",
  "admin.shell.nav.agent":             "Agent",
  "admin.shell.nav.workers":           "Workers",
  "admin.agent.loading":               "Loading agent activity…",
  "admin.agent.summary":               "{sessions} sessions, {failed} failed, {acu} ACU consumed",
  "admin.agent.filterStatus":          "Filter by status",
  "admin.agent.allStatuses":           "All statuses",
  "admin.agent.refresh":               "Refresh",
  "admin.agent.none":                  "No agent sessions recorded yet.",
  "admin.agent.colWorkOrder":          "Work order",
  "admin.agent.colGoal":               "Goal",
  "admin.agent.colUser":               "User",
  "admin.agent.colStatus":             "Status",
  "admin.agent.colTasks":              "Tasks",
  "admin.agent.colAcu":                "ACU",
  "admin.agent.colStarted":            "Started",
  "admin.agent.prev":                  "Previous",
  "admin.agent.next":                  "Next",
  "admin.agent.page":                  "Page {page}",
  "admin.shell.nav.billing":        "Billing & Credits",
  "admin.shell.nav.executive":     "Executive",
  "admin.shell.nav.financials":     "Financial Health",
  "admin.shell.nav.alerts":         "Alerts",
  "admin.shell.nav.communications": "Communications",
  "admin.shell.nav.security":       "Security",
  "admin.shell.nav.audit":          "Audit Log",
  "admin.shell.nav.insights":       "Insights",
  "admin.shell.nav.flags":          "Feature Flags",
  "admin.shell.nav.logs":           "Logs",
  "admin.shell.nav.simulate":       "Simulate",
  "admin.shell.nav.database":       "Database",
  "admin.comms.intro":               "Platform communications reach every account at once and cannot be recalled. Each one is deduplicated per person, so re-sending the same release or policy version tells nobody twice.",
  "admin.comms.release.announce":    "Announce release to all users",
  "admin.comms.policy.announce":     "Announce document update to all users",
  "admin.comms.sent":                "Delivered to {created} of {recipients} accounts. {deduplicated} already had it.",
  "admin.comms.error.forbidden":     "Your account is not a platform administrator.",
  "admin.comms.error.unknownRelease":"That release is not in the published release notes, so it cannot be announced.",
  "admin.comms.error.invalidRequest":"Something required is missing. Check the fields and try again.",
  "admin.comms.error.unavailable":   "The communication could not be sent. Nothing was delivered.",
  "admin.comms.release.title":       "What's New",
  "admin.comms.release.body":        "Announce a release that is already published in the release notes. The wording comes from the release itself, so the notification and the What's New page always agree.",
  "admin.comms.release.version":     "Release",
  "admin.comms.policy.title":        "Privacy and Terms",
  "admin.comms.policy.body":         "Tell every account that a legal document changed. The version is required, because a notice nobody can tie to a document is not something a person can act on.",
  "admin.comms.policy.document":     "Document",
  "admin.comms.policy.privacy":      "Privacy Policy",
  "admin.comms.policy.terms":        "Terms of Service",
  "admin.comms.policy.version":      "Version",
  "admin.comms.policy.effective":    "Effective date (optional)",
  "admin.comms.security.title":      "Security notice",
  "admin.comms.security.body":       "The highest severity message the platform can send, and the one most damaged by overuse. Send it for a real security event only.",
  "admin.comms.security.reference":  "Reference",
  "admin.comms.security.referenceHint": "A stable identifier for this notice. Reusing it means nobody is told the same thing twice.",
  "admin.comms.security.subject":    "Subject",
  "admin.comms.security.message":    "Message",
  "admin.comms.security.send":       "Send security notice",
  "admin.shell.verifyingAccess":    "Verifying access…",
  "admin.shell.deniedMissingTitle": "Admin tools not deployed",
  "admin.shell.deniedNotAdminTitle":"Admin access required",
  "admin.shell.deniedMissingBody":  "The admin database functions are not present in this environment yet. Apply the admin panel migrations, then reload.",
  "admin.shell.deniedNotAdminBody": "Your account does not have the admin role. An existing admin must grant it (user_roles.role = 'admin') before this console is available.",
  "admin.shell.deniedCacheTitle":   "Admin tools unreachable",
  "admin.shell.deniedCacheBody":    "The API cannot resolve the admin functions. Either they have not been applied to this database, or they were applied and the schema cache has not reloaded. Check that they exist, then reload the cache.",
  "admin.shell.deniedSchemaTitle":  "Admin schema incomplete",
  "admin.shell.deniedSchemaBody":   "The admin functions are deployed, but a table or column they read is missing from this database. Apply the outstanding migrations, then reload.",
  "admin.shell.deniedGrantTitle":   "Execute permission missing",
  "admin.shell.deniedGrantBody":    "The admin functions exist but your role cannot run them. Grant EXECUTE on the admin functions to authenticated, then reload.",
  "admin.shell.deniedSignedOutTitle":"Session expired",
  "admin.shell.deniedSignedOutBody":"Your session is no longer valid. Sign in again to reopen the console.",
  "admin.shell.deniedUnknownTitle": "Console could not open",
  "admin.shell.deniedUnknownBody":  "The access check failed for a reason Nuvra cannot classify. The database response is shown below.",
  "admin.shell.backToDashboard":    "Back to dashboard",
  "admin.shell.title":              "Nuvra Control Center",
  "admin.shell.subtitle":           "Internal operations dashboard. Admin access only.",

  // Admin Overview tab
  "admin.overview.loading":               "Loading platform stats…",
  "admin.overview.autoRefresh":           "Auto-refreshes every 30s · Last:",
  "admin.overview.refresh":               "Refresh",
  "admin.overview.usersHeading":          "Users",
  "admin.overview.totalUsers":            "Total Users",
  "admin.overview.active30d":             "Active (30d)",
  "admin.overview.newThisWeek":           "New This Week",
  "admin.overview.suspended":             "Suspended",
  "admin.overview.platformActivity":      "Platform Activity",
  "admin.overview.projects":              "Projects",
  "admin.overview.activeBuilds":          "Active Builds",
  "admin.overview.failedBuilds7d":        "Failed Builds (7d)",
  "admin.overview.unreadAlerts":          "Unread Alerts",
  "admin.overview.successRatesTitle":     "Success Rates (7 days)",
  "admin.overview.buildSuccessRate":      "Build success rate",
  "admin.overview.deploymentSuccessRate": "Deployment success rate",
  "admin.overview.revenueTitle":          "Revenue",
  "admin.overview.thisMonth":             "This month",
  "admin.overview.allTime":               "All time",
  "admin.overview.acuCredits":            "Credits",
  "admin.overview.totalGranted":          "Total Granted",
  "admin.overview.totalConsumed":         "Total Consumed",
  "admin.overview.inProfilesNow":         "In Profiles Now",
  "admin.overview.allTimeSub":            "all time",
  "admin.overview.liveTotalSub":          "live total across all users",
  "admin.overview.sevenDayTrends":        "7-Day Trends",
  "admin.overview.buildActivityTitle":    "Build Activity (7 days)",
  "admin.overview.noBuildData":           "No build data in the last 7 days.",
  "admin.overview.newUsersTitle":         "New Users (7 days)",
  "admin.overview.noSignupData":          "No signup data in the last 7 days.",
  "admin.overview.seriesSuccess":         "Success",
  "admin.overview.seriesFailed":          "Failed",
  "admin.overview.seriesNewUsers":        "New Users",

  // Admin Trust & Safety tab
  "admin.trust.loading":             "Loading the founder console",
  "admin.trust.intro":               "Trust and Safety: readiness, the review queue, the security feed, and creator enforcement.",
  "admin.trust.refresh":             "Refresh",
  "admin.trust.appealDecideError":   "Could not decide the appeal.",
  "admin.trust.appealApproved":      "Appeal approved. The hold was released.",
  "admin.trust.appealDenied":        "Appeal denied.",
  "admin.trust.replyPrompt":         "Reply to the creator (this resolves the ticket):",
  "admin.trust.replyError":          "Could not send the reply.",
  "admin.trust.replySent":           "Reply sent and ticket resolved.",
  "admin.trust.lookupError":         "Could not look up the creator.",
  "admin.trust.violationError":      "Could not record the violation.",
  "admin.trust.violationRecorded":   "Violation recorded. Standing: {standing}{terminated}.",
  "admin.trust.terminatedSuffix":    "(terminated)",
  "admin.trust.resolveError":        "Could not resolve the case.",
  "admin.trust.resolveSuccess":      "Case resolved and revenue hold released.",
  "admin.trust.verifyError":         "Could not request verification.",
  "admin.trust.verifyRequested":     "Requested {kind} verification. Payouts are frozen until the creator completes it.",
  "admin.trust.chargebackError":     "Could not evaluate chargebacks.",
  "admin.trust.chargebackEnforced":  "Rule 9 violation recorded; payout hold applied.",
  "admin.trust.chargebackNoThreshold": "Evaluated. No hold threshold reached.",
  "admin.trust.readinessTitle":      "Release readiness",
  "admin.trust.ready":               "Ready",
  "admin.trust.notReady":            "Not ready",
  "admin.trust.readinessLoadError":  "Could not load readiness (admin access required).",
  "admin.trust.blockers":            "Blockers:",
  "admin.trust.recommended":         "Recommended:",
  "admin.trust.schemaMissing":       "Schema missing:",
  "admin.trust.optionalOff":         "Optional off:",
  "admin.trust.appealsQueue":        "Appeals queue",
  "admin.trust.noAppeals":           "No open appeals.",
  "admin.trust.creatorPrefix":       "creator",
  "admin.trust.approve":             "Approve",
  "admin.trust.deny":                "Deny",
  "admin.trust.supportQueue":        "Support queue",
  "admin.trust.noTickets":           "No open or escalated tickets.",
  "admin.trust.replyResolve":        "Reply and resolve",
  "admin.trust.creatorEnforcement":  "Creator enforcement",
  "admin.trust.creatorIdPlaceholder":"creator user id",
  "admin.trust.lookUp":              "Look up",
  "admin.trust.riskLabel":           "risk",
  "admin.trust.standingLabel":       "standing:",
  "admin.trust.strikesLabel":        "strikes:",
  "admin.trust.terminated":          "terminated",
  "admin.trust.ruleOption":          "Rule {number}: {title}",
  "admin.trust.recordViolation":     "Record violation",
  "admin.trust.resolveCase":         "Resolve case",
  "admin.trust.verificationLabel":   "Verification (Rule 11):",
  "admin.trust.requestFreezes":      "Request (freezes payouts)",
  "admin.trust.chargebackTitle":     "Chargeback monitoring",
  "admin.trust.sellerIdPlaceholder": "seller account id",
  "admin.trust.evaluate":            "Evaluate",
  "admin.trust.enforce":             "Enforce",
  "admin.trust.chargebackRate":      "chargeback rate",
  "admin.trust.chargebackStats":     "{sales} sales, {chargebacks} chargebacks, {disputes} disputes",
  "admin.trust.securityFeedTitle":   "Security and abuse feed",
  "admin.trust.noSecurityEvents":    "No recent security events.",

  // Admin Users tab
  "admin.users.loading":                 "Loading users…",
  "admin.users.amountMustBePositive":    "Amount must be positive.",
  "admin.users.grantFailed":             "Grant failed.",
  "admin.users.grantSuccess":            "Granted {amount} credits successfully.",
  "admin.users.amountZeroOrPositive":    "Amount must be zero or positive.",
  "admin.users.setFailed":               "Set failed.",
  "admin.users.setSuccess":              "Balance set to {amount} credits (was {old}).",
  "admin.users.failed":                  "Failed.",
  "admin.users.userSuspended":           "User suspended.",
  "admin.users.userReactivated":         "User reactivated.",
  "admin.users.profileLoadFailed":       "Failed to load profile.",
  "admin.users.linkGenFailed":           "Failed to generate link.",
  "admin.users.totalUsers":              "Total users",
  "admin.users.totalProfileAcu":         "Total credits",
  "admin.users.grantAcu":                "Grant credits",
  "admin.users.emailOrUserId":           "Email or user ID",
  "admin.users.targetPlaceholder":       "user@example.com or uuid",
  "admin.users.amountAcu":               "Amount (credits)",
  "admin.users.reason":                  "Reason",
  "admin.users.granting":                "Granting…",
  "admin.users.setAcuBalance":           "Set Credit Balance (exact)",
  "admin.users.setToAcu":                "Set to (credits)",
  "admin.users.setting":                 "Setting…",
  "admin.users.setBalance":              "Set Balance",
  "admin.users.setBalanceHelp":          "Overwrites the exact balance (not additive). Admin only; every change is logged to the ledger.",
  "admin.users.usersTitle":              "Users ({count})",
  "admin.users.planAll":                 "all",
  "admin.users.searchPlaceholder":       "Search…",
  "admin.users.colEmail":                "Email",
  "admin.users.colPlan":                 "Plan",
  "admin.users.colStatus":               "Status",
  "admin.users.colProfileAcu":           "Credits",
  "admin.users.colRole":                 "Role",
  "admin.users.grantRole":             "Grant {role}",
  "admin.users.revokeRole":            "Revoke {role}",
  "admin.users.roleGranted":           "Role {role} granted.",
  "admin.users.roleRevoked":           "Role {role} revoked.",
  "admin.users.roleUnchanged":         "No change: the user already had that {role} state.",
  "admin.users.subscriptionHeading":     "Subscription",
  "admin.users.subPlan":                 "Plan",
  "admin.users.subStatus":               "Status",
  "admin.users.subRenews":               "Renews",
  "admin.users.subCancelling":           "Cancels at period end",
  "admin.users.subNone":                 "No subscription record.",
  "admin.users.deploymentsHeading":      "Deployments ({count})",
  "admin.users.noDeployments":           "No deployments.",
  "admin.users.colJoined":               "Joined",
  "admin.users.colActions":              "Actions",
  "admin.users.noUsersFound":            "No users found.",
  "admin.users.statusSuspended":         "Suspended",
  "admin.users.statusActive":            "Active",
  "admin.users.grant":                   "Grant",
  "admin.users.view":                    "View",
  "admin.users.reactivate":              "Reactivate",
  "admin.users.suspend":                 "Suspend",
  "admin.users.suspendConfirm":          "Suspend {email}?\nReason: {reason}",
  "admin.users.generateLoginLink":       "Generate Login Link",
  "admin.users.magicLinkHelp":           "Opens a 1-hour magic link to log in as this user (incognito)",
  "admin.users.copyLink":                "Copy link",
  "admin.users.loadingProfile":          "Loading profile…",
  "admin.users.riskProfile":             "Risk Profile",
  "admin.users.riskLevel":               "Risk level:",
  "admin.users.blockedPrompts":          "Blocked prompts:",
  "admin.users.suspendedLabel":          "Suspended:",
  "admin.users.yes":                     "Yes",
  "admin.users.no":                      "No",
  "admin.users.reasonLabel":             "Reason:",
  "admin.users.projectsHeading":         "Projects ({count})",
  "admin.users.noProjects":              "No projects.",
  "admin.users.recentBuildsHeading":     "Recent Builds ({count})",
  "admin.users.noBuilds":                "No builds.",
  "admin.users.acuTransactionsHeading":  "Credit Transactions ({count})",
  "admin.users.noTransactions":          "No transactions.",
  "admin.users.paymentsHeading":         "Payments ({count})",
  "admin.users.noPayments":              "No payment history.",
  "admin.users.sessionsHeading":         "Sessions ({count})",
  "admin.users.noSessions":              "No sessions.",
  "admin.users.sessionRevoked":          "Revoked",
  "admin.users.sessionActive":           "Active",

  // Admin Projects tab
  "admin.projects.loading":          "Loading projects…",
  "admin.projects.flagConfirm":      "Flag \"{name}\" for admin review?\nThis will create an alert in the Alerts tab.",
  "admin.projects.flagFailed":       "Flag failed.",
  "admin.projects.flagSuccess":      "\"{name}\" flagged for review.",
  "admin.projects.title":            "Projects",
  "admin.projects.searchPlaceholder":"Search project or email…",
  "admin.projects.search":           "Search",
  "admin.projects.colProject":       "Project",
  "admin.projects.colOwner":         "Owner",
  "admin.projects.colType":          "Type",
  "admin.projects.colVisibility":    "Visibility",
  "admin.projects.colLatestBuild":   "Latest Build",
  "admin.projects.colLatestDeploy":  "Latest Deploy",
  "admin.projects.colCreated":       "Created",
  "admin.projects.colActions":       "Actions",
  "admin.projects.noProjects":       "No projects found.",
  "admin.projects.openWorkspace":    "Open workspace",
  "admin.projects.open":             "Open",
  "admin.projects.flag":             "Flag",
  "admin.projects.pageInfo":         "Page {page} - showing {count} projects",
  "admin.projects.prev":             "Prev",
  "admin.projects.next":             "Next",

  // Admin Builds tab
  "admin.builds.cancelConfirm":      "Cancel this build? The running Railway deployment will be stopped.",
  "admin.builds.cancelFailed":       "Cancel failed",
  "admin.builds.cancelled":          "Build cancelled.",
  "admin.builds.retryConfirm":       "Retry this build? Railway will redeploy the last deployment.",
  "admin.builds.retryFailed":        "Retry failed",
  "admin.builds.redeployTriggered":  "Redeploy triggered.",
  "admin.builds.loading":            "Loading builds…",
  "admin.builds.title":              "AI Builds",
  "admin.builds.queuedCount":        "{count} queued",
  "admin.builds.runningCount":       "{count} running",
  "admin.builds.failedCount":        "{count} failed",
  "admin.builds.filter.all":         "all",
  "admin.builds.filter.running":     "running",
  "admin.builds.filter.done":        "done",
  "admin.builds.filter.failed":      "failed",
  "admin.builds.colBuildId":         "Build ID",
  "admin.builds.colProject":         "Project",
  "admin.builds.colUser":            "User",
  "admin.builds.colStatus":          "Status",
  "admin.builds.colDuration":        "Duration",
  "admin.builds.colCreated":         "Created",
  "admin.builds.colActions":         "Actions",
  "admin.builds.noBuilds":           "No builds found.",
  "admin.builds.cancel":             "Cancel",
  "admin.builds.retry":              "Retry",
  "admin.builds.errorLabel":         "Error",
  "admin.builds.pageInfo":           "Page {page} - {count} builds",
  "admin.builds.prev":               "Prev",
  "admin.builds.next":               "Next",

  // Admin Billing tab
  "admin.billing.refundConfirm":   "Refund this credits transaction?",
  "admin.billing.refundFailed":    "Refund failed.",
  "admin.billing.refundSuccess":   "Refunded successfully.",
  "admin.billing.loading":         "Loading transactions…",
  "admin.billing.title":           "Billing & Credits",
  "admin.billing.subAcu":          "Credit Transactions",
  "admin.billing.subPayments":     "Payments",
  "admin.billing.colUser":         "User",
  "admin.billing.colType":         "Type",
  "admin.billing.colAcu":          "Credits",
  "admin.billing.colDescription":  "Description",
  "admin.billing.colBuildId":      "Build ID",
  "admin.billing.colDate":         "Date",
  "admin.billing.colActions":      "Actions",
  "admin.billing.noTransactions":  "No transactions.",
  "admin.billing.highChip":        "⚠ high",
  "admin.billing.refund":          "Refund",
  "admin.billing.colGateway":      "Gateway",
  "admin.billing.colAmount":       "Amount",
  "admin.billing.colStatus":       "Status",
  "admin.billing.noPayments":      "No payments.",
  "admin.billing.tariffTitle":     "Action pricing (internal)",
  "admin.billing.tariffNote":      "The credit price of every metered action, from the same registry the build path charges through. Users see per-action costs in the pre-run preview and their usage feed, not this table.",
  "admin.billing.pageInfo":        "Page {page} - {count} records",
  "admin.billing.prev":            "Prev",
  "admin.billing.next":            "Next",

  // Admin Alerts tab
  "admin.alerts.loading":              "Loading alerts…",
  "admin.alerts.acknowledgeFailed":      "Could not acknowledge that alert.",
  "admin.alerts.resolveFailed":          "Could not resolve that alert.",
  "admin.alerts.unacknowledgedCount":  "{count} unacknowledged",
  "admin.alerts.unresolvedCount":      "{count} unresolved",
  "admin.alerts.criticalBadge":        "CRITICAL",
  "admin.alerts.sev.all":              "all",
  "admin.alerts.sev.critical":         "critical",
  "admin.alerts.sev.high":             "high",
  "admin.alerts.sev.medium":           "medium",
  "admin.alerts.sev.low":              "low",
  "admin.alerts.hideResolved":         "Hide resolved",
  "admin.alerts.showAll":              "Show all",
  "admin.alerts.allClear":             "No alerts. All clear.",
  "admin.alerts.resolvedChip":         "Resolved",
  "admin.alerts.acknowledgedChip":     "Acknowledged",
  "admin.alerts.acknowledge":          "Acknowledge",
  "admin.alerts.resolve":              "Resolve",

  // Admin Security tab
  "admin.security.forceLogoutConfirm": "Force logout {email}?\nAll their active sessions will be immediately terminated.",
  "admin.security.forceLogoutFailed":  "Force logout failed.",
  "admin.security.forceLogoutSuccess": "{email} has been logged out of all sessions.",
  "admin.security.suspendConfirm":     "Suspend this user?",
  "admin.security.failed":             "Failed.",
  "admin.security.userSuspended":      "User suspended.",
  "admin.security.userReactivated":    "User reactivated.",
  "admin.security.loading":            "Loading security data…",
  "admin.security.title":              "Security",
  "admin.security.subSafetyEvents":    "Safety Events",
  "admin.security.subSessions":        "Sessions",
  "admin.security.subAbuseTracking":   "Abuse Tracking",
  "admin.security.sev.all":            "all",
  "admin.security.sev.critical":       "critical",
  "admin.security.sev.high":           "high",
  "admin.security.sev.medium":         "medium",
  "admin.security.sev.low":            "low",
  "admin.security.colUser":            "User",
  "admin.security.colProvider":        "Provider",
  "admin.security.colStatus":          "Status",
  "admin.security.colSuspicious":      "Suspicious",
  "admin.security.colLastSeen":        "Last Seen",
  "admin.security.colExpires":         "Expires",
  "admin.security.colActions":         "Actions",
  "admin.security.noSessions":         "No sessions found.",
  "admin.security.revoked":            "Revoked",
  "admin.security.active":             "Active",
  "admin.security.forceLogout":        "Force Logout",
  "admin.security.colType":            "Type",
  "admin.security.colSeverity":        "Severity",
  "admin.security.colDescription":     "Description",
  "admin.security.colDate":            "Date",
  "admin.security.noEvents":           "No security events.",
  "admin.security.colRisk":            "Risk",
  "admin.security.colBlocked":         "Blocked",
  "admin.security.colFlagged":         "Flagged",
  "admin.security.colViolations":      "Violations",
  "admin.security.colLastViolation":   "Last Violation",
  "admin.security.noAbuse":            "No abuse records.",
  "admin.security.suspended":          "Suspended",
  "admin.security.reactivate":         "Reactivate",
  "admin.security.suspend":            "Suspend",
  "admin.security.pageInfo":           "Page {page}",
  "admin.security.prev":               "Prev",
  "admin.security.next":               "Next",

  // Admin Audit tab
  "admin.audit.loading":               "Loading audit log…",
  "admin.audit.targetType":            "Target type",
  "admin.audit.targetTypePlaceholder": "e.g. user, acu_transaction…",
  "admin.audit.action":                "Action",
  "admin.audit.actionPlaceholder":     "e.g. suspend, grant…",
  "admin.audit.filter":                "Filter",
  "admin.audit.clear":                 "Clear",
  "admin.audit.title":                 "Audit Log",
  "admin.audit.immutable":             "(immutable)",
  "admin.audit.colActor":              "Actor",
  "admin.audit.colAction":             "Action",
  "admin.audit.colTargetType":         "Target Type",
  "admin.audit.colTargetId":           "Target ID",
  "admin.audit.colIp":                 "IP",
  "admin.audit.colDate":               "Date",
  "admin.audit.noEntries":             "No audit log entries.",
  "admin.audit.before":                "Before",
  "admin.audit.after":                 "After",
  "admin.audit.pageInfo":              "Page {page} - {count} entries",
  "admin.audit.prev":                  "Prev",
  "admin.audit.next":                  "Next",

  // Admin Insights tab
  "admin.insights.loading":             "Loading insights…",
  "admin.insights.subtitle":            "Last 30 days. Refresh for latest data.",
  "admin.insights.refresh":             "Refresh",
  "admin.insights.funnelTitle":         "User Activation Funnel",
  "admin.insights.funnelRegistered":    "Registered",
  "admin.insights.funnelCreatedProject":"Created project",
  "admin.insights.funnelRanFirstBuild": "Ran first build",
  "admin.insights.funnelBuildSucceeded":"Build succeeded",
  "admin.insights.funnelDeployed":      "Deployed",
  "admin.insights.fromPrev":            "({pct} from prev)",
  "admin.insights.signupToBuild":       "Signup → Build",
  "admin.insights.buildSuccessRate":    "Build success rate",
  "admin.insights.signupToDeploy":      "Signup → Deploy",
  "admin.insights.usersNoProject":      "Users w/no project",
  "admin.insights.failurePatternsTitle":"Build Failure Patterns",
  "admin.insights.last30d":             "(30d)",
  "admin.insights.noBuildFailures":     "No build failures in the last 30 days.",
  "admin.insights.safetyEventsTitle":   "Safety Events",
  "admin.insights.noSafetyEvents":      "No safety events in the last 30 days.",
  "admin.insights.colEventType":        "Event type",
  "admin.insights.colSeverity":         "Severity",
  "admin.insights.colCount":            "Count",
  "admin.insights.topFailingProjects":  "Top Failing Projects",
  "admin.insights.noProjectFailures":   "No project failures recorded.",
  "admin.insights.colProject":          "Project",
  "admin.insights.colOwner":            "Owner",
  "admin.insights.colFailures":         "Failures",
  "admin.insights.topFailingUsers":     "Top Failing Users",
  "admin.insights.noUserFailures":      "No user failures recorded.",
  "admin.insights.colUser":             "User",
  "admin.insights.colFailedBuilds":     "Failed builds",

  // Admin Feature Flags tab
  "admin.featureFlags.toggleFailed":          "Toggle failed",
  "admin.featureFlags.deleteConfirm":         "Delete flag \"{key}\"?\nThis cannot be undone.",
  "admin.featureFlags.deleteFailed":          "Delete failed",
  "admin.featureFlags.keyNameRequired":       "Key and name are required.",
  "admin.featureFlags.rolloutRange":          "Rollout must be 0–100.",
  "admin.featureFlags.saveFailed":            "Save failed.",
  "admin.featureFlags.flagCreated":           "Flag created.",
  "admin.featureFlags.loading":               "Loading feature flags…",
  "admin.featureFlags.intro":                 "Toggle features globally or roll out to a percentage of users. All changes are logged in the Audit Log.",
  "admin.featureFlags.cancel":                "Cancel",
  "admin.featureFlags.newFlag":               "New Flag",
  "admin.featureFlags.newFlagTitle":          "New Feature Flag",
  "admin.featureFlags.keyLabel":              "Key",
  "admin.featureFlags.keyPlaceholder":        "e.g. new_dashboard",
  "admin.featureFlags.keyHelp":               "Lowercase, underscores only. Used in code.",
  "admin.featureFlags.displayNameLabel":      "Display name",
  "admin.featureFlags.displayNamePlaceholder":"New Dashboard",
  "admin.featureFlags.descriptionLabel":      "Description",
  "admin.featureFlags.descriptionPlaceholder":"What this flag controls",
  "admin.featureFlags.rolloutLabel":          "Rollout % (0 = nobody, 100 = everyone)",
  "admin.featureFlags.creating":              "Creating…",
  "admin.featureFlags.createFlag":            "Create Flag",
  "admin.featureFlags.tableTitle":            "Feature Flags",
  "admin.featureFlags.noFlags":               "No feature flags yet. Create one above.",
  "admin.featureFlags.colKey":                "Key",
  "admin.featureFlags.colName":               "Name",
  "admin.featureFlags.colDescription":        "Description",
  "admin.featureFlags.colEnabled":            "Enabled",
  "admin.featureFlags.colRollout":            "Rollout %",
  "admin.featureFlags.colUpdated":            "Updated",
  "admin.featureFlags.colActions":            "Actions",
  "admin.featureFlags.disable":               "Disable",
  "admin.featureFlags.enable":                "Enable",
  "admin.featureFlags.setRollout":            "Set rollout percentage",

  // Admin Logs tab
  "admin.logs.loading":            "Loading logs…",
  "admin.logs.loadingShort":       "Loading…",
  "admin.logs.filterAll":          "All",
  "admin.logs.filterBuildErrors":  "Build errors",
  "admin.logs.filterSafety":       "Safety",
  "admin.logs.filterAudit":        "Audit",
  "admin.logs.filterTransactions": "Transactions",
  "admin.logs.typeBuildError":     "build error",
  "admin.logs.typeSafety":         "safety",
  "admin.logs.typeAudit":          "audit",
  "admin.logs.typeTransaction":    "transaction",
  "admin.logs.filterByEmail":      "Filter by email…",
  "admin.logs.search":             "Search",
  "admin.logs.clear":              "Clear",
  "admin.logs.title":              "Unified Event Log",
  "admin.logs.colDate":            "Date",
  "admin.logs.colType":            "Type",
  "admin.logs.colUser":            "User",
  "admin.logs.colSeverity":        "Severity",
  "admin.logs.colSummary":         "Summary",
  "admin.logs.noEvents":           "No log events found.",
  "admin.logs.pageInfo":           "Page {page} - {count} events",
  "admin.logs.filteredBy":         "· filtered by \"{email}\"",
  "admin.logs.prev":               "Prev",
  "admin.logs.next":               "Next",

  // Admin Simulate tab
  "admin.simulate.notRun":              "Not run",
  "admin.simulate.running":             "Running…",
  "admin.simulate.linkFailed":          "Failed to generate link.",
  "admin.simulate.grantStepFailed":     "Grant step failed: {error}",
  "admin.simulate.unknownError":        "Unknown error",
  "admin.simulate.acuRoundTripPassed":  "Credit round-trip passed: grant +1 credit to {email} succeeded, refund applied.",
  "admin.simulate.acuNoTxId":            "The grant to {email} returned no transaction id, so nothing was refunded. Check that account before re-running.",
  "admin.simulate.acuRefundFailed":      "Granted 1 ACU to {email} but the refund failed: {error}. That 1 ACU is still on the account.",
  "admin.simulate.title":               "Simulation & Health Check",
  "admin.simulate.subtitle":            "Verify platform connectivity and run diagnostics. The ACU check grants and then refunds 1 real ACU on the account you name.",
  "admin.simulate.healthCheckTitle":    "System Health Check",
  "admin.simulate.passing":             "{pass}/{total} passing",
  "admin.simulate.runChecks":           "Run Checks",
  "admin.simulate.runChecksHint":       "Click \"Run Checks\" to test all critical admin RPCs and the Edge Function.",
  "admin.simulate.journeyTitle":        "User Journey Simulation",
  "admin.simulate.journeyHelp":         "Generate a 1-hour login link for any user to simulate their experience in a private window. This does NOT change their account.",
  "admin.simulate.userEmail":           "User email",
  "admin.simulate.generate":            "Generate",
  "admin.simulate.copyLink":            "Copy link",
  "admin.simulate.linkNote":            "Open in incognito. Expires in 1 hour. The user is NOT notified.",
  "admin.simulate.acuTestTitle":        "Credit Grant/Refund Test",
  "admin.simulate.acuTestHelp":         "Grants 1 test credit to a user then immediately refunds it. Verifies the full billing pipeline end-to-end without permanent balance changes.",
  "admin.simulate.targetUserEmail":     "Target user email",
  "admin.simulate.test":                "Test",
  "admin.simulate.acuTestNote":         "All test actions are logged in the Audit Log with reason \"system_test_grant\" / \"system_test_cleanup\".",

  // Admin Database tab
  "admin.database.loadError":      "Could not load schema health.",
  "admin.database.applyError":     "Could not apply the bootstrap.",
  "admin.database.title":          "Database schema",
  "admin.database.allPresent":     "all present",
  "admin.database.missingCount":   "{count} missing",
  "admin.database.refresh":        "Refresh",
  "admin.database.applyBootstrap": "Apply bootstrap",
  "admin.database.noRows":         "No rows yet. They appear here once this table has data.",
  "admin.database.present":        "present",
  "admin.database.missing":        "missing",
  "admin.database.helpText":       "Apply is idempotent and additive (create-if-not-exists only). It never drops a table, column, or row. Requires DATABASE_URL on the server; otherwise apply the SQL manually in the Supabase SQL editor.",

  /* Top bar (brand link, command palette) */
  "topbar.home":        "Nuvra home",
  "topbar.searchAria":  "Search commands",
  "topbar.searchTitle": "Search commands (Ctrl/Cmd + K)",

  /* Sidebar projects group (live project navigation) */
  "sidebarProjects.nBuilding":        "{n} building",
  "sidebarProjects.newProject":       "New project",
  "sidebarProjects.loading":          "Loading",
  "sidebarProjects.empty":            "No projects yet. Start a build →",
  "sidebarProjects.status.building":  "Building",
  "sidebarProjects.status.live":      "Live",
  "sidebarProjects.status.idle":      "Idle",
  "sidebarProjects.moreOnDashboard":  "+{n} more on the dashboard",

  /* Share project dialog (owner-facing share-link management) */
  "share.title":                 "Share \"{name}\"",
  "share.description":           "Anyone signed in who opens a link becomes a member at its role. Revoke a link any time.",
  "share.roleViewer":           "Viewer",
  "share.roleEditor":           "Editor",
  "share.createLink":           "Create share link",
  "share.copyNow":              "Copy this link now. For security it will not be shown again.",
  "share.activeLinks":          "Active links",
  "share.noActiveLinks":        "No active share links.",
  "share.usedN":                "{n} used",
  "share.unused":               "unused",
  "share.expiresSuffix":        " · expires {date}",
  "share.revokeLink":           "Revoke link",
  "share.toast.createFailTitle":"Could not create link",
  "share.toast.createFailBody": "Only the project owner can share this project.",
  "share.toast.copyFailTitle":  "Copy failed",
  "share.toast.copyFailBody":   "Copy the link manually.",
  "share.toast.revokeFailTitle":"Could not revoke",
  "share.toast.revokeFailBody": "Try again.",

  /* Command palette (global command bar, search/navigate/build) */
  "cmdk.section.navigate":      "Navigate",
  "cmdk.section.actions":       "Actions",
  "cmdk.section.favorites":     "Favorites",
  "cmdk.section.suggested":     "Suggested",
  "cmdk.section.recent":        "Recent",
  "cmdk.searchPlaceholder":     "Search commands, navigate, or describe what you want to build...",
  "cmdk.voiceMode":             "Voice Mode",
  "cmdk.voiceInput":            "Voice input",
  "cmdk.undoLabel":             "Undo: {label}",
  "cmdk.confirmLabel":          "Confirm: {label}?",
  "cmdk.confirm":               "Confirm",
  "cmdk.cancel":                "Cancel",
  "cmdk.aiBuild":               "AI Build",
  "cmdk.buildWithAI":           "Build with AI",
  "cmdk.macro":                 "Macro",
  "cmdk.stepsCount":            "{n} steps",
  "cmdk.noMatch":               "No commands match \"{query}\"",
  "cmdk.noMatchHint":           "Try describing what you want to build, or use \"/\" to trigger a macro",
  "cmdk.emptyHint":             "Search commands, navigate, or describe what to build",
  "cmdk.hint.nav":              "Nav",
  "cmdk.hint.run":              "Run",
  "cmdk.hint.undo":             "Undo",
  "cmdk.hint.macro":            "Macro",
  "cmdk.voiceBadge":            "🎙 Voice",
  "cmdk.toast.build":           "→ Build",
  "cmdk.toast.executing":       "Executing...",
  "cmdk.toast.undone":          "↩ Undone",
  "cmdk.toast.nothingToUndo":   "Nothing to undo",
  "cmdk.toast.addedFavorite":   "⭐ Added to favorites",
  "cmdk.toast.removedFavorite": "Removed from favorites",
  "cmdk.toast.listening":       "🎙 Listening...",
  "cmdk.toast.sayCommand":      "Say a command or describe what to build",
  "cmdk.toast.voiceCaptured":   "🎙 Voice captured",
  "cmdk.toast.processing":      "Processing command...",
  "cmdk.toast.macro":           "⚡ Macro: {name}",
  "cmdk.toast.executedSteps":   "Executed {n} steps",

  /* Site footer (marketing footer, navigation + legal) */
  "footer.section.platform":    "Platform",
  "footer.section.product":     "Product",
  "footer.section.legal":       "Legal",
  "footer.link.features":       "Features",
  "footer.link.howItWorks":     "How It Works",
  "footer.link.useCases":       "Use Cases",
  "footer.link.liveDemo":       "Architecture Planner",
  "footer.link.dashboard":      "Dashboard",
  "footer.link.deploy":         "Deploy",
  "footer.link.billing":        "Billing",
  "footer.link.privacyPolicy":  "Privacy Policy",
  "footer.link.termsOfService": "Terms of Service",
  "footer.link.acceptableUse":  "Acceptable Use",
  "footer.link.aiContentPolicy":"AI Content Policy",
  "footer.link.securityPolicy": "Security Policy",
  "footer.social.community":    "Community",
  "footer.rightsReserved":      "All rights reserved.",
  "footer.bottom.privacy":      "Privacy",
  "footer.bottom.terms":        "Terms",
  "footer.bottom.cookies":      "Cookies",

  /* Project backend menu (generated database viewer/editor popover) */
  "backendMenu.triggerTitle":   "Generated database and backend",
  "backendMenu.triggerAria":    "Database",
  "backendMenu.triggerLabel":   "Database",
  "backendMenu.heading":        "Generated backend",
  "backendMenu.tab.deploys":    "Deploys",
  "backendMenu.tab.backups":    "Backups",
  "backendMenu.tab.audit":      "Audit",
  "backendMenu.tab.diagram":    "Diagram",
  "backendMenu.status.liveDatabase": "Live database",
  "backendMenu.status.needsReview":  "Needs review",
  "backendMenu.status.schemaReady":  "Schema ready",
  "backendMenu.deployHistory":  "Deploy history",
  "backendMenu.loading":        "Loading",
  "backendMenu.noDeploys":      "No deploys yet.",
  "backendMenu.open":           "open",
  "backendMenu.backupNow":      "Backup now",
  "backendMenu.noBackups":      "No backups yet.",
  "backendMenu.confirm":        "confirm",
  "backendMenu.cancel":         "cancel",
  "backendMenu.restore":        "restore",
  "backendMenu.restoreTitle":   "Restore overwrites current rows with this snapshot; rows added since are kept",
  "backendMenu.download":       "download",
  "backendMenu.auditLog":       "Data audit log",
  "backendMenu.noChanges":      "No changes recorded yet.",
  "backendMenu.deployed":       "Deployed",
  "backendMenu.deploying":      "Deploying",
  "backendMenu.openLiveApp":    "Open live app",
  "backendMenu.loadingBackend": "Loading backend",
  "backendMenu.noTables":       "No tables yet. Add one below, or Nuvra creates them when it builds a full-stack app.",
  "backendMenu.pending":        "pending",
  "backendMenu.column":         "column",
  "backendMenu.colTo":          "to",
  "backendMenu.colUnique":      "unique",
  "backendMenu.restoreColTitle":"Restore (your data was preserved)",
  "backendMenu.removeColTitle": "Remove from the app (data is kept, reversible)",
  "backendMenu.view":           "view",
  "backendMenu.viewTitle":      "Browse rows (requires a materialized database)",
  "backendMenu.import":         "import",
  "backendMenu.importTitle":    "Import rows from a CSV file",
  "backendMenu.seed":           "seed",
  "backendMenu.seedTitle":      "Insert a few sample rows (requires a materialized database)",
  "backendMenu.reference":      "reference",
  "backendMenu.required":       "required",
  "backendMenu.unique":         "unique",
  "backendMenu.indexed":        "indexed",
  "backendMenu.noReference":    "no reference",
  "backendMenu.referenceTitle": "Reference another table",
  "backendMenu.add":            "Add",
  "backendMenu.isolatedSchema": "Isolated schema",
  "backendMenu.newTable":       "New table",
  "backendMenu.field":          "field",
  "backendMenu.createTable":    "Create table",
  "backendMenu.rebuildDeploy":  "Rebuild & deploy",
  "backendMenu.rebuildTitle":   "Rebuild the app with your schema changes and deploy it live (uses credits)",
  "backendMenu.ph.columnName":  "column name",
  "backendMenu.ph.enumValues":  "allowed values, comma separated",
  "backendMenu.ph.min":         "min",
  "backendMenu.ph.minLength":   "min length",
  "backendMenu.ph.max":         "max",
  "backendMenu.ph.maxLength":   "max length",
  "backendMenu.ph.pattern":     "pattern (regex, optional)",
  "backendMenu.ph.default":     "default (optional)",
  "backendMenu.ph.tableName":   "Table name (e.g. Invoice)",
  "backendMenu.ph.fieldName":   "field name",
  "backendMenu.unit.row":       "row",
  "backendMenu.unit.rows":      "rows",
  "backendMenu.unit.table":     "table",
  "backendMenu.unit.tables":    "tables",
  "backendMenu.notice.tableCreated":    "Created in your database.",
  "backendMenu.notice.savedNextBuild":  "Saved. Applies on the next build.",
  "backendMenu.notice.columnAdded":     "Added to your database.",
  "backendMenu.notice.columnRemoved":   "Removed from the app. Your data is preserved and the change is reversible.",
  "backendMenu.notice.columnRestored":  "Column restored.",
  "backendMenu.notice.relationshipAdded": "Relationship added.",
  "backendMenu.notice.seededOne":       "Seeded {n} sample row into {table}.",
  "backendMenu.notice.seededN":         "Seeded {n} sample rows into {table}.",
  "backendMenu.notice.backedUp":        "Backed up {rows} {rowLabel} across {tables} {tableLabel}.",
  "backendMenu.notice.restored":        "Restored {rows} {rowLabel} across {tables} {tableLabel}.",
  "backendMenu.notice.imported":        "Imported {imported} of {total} {rowLabel} into {table}.",
  "backendMenu.notice.importedSkipped": "{failed} skipped.",
  "backendMenu.notice.rebuilding":      "Rebuilding and deploying. Your live app updates when it finishes.",
  "backendMenu.error.load":             "Could not load the backend.",
  "backendMenu.error.addTable":         "Could not add the table.",
  "backendMenu.error.addColumn":        "Could not add the column.",
  "backendMenu.error.removeColumn":     "Could not remove the column.",
  "backendMenu.error.restoreColumn":    "Could not restore the column.",
  "backendMenu.error.seed":             "Could not seed the table.",
  "backendMenu.error.backup":           "Could not create the backup.",
  "backendMenu.error.restoreBackup":    "Could not restore the backup.",
  "backendMenu.error.import":           "Could not import the CSV.",
  "backendMenu.error.insufficientCredits": "Insufficient credits to deploy.",
  "backendMenu.error.startDeploy":      "Could not start the deploy.",
  "backendMenu.audit.seed":             "seeded",
  "backendMenu.audit.import":           "imported",
  "backendMenu.audit.cellUpdate":       "edited a cell",
  "backendMenu.audit.restore":          "restored a backup",
  "backendMenu.audit.backup":           "backed up",
  "backendMenu.audit.addTable":         "added table",
  "backendMenu.audit.addColumn":        "added column",
  "backendMenu.audit.removeColumn":     "removed column",
  "backendMenu.audit.restoreColumn":    "restored column",
  "backendMenu.audit.addRelationship":  "added relationship",
  "backendMenu.audit.build":            "built the app",
  "backendMenu.audit.buildFailed":      "build failed",
  "backendMenu.audit.lockSection":      "locked a page",
  "backendMenu.audit.unlockSection":    "unlocked a page",
  "backendMenu.audit.hidePage":         "hid a page",
  "backendMenu.audit.showPage":         "restored a page",
  "backendMenu.audit.renamePage":       "renamed a page",
  "backendMenu.audit.hideComponent":    "hid a component",
  "backendMenu.audit.showComponent":    "restored a component",
  "backendMenu.audit.codeEdit":         "edited code",
  "backendMenu.audit.codePush":         "pushed to GitHub",
  "backendMenu.audit.hostDeploy":       "deployed",
  "backendMenu.audit.supabaseConnect":  "connected Supabase",

  /* Strategic memory panel (project reasoning artifacts) */
  "strategicMemory.loading":      "Loading strategy",
  "strategicMemory.error":        "Could not load this project's strategy right now. It will appear once reachable.",
  "strategicMemory.empty.title":  "No strategy captured yet",
  "strategicMemory.empty.body":   "As Nuvra reasons about your product, its blueprint, discovery, and recommendations show up here and follow you across devices.",
  "strategicMemory.title":        "Strategy",
  "strategicMemory.subtitle":     "What Nuvra understands about this product, from concept to recommendations.",
  "strategicMemory.confidence":   "{pct} confidence",
  "strategicMemory.section.concept":         "The concept",
  "strategicMemory.section.discovery":       "Discovery",
  "strategicMemory.section.blueprint":       "Blueprint",
  "strategicMemory.section.implications":    "Implications",
  "strategicMemory.section.recommendations": "Recommendations",
  "strategicMemory.label.value":     "Value:",
  "strategicMemory.label.for":       "For:",
  "strategicMemory.label.distinct":  "What makes it distinct",
  "strategicMemory.label.problems":  "Problems it solves",
  "strategicMemory.label.domain":    "Domain:",
  "strategicMemory.label.form":      "Form:",
  "strategicMemory.label.readiness": "Readiness:",
  "strategicMemory.label.nextSteps": "Next steps",
  "strategicMemory.stat.modules":       "Modules",
  "strategicMemory.stat.pages":         "Pages",
  "strategicMemory.stat.models":        "Models",
  "strategicMemory.stat.roles":         "Roles",
  "strategicMemory.stat.workflows":     "Workflows",
  "strategicMemory.stat.integrations":  "Integrations",
  "strategicMemory.stat.architectural": "Architectural",
  "strategicMemory.stat.requiredCaps":  "Required caps",
  "strategicMemory.stat.risks":         "Risks",
  "strategicMemory.stat.dependencies":  "Dependencies",

  /* Monetization menu (define plans end-users can buy) */
  "monetizationMenu.title":        "Monetization",
  "monetizationMenu.monetize":     "Monetize",
  "monetizationMenu.notOwner":     "Only the project owner can set up monetization.",
  "monetizationMenu.intro":        "Define what your end-users can buy. Gate features by entitlement keys.",
  "monetizationMenu.enabled":      "Enabled",
  "monetizationMenu.featured":     "Featured",
  "monetizationMenu.addPlan":      "Add plan",
  "monetizationMenu.save":         "Save",
  "monetizationMenu.saved":        "Saved.",
  "monetizationMenu.billingActive":"Billing active for {n} plan(s).",
  "monetizationMenu.addPriceId":   "Add a provider price id to a paid plan to enable checkout.",
  "monetizationMenu.error.save":   "Could not save.",
  "monetizationMenu.interval.month": "/ mo",
  "monetizationMenu.interval.year":  "/ yr",
  "monetizationMenu.interval.once":  "once",
  "monetizationMenu.ph.planName":     "Plan name",
  "monetizationMenu.ph.features":     "Features (comma separated)",
  "monetizationMenu.ph.entitlements": "Entitlement keys (comma separated)",
  "monetizationMenu.ph.priceId":      "Provider price id (for checkout)",

  /* Members menu (invite collaborators) */
  "membersMenu.title":        "Members",
  "membersMenu.notOwner":     "Only the project owner can manage members.",
  "membersMenu.invite":       "Invite",
  "membersMenu.invited":      "invited",
  "membersMenu.empty":        "No collaborators yet. Invite someone by email.",
  "membersMenu.role.editor":  "Editor",
  "membersMenu.role.viewer":  "Viewer",
  "membersMenu.ph.email":     "collaborator@email.com",
  "membersMenu.error.invite": "Could not invite",

  /* Marketplace menu (publish and clone templates) */
  "marketplaceMenu.title":         "Marketplace",
  "marketplaceMenu.tab.browse":    "Browse",
  "marketplaceMenu.tab.publish":   "Publish this project",
  "marketplaceMenu.search":        "Search",
  "marketplaceMenu.empty":         "No templates yet. Publish one from any project.",
  "marketplaceMenu.clones":        "{n} clones",
  "marketplaceMenu.clone":         "Clone",
  "marketplaceMenu.publishIntro":  "Publish this project's structure as a reusable template. Secrets, data, and domains are never included.",
  "marketplaceMenu.publish":       "Publish",
  "marketplaceMenu.published":     "Published to the marketplace.",
  "marketplaceMenu.titleTooShort": "Add a title of at least 3 characters.",
  "marketplaceMenu.ph.search":      "Search templates",
  "marketplaceMenu.ph.title":       "Template title",
  "marketplaceMenu.ph.description":  "What does it build?",
  "marketplaceMenu.error.clone":   "Could not clone.",
  "marketplaceMenu.error.publish": "Could not publish.",

  /* Observability menu (deployed app usage, errors, self-healing) */
  "observabilityMenu.triggerTitle": "App observability",
  "observabilityMenu.insights":     "Insights",
  "observabilityMenu.appInsights":  "App insights",
  "observabilityMenu.autoFix":      "Auto Fix",
  "observabilityMenu.mode.fullAuto": "Full Auto",
  "observabilityMenu.mode.safe":     "Safe",
  "observabilityMenu.mode.manual":   "Manual",
  "observabilityMenu.modeDesc.fullAuto": "Low and medium-risk fixes apply automatically. High-risk always asks you first.",
  "observabilityMenu.modeDesc.safe":     "Low-risk fixes apply automatically. Everything else is proposed for your approval.",
  "observabilityMenu.modeDesc.manual":   "Nuvra detects issues and proposes fixes. You approve every fix.",
  "observabilityMenu.noIssues":     "No recurring issues detected. The feed updates as real runtime errors are observed.",
  "observabilityMenu.risk":         "{risk} risk",
  "observabilityMenu.confidence":   "{n}% confidence",
  "observabilityMenu.autoFixing":   "Auto-fixing",
  "observabilityMenu.autoFixWhenKeyed":      "Auto-fix when AI active",
  "observabilityMenu.autoFixWhenKeyedTitle": "This fix qualifies for automatic application; it activates once an AI provider key is configured.",
  "observabilityMenu.approveFix":   "Approve fix",
  "observabilityMenu.dismiss":      "Dismiss",
  "observabilityMenu.noEvents":     "No events yet. Set {ingestVar} and {projectVar} in your deployed app to report usage and errors here.",
  "observabilityMenu.stat.views":    "Views",
  "observabilityMenu.stat.sessions": "Sessions",
  "observabilityMenu.stat.errors":   "Errors",
  "observabilityMenu.stat.logs":     "Logs",
  "observabilityMenu.topPages":     "Top pages",
  "observabilityMenu.errors":       "Errors",
  "observabilityMenu.fixThis":      "Fix this",
  "observabilityMenu.recentLogs":   "Recent logs",

  /* Build chat thread (work-order engineering log) */
  "bct.turn.you":              "You",
  "bct.turn.nuvra":            "Nuvra",
  "bct.turn.buildRequested":   "Build requested",
  "bct.session.expand":             "Inspect session",
  "bct.wo.status.queued":           "Queued",
  "bct.wo.status.understanding":    "Understanding",
  "bct.wo.status.planning":         "Planning",
  "bct.wo.status.implementing":     "Implementing",
  "bct.wo.status.verifying":        "Verifying",
  "bct.wo.status.completed":        "Completed",
  "bct.wo.status.blocked":          "Blocked",
  "bct.wo.status.failed":           "Failed",
  "bct.summary.files": "Files",
  "bct.summary.validation": "Validation",
  "bct.summary.tests": "Tests",
  "bct.summary.preview": "Preview",
  "bct.summary.passed": "passed",
  "bct.summary.review": "needs review",
  "bct.summary.available": "available",
  "bct.summary.none": "none",
  "bct.stage.artifactsGenerated":   "Project files",
  "bct.stage.finalStatus":          "Result",
  "bct.stage.engineeringNote":      "Note",
  "bct.status.queued":              "Queued",
  "bct.status.inProgress":          "In progress",
  "bct.status.failed":              "Failed",
  "bct.status.cancelled":           "Cancelled",
  "bct.status.working":             "Nuvra is working",
  "bct.status.planned":             "Planned",
  "bct.turn.planRequested":         "Plan requested",
  "bct.plan.pages":                 "Pages",
  "bct.plan.dataModels":            "Data models",
  "bct.plan.features":              "Features",
  "bct.stream.jumpToLatest":        "Jump to latest",
  "bct.status.completed":           "Completed",
  "bct.status.note":                "Note",
  "bct.blueprint.pages":            "Pages",
  "bct.blueprint.components":       "Components",
  "bct.blueprint.models":           "Models",
  "bct.blueprint.routes":           "Routes",
  "bct.blueprint.auth":             "Auth",
  "bct.blueprint.deploy":           "Deploy",
  "bct.blueprint.fallbackTitle":    "Application Blueprint",
  "bct.blueprint.suggestedNext":    "Suggested next",
  // Build credits used: the customer-facing record of what one build's credits went on.
  "bcu.title":                      "Credits used",
  "bcu.credits":                    "{count} credits",
  "bcu.note":                       "Shown by the share of work each part of this build took.",
  "bcu.noBreakdown":                "A detailed breakdown is not available for this build.",
  "bcu.zero":                       "This build used no credits.",
  "bcu.stage.understanding":        "Understanding your request",
  "bcu.stage.planning":             "Planning the build",
  "bcu.stage.writing":              "Writing your code",
  "bcu.stage.checking":             "Checking your app",
  "bcu.stage.fixing":               "Fixing issues",
  "bcu.stage.other":                "Other work",
  "bct.report.completed":           "Engineering completed",
  "bct.report.completedWithBlockers": "Engineering completed with blockers",
  "bct.report.blockersHint":        "The build finished, but it is blocked from deployment until these are resolved.",
  "bct.report.scaffoldDraft":       "Draft scaffold generated",
  "bct.report.scaffoldDraftHint":   "This is a deterministic draft, not AI-generated software. Add AI generation to build the real product.",
  "bct.mode.aiGenerated":           "AI-generated",
  "bct.mode.scaffoldDraft":         "Scaffold draft",
  "bct.report.openRuntime":         "Open Runtime",
  "bct.report.capabilities":        "Capabilities",
  "bct.receipt.withBalance":        "This build used {cost} credits. {balance} credits left.",
  "bct.receipt.costOnly":           "This build used {cost} credits.",
  "bct.security.passed":            "Security reviewed: no critical issues found ({score}/100).",
  "bct.security.issues":            "Security reviewed: {critical} critical, {high} high issues flagged.",
  "bct.report.decisions":           "Decisions your team made",
  "bct.report.pages":               "Pages",
  "bct.report.blockers":            "Blockers",
  "bct.report.heading":             "Build details",
  "bct.report.findings":          "Findings & repairs",
  "bct.report.verified":          "Verified",
  "bct.report.artifact":          "Artifact",
  "bct.report.artifactVerified":  "Verified artifact",
  "bct.report.remaining":         "Remaining",
  "bct.report.next":              "Next",
  "bct.report.implemented":         "Implemented",
  "bct.report.requested":      "Requested",
  "bct.report.changedFiles":   "Files changed",
  "bct.report.created":        "created",
  "bct.report.modified":       "modified",
  "bct.report.removed":        "removed",
  "bct.report.andMoreFiles":   "and {n} more",
  "bct.report.filesWritten":        "{n} files written",
  "bct.report.details":             "Details",
  "bct.report.listen":              "Listen",
  "bct.report.stopListening":       "Stop",
  "bct.report.nextInvite":          "Want anything next on this? Describe the change and Nuvra builds on this exact app.",
  "bct.report.hideDetails":         "Hide details",
  "bct.report.preview":             "Preview",
  "bct.error.buildFailed":          "Build failed",
  "bct.error.retry":                "Retry",
  "bct.error.rerun":                "Re-run work order",
  "bct.latestWorkOrder":            "Latest work order",
  "bct.reuseRequestTitle":          "Re-use this request in the composer",
  "bct.msg.edit":                   "Edit",
  "bct.msg.copy":                   "Copy",
  "bct.msg.copied":                 "Copied",
  "bct.msg.share":                  "Share",
  "bct.msg.like":                   "Good response",
  "bct.msg.dislike":                "Bad response",
  "bct.history.loadEarlier":     "Load earlier history",
  "bct.history.loadingEarlier":  "Loading earlier history...",
  "bct.msg.showMore":               "Show more",
  "bct.msg.showLess":               "Show less",
  "bct.msg.rerun":                  "Reuse this request",
  "bct.buildRequestFallback":       "Build request",
  "bct.cap.auth":                   "Authentication",
  "bct.cap.search":                 "Search",
  "bct.cap.payments":               "Payments",
  "bct.cap.realtime":               "Realtime",
  "bct.cap.notifications":          "Notifications",
  "bct.cap.upload":                 "File uploads",
  "bct.cap.export":                 "Data export",
  "bct.cap.analytics":              "Analytics",
  "bct.cap.email":                  "Email",

  /* Extensions page */
  "ext.title":                      "Extensions",
  "ext.subtitle":                   "Add capabilities to your project. You grant each permission explicitly.",
  "ext.noProject":                  "Open this page from a project to install and manage its extensions. You can browse the registry below.",
  "ext.loading":                    "Loading extensions...",
  "ext.errorLoad":                  "Could not load extensions.",
  "ext.installed":                  "Installed",
  "ext.installedNone":              "No extensions installed in this project yet.",
  "ext.permissionsNone":            "No permissions granted.",
  "ext.suspend":                    "Suspend",
  "ext.activate":                   "Activate",
  "ext.uninstall":                  "Uninstall",
  "ext.registry":                   "Registry",
  "ext.searchPlaceholder":          "Search extensions",
  "ext.typeAll":                    "All types",
  "ext.search":                     "Search",
  "ext.registryNone":               "No published extensions yet.",
  "ext.installOpenProject":         "Open a project to install",
  "ext.installIncompat":            "Incompatible with this platform",
  "ext.install":                    "Install",
  "ext.permRequested.one":          "{n} permission requested",
  "ext.permRequested.other":        "{n} permissions requested",
  "ext.installCount.one":           "{n} install",
  "ext.installCount.other":         "{n} installs",
  "ext.modalTitle":                 "Grant permissions",
  "ext.modalDesc":                  "{title} is requesting access. Grant only what you trust.",
  "ext.paidNotice":                 "Paid extension. Purchase it in the marketplace first. Once you own it, installing here completes.",
  "ext.deprecatedNotice":           "This version is deprecated but still works. Consider a newer version when available.",
  "ext.permissionsRequestedNone":   "This extension requests no permissions.",
  "ext.optional":                   "optional",
  "ext.cancel":                     "Cancel",
  "ext.installWithPerm.one":        "Install with {n} permission",
  "ext.installWithPerm.other":      "Install with {n} permissions",
  "ext.badgeVerified":              "Verified",
  "ext.badgeCommunity":             "Community",
  "ext.badgeExperimental":          "Experimental",
  "ext.badgeIncompat":              "Incompatible",
  "ext.badgeDeprecated":            "Deprecated",
  "ext.badgePaid":                  "Paid",
  "ext.toastInstalled":             "Extension installed",
  "ext.toastPurchaseRequired":      "Purchase required",
  "ext.toastPurchaseDesc":          "This extension is paid. Buy it in the marketplace, then install.",
  "ext.toastInstallFailed":         "Install failed",
  "ext.toastTryAgain":              "Try again.",
  "ext.toastActionFailed":          "Action failed",
  "ext.toastPermFailed":            "Could not update permission",

  /* Capabilities page */
  "cap.page.label":   "Platform Intelligence",
  "cap.page.title":   "What Nuvra Can Do",
  "cap.page.desc":    "Every capability here is real, running in production, and available to you right now.",
  "cap.page.allLive": "All capabilities live",
  "cap.filter.all":   "All Capabilities",
  "cap.history.label": "Infrastructure Timeline",
  "cap.history.title": "Platform Build History",
  "cap.history.desc":  "Each phase below is a real, completed, deployed infrastructure release.",
  "cap.footer.live":   "All capabilities live in production",
  "cap.footer.status": "Live platform status",

  /* Capabilities - AI Generation */
  "cap.aiGen.label":    "AI Generation",
  "cap.aiGen.headline": "Every layer of your application, generated in parallel by specialized AI agents",
  "cap.aiGen.multiAgent.name":   "Multi-Agent Code Generation",
  "cap.aiGen.multiAgent.desc":   "Specialized agents run simultaneously. One builds the frontend, one builds the API, one schemas the database, one configures infrastructure. Nothing is sequential.",
  "cap.aiGen.multiAgent.badge":  "Parallel agents",
  "cap.aiGen.aiRouting.name":    "Redundant AI Routing",
  "cap.aiGen.aiRouting.desc":    "If one AI source is unavailable, the system automatically routes to the next. Your generation never stops because of a provider outage.",
  "cap.aiGen.aiRouting.badge":   "Fault-tolerant",
  "cap.aiGen.consensus.name":    "Agent Consensus System",
  "cap.aiGen.consensus.desc":    "Multiple independent agents generate competing outputs. A consensus layer selects the highest-quality result through weighted agreement, not random selection.",
  "cap.aiGen.consensus.badge":   "Consensus",
  "cap.aiGen.adaptive.name":     "Adaptive Routing",
  "cap.aiGen.adaptive.desc":     "Routing decisions improve automatically over time based on outcome data. The system learns which agents perform best for which types of requests.",
  "cap.aiGen.adaptive.badge":    "Adaptive",
  "cap.aiGen.arbitration.name":  "Agent Arbitration",
  "cap.aiGen.arbitration.desc":  "When agents disagree, their outputs compete in structured arbitration rather than random selection. Disagreements produce better output.",
  "cap.aiGen.arbitration.badge": "Multi-agent",
  "cap.aiGen.copilot.name":      "Context-Aware AI Copilot",
  "cap.aiGen.copilot.desc":      "The built-in assistant reads your actual project data, build history, and deployment state before responding. It knows your system, not just your question.",
  "cap.aiGen.copilot.badge":     "Context-aware",
  "cap.aiGen.template.name":     "AI Template Engine",
  "cap.aiGen.template.desc":     "Generates production-ready application templates with version tracking and compatibility validation. Not boilerplate. Fully wired starting points.",
  "cap.aiGen.template.badge":    "Template AI",
  "cap.aiGen.marketplace.name":  "Marketplace Intelligence",
  "cap.aiGen.marketplace.desc":  "AI-powered recommendations for listings, pricing signals, and compatibility analysis across the Nuvra marketplace ecosystem.",
  "cap.aiGen.marketplace.badge": "Marketplace AI",

  /* Capabilities - Deployment Pipeline */
  "cap.deploy.label":    "Deployment Pipeline",
  "cap.deploy.headline": "From generated code to live production URL, a fully automated, self-healing pipeline",
  "cap.deploy.fullStack.name":    "Full-Stack Deployment Engine",
  "cap.deploy.fullStack.desc":    "Orchestrates packaging, distribution, and container activation in a single atomic operation. One button. A complete live system.",
  "cap.deploy.fullStack.badge":   "Full pipeline",
  "cap.deploy.stateMachine.name": "Deployment State Machine",
  "cap.deploy.stateMachine.desc": "Every deployment follows a strict ordered transition: queued, building, deploying, live. Invalid state jumps are blocked. Rollback is automatic on any violation.",
  "cap.deploy.stateMachine.badge": "State enforcement",
  "cap.deploy.healing.name":      "Self-Healing Reconciler",
  "cap.deploy.healing.desc":      "Runs continuously in the background. Finds and repairs stuck builds and stale activations without human intervention.",
  "cap.deploy.healing.badge":     "Self-healing",
  "cap.deploy.repair.name":       "Deployment Repair Engine",
  "cap.deploy.repair.desc":       "When a deployment fails, structured recovery strategies are applied automatically. Most failures resolve without user involvement.",
  "cap.deploy.repair.badge":      "Auto-repair",
  "cap.deploy.atomic.name":       "Atomic Deployment Transactions",
  "cap.deploy.atomic.desc":       "Multi-step deployment operations are treated as a single transaction. If any step fails, all previous steps roll back. No partial deploys.",
  "cap.deploy.atomic.badge":      "Atomic",
  "cap.deploy.cdn.name":          "CDN Activation Layer",
  "cap.deploy.cdn.desc":          "Manages the moment a build goes live: DNS switching, global propagation, and health verification before traffic is routed.",
  "cap.deploy.cdn.badge":         "CDN activation",
  "cap.deploy.governance.name":   "Deployment Governance",
  "cap.deploy.governance.desc":   "Policy enforcement validates every deployment against plan limits, project rules, and resource constraints before execution begins.",
  "cap.deploy.governance.badge":  "Policy",
  "cap.deploy.retryLedger.name":  "Durable Retry Ledger",
  "cap.deploy.retryLedger.desc":  "Every retry attempt is recorded durably. Prevents infinite loops, tracks failure patterns across restarts, and enables post-incident analysis.",
  "cap.deploy.retryLedger.badge": "Durable retry",
  "cap.deploy.garbage.name":      "Artifact Garbage Collection",
  "cap.deploy.garbage.desc":      "Stale build artifacts are cleaned up automatically on a schedule. Storage costs do not creep up as you iterate.",
  "cap.deploy.garbage.badge":     "Cost control",

  /* Capabilities - Mobile Builds */
  "cap.mobile.label":    "Mobile Builds",
  "cap.mobile.headline": "Native Android and iOS builds, real toolchain, real signing, real artifacts",
  "cap.mobile.aab.name":        "Android App Bundle",
  "cap.mobile.aab.desc":        "Full Android build pipeline producing a signed, optimized bundle. Verified before delivery. Ready for Play Store upload.",
  "cap.mobile.aab.badge":       "Android",
  "cap.mobile.apk.name":        "APK Builder",
  "cap.mobile.apk.desc":        "Signed APK with alignment optimization and signature verification. Direct install-ready artifacts.",
  "cap.mobile.apk.badge":       "Android",
  "cap.mobile.ios.name":        "iOS Build",
  "cap.mobile.ios.desc":        "Full archive build chain producing distributable iOS packages. Runs on macOS build hosts with accurate environment detection.",
  "cap.mobile.ios.badge":       "iOS",
  "cap.mobile.pwa.name":        "Progressive Web App to Native",
  "cap.mobile.pwa.desc":        "Wraps Progressive Web Apps into native Play Store-distributable packages. Full PWA-to-native bridge.",
  "cap.mobile.pwa.badge":       "PWA to Native",
  "cap.mobile.concurrent.name": "Concurrent Build Worker Pool",
  "cap.mobile.concurrent.desc": "Multiple mobile builds run in parallel with per-job timeout enforcement and artifact lifecycle management. No queue bottlenecks.",
  "cap.mobile.concurrent.badge": "Concurrent builds",
  "cap.mobile.sdk.name":        "SDK Auto-Provisioner",
  "cap.mobile.sdk.desc":        "Detects missing SDK components and provisions them automatically. Reports the exact missing component instead of a generic failure.",
  "cap.mobile.sdk.badge":       "Toolchain",
  "cap.mobile.governor.name":   "Build Runtime Governor",
  "cap.mobile.governor.desc":   "Manages build daemon lifecycle, memory configuration, and incremental build caching for consistent build performance.",
  "cap.mobile.governor.badge":  "Build runtime",
  "cap.mobile.signing.name":    "Signing Key Management",
  "cap.mobile.signing.desc":    "Full keystore registration with fingerprinting, expiry tracking, and verification. Signing keys are governed, not just stored.",
  "cap.mobile.signing.badge":   "Code signing",
  "cap.mobile.icons.name":      "Icon and Asset Generator",
  "cap.mobile.icons.desc":      "Generates app icon assets at every required density and format for both Android and iOS in one operation.",
  "cap.mobile.icons.badge":     "Asset generation",
  "cap.mobile.manifest.name":   "Manifest Generator",
  "cap.mobile.manifest.desc":   "Generates Android and iOS configuration manifests from project settings. No manual editing required.",
  "cap.mobile.manifest.badge":  "Auto-manifest",
  "cap.mobile.telemetry.name":  "Mobile Build Telemetry",
  "cap.mobile.telemetry.desc":  "Every build lifecycle event is recorded: start, completion, failure, artifact details. Full build history visible on your dashboard.",
  "cap.mobile.telemetry.badge": "Telemetry",

  /* Capabilities - Store Publishing */
  "cap.store.label":    "Store Publishing",
  "cap.store.headline": "Authenticated, governed store submission with real signing and audit trails",
  "cap.store.google.name":       "Google Play Console Integration",
  "cap.store.google.desc":       "Full Play Store API pipeline: create session, upload bundle, update track, commit. Authenticated against your service account.",
  "cap.store.google.badge":      "Play Store",
  "cap.store.appstore.name":     "App Store Connect Integration",
  "cap.store.appstore.desc":     "Full App Store Connect API pipeline with authenticated upload chain for production distribution.",
  "cap.store.appstore.badge":    "App Store",
  "cap.store.staged.name":       "Staged Rollout Manager",
  "cap.store.staged.desc":       "Phased release enforcement with configurable time gates between each stage before promotion. No accidental full rollouts.",
  "cap.store.staged.badge":      "Gated rollout",
  "cap.store.vault.name":        "Encrypted Credential Vault",
  "cap.store.vault.desc":        "Store credentials are encrypted at rest. Decryption happens server-side only. Plaintext credentials never touch the database.",
  "cap.store.vault.badge":       "Encrypted",
  "cap.store.release.name":      "Release Orchestrator",
  "cap.store.release.desc":      "Every release runs through validated stages with per-stage timing: artifact verification, provenance record, credential validation, store submission, and audit record.",
  "cap.store.release.badge":     "Governed pipeline",
  "cap.store.rollback.name":     "Verified Rollback",
  "cap.store.rollback.desc":     "Before initiating a rollback, the system recomputes and verifies artifact integrity. Corrupted artifacts are blocked. You cannot accidentally roll back to a bad build.",
  "cap.store.rollback.badge":    "Verified rollback",
  "cap.store.keyGov.name":       "Signing Key Governance",
  "cap.store.keyGov.desc":       "Signing keys are validated, monitored, and governed. Warns well in advance of expiry. Blocks on certificate mismatch.",
  "cap.store.keyGov.badge":      "Key governance",
  "cap.store.provenance.name":   "Artifact Provenance",
  "cap.store.provenance.desc":   "A tamper-proof hash is computed at build time and stored with the artifact. At release time, the hash is verified. Tamper detection is automatic.",
  "cap.store.provenance.badge":  "Tamper-proof",
  "cap.store.manifestZip.name":  "Manifest ZIP Exporter",
  "cap.store.manifestZip.desc":  "Creates ZIP archives with per-entry integrity records. Every file in the archive is independently verified.",
  "cap.store.manifestZip.badge": "Manifest ZIP",

  /* Capabilities - Execution Infrastructure */
  "cap.exec.label":    "Execution Infrastructure",
  "cap.exec.headline": "Async, durable, memory-bounded job execution with graceful degradation",
  "cap.exec.renderer.name":        "Isolated Media Renderer",
  "cap.exec.renderer.desc":        "Isolated browser rendering for screenshots and media generation. Multiple retry attempts with exponential backoff. Per-context memory bounded.",
  "cap.exec.renderer.badge":       "Sandboxed",
  "cap.exec.asyncQueue.name":      "Async Processing Queue",
  "cap.exec.asyncQueue.desc":      "Async audio and media generation via a durable queue system. Degrades gracefully to synchronous execution when the queue backend is unavailable.",
  "cap.exec.asyncQueue.badge":     "Async queue",
  "cap.exec.aiWorker.name":        "AI Build Execution Worker",
  "cap.exec.aiWorker.desc":        "Async AI code generation execution with memory-bounded concurrency. Scales with available container resources.",
  "cap.exec.aiWorker.badge":       "Execution queue",
  "cap.exec.faultIsolation.name":  "Connection Fault Isolation",
  "cap.exec.faultIsolation.desc":  "After repeated failures, the system pauses and retries on a timer. Prevents cascade failures from a temporary backend outage.",
  "cap.exec.faultIsolation.badge": "Fault isolation",
  "cap.exec.heartbeat.name":       "Worker Heartbeat",
  "cap.exec.heartbeat.desc":       "Workers register health state periodically. Dead workers are detected automatically, enabling job rebalancing without manual intervention.",
  "cap.exec.heartbeat.badge":      "Health tracking",
  "cap.exec.retryScheduler.name":  "Durable Retry Scheduler",
  "cap.exec.retryScheduler.desc":  "Failed operations are re-enqueued on a schedule rather than discarded. Retry state survives server restarts.",
  "cap.exec.retryScheduler.badge": "Durable",
  "cap.exec.retryConsumer.name":   "Retry Consumer",
  "cap.exec.retryConsumer.desc":   "Processes retry jobs with configurable backoff, records outcomes, and escalates after maximum attempts.",
  "cap.exec.retryConsumer.badge":  "Backoff",
  "cap.exec.eventBridge.name":     "Cross-Service Event Bridge",
  "cap.exec.eventBridge.desc":     "Routes events between real-time connections, database subscriptions, and processing queues. Build events, deployment state changes, and alerts flow through a single hub.",
  "cap.exec.eventBridge.badge":    "Event routing",

  /* Capabilities - Observability */
  "cap.observ.label":    "Observability",
  "cap.observ.headline": "Real-time visibility into every layer of the platform, zero fabricated metrics",
  "cap.observ.bootValidator.name":    "Boot-Time Infrastructure Validator",
  "cap.observ.bootValidator.desc":    "Validates that all required backend services are reachable before accepting traffic. Fails safely if critical services are absent.",
  "cap.observ.bootValidator.badge":   "Boot safety",
  "cap.observ.releaseTelemetry.name": "Release Event Telemetry",
  "cap.observ.releaseTelemetry.desc": "Every release pipeline event is written as an append-only record. The telemetry layer never throws and never interrupts the pipeline it observes.",
  "cap.observ.releaseTelemetry.badge": "Append-only",
  "cap.observ.auditLog.name":         "Immutable Release Audit Log",
  "cap.observ.auditLog.desc":         "Every release decision, credential access, and store submission is written to a compliance log. Records are never modified or deleted.",
  "cap.observ.auditLog.badge":        "Compliance",
  "cap.observ.platformStatus.name":   "Platform Status Service",
  "cap.observ.platformStatus.desc":   "Real-time health polling across all services with incident detection, subscriber callbacks for status changes, and persistent state.",
  "cap.observ.platformStatus.badge":  "Live status",
  "cap.observ.buildLog.name":         "Mobile Build Event Log",
  "cap.observ.buildLog.desc":         "Complete lifecycle event recording for every mobile build job: start time, duration, artifact details, failure reasons, and retry history.",
  "cap.observ.buildLog.badge":        "Build telemetry",
  "cap.observ.runtimeValidator.name": "Runtime Execution Validator",
  "cap.observ.runtimeValidator.desc": "Validates execution node health and output integrity during active build pipeline runs. Catches corrupt outputs before they propagate.",
  "cap.observ.runtimeValidator.badge": "In-pipeline",
  "cap.observ.integrity.name":        "Artifact Integrity Verifier",
  "cap.observ.integrity.desc":        "Independently verifies mobile build artifact signatures and checksums before any release submission. A separate check from the build process itself.",
  "cap.observ.integrity.badge":       "Integrity",
  "cap.observ.metrics.name":          "System Metrics Endpoint",
  "cap.observ.metrics.desc":          "Exposes queue depths, active job counts, memory usage, and connection status. Real-time infrastructure visibility for platform operators.",
  "cap.observ.metrics.badge":         "Metrics",

  /* Capabilities - Security */
  "cap.security.label":    "Security",
  "cap.security.headline": "Defense-in-depth at every layer: encryption, isolation, access control, audit trails",
  "cap.security.sandbox.name":     "Isolated Render Sandbox",
  "cap.security.sandbox.desc":     "Every browser render executes in a dedicated isolated context with network protection, content security enforcement, and bounded memory per render.",
  "cap.security.sandbox.badge":    "Sandboxed",
  "cap.security.vault.name":       "Encrypted Credential Vault",
  "cap.security.vault.desc":       "Store credentials are encrypted at rest using strong encryption. Only decrypted inside the server process. The key never appears in logs, responses, or client code.",
  "cap.security.vault.badge":      "Field encryption",
  "cap.security.adminGate.name":   "Timing-Safe Admin Gate",
  "cap.security.adminGate.desc":   "Admin API key comparison is immune to timing attacks. Fails closed if the admin key environment variable is not configured.",
  "cap.security.adminGate.badge":  "Timing-safe",
  "cap.security.authConn.name":    "Authenticated Real-Time Connections",
  "cap.security.authConn.desc":    "Every real-time connection authenticates a signed session token before room access is granted. Any auth error closes the connection. No partial auth.",
  "cap.security.authConn.badge":   "Token-gated",
  "cap.security.rateLimit.name":   "API Rate Limiting",
  "cap.security.rateLimit.desc":   "Incoming requests are limited per IP address. Enforced before request processing begins.",
  "cap.security.rateLimit.badge":  "Rate-limited",
  "cap.security.auditSvc.name":    "Security Audit Service",
  "cap.security.auditSvc.desc":    "Security-significant events are written to a dedicated audit log for post-incident forensics and compliance reporting.",
  "cap.security.auditSvc.badge":   "Forensics",
  "cap.security.fieldEnc.name":    "Field-Level Encryption",
  "cap.security.fieldEnc.desc":    "General-purpose field-level encryption utilities for any sensitive data stored at rest, beyond just store credentials.",
  "cap.security.fieldEnc.badge":   "At-rest",
  "cap.security.roomAuth.name":    "Project Room Authorization",
  "cap.security.roomAuth.desc":    "Real-time project room joins require ownership verification via database lookup. Input format is validated before any query executes.",
  "cap.security.roomAuth.badge":   "Ownership check",

  /* Capabilities - Real-time Infrastructure */
  "cap.realtime.label":    "Real-time Infrastructure",
  "cap.realtime.headline": "Live event delivery across every surface: builds, deployments, system health",
  "cap.realtime.server.name":    "Authenticated Real-Time Server",
  "cap.realtime.server.desc":    "Live event delivery with token-authenticated per-user and per-project rooms. Keep-alive intervals maintain connection stability.",
  "cap.realtime.server.badge":   "Real-time",
  "cap.realtime.logStream.name": "Live Build Log Streaming",
  "cap.realtime.logStream.desc": "Raw WebSocket channel streams build logs in real time as they are generated. Low-latency, separate from the main event connection.",
  "cap.realtime.logStream.badge": "Log streaming",
  "cap.realtime.dbSubs.name":    "Database Change Subscriptions",
  "cap.realtime.dbSubs.desc":    "Database change subscriptions on build history and user profiles power live dashboard updates without polling.",
  "cap.realtime.dbSubs.badge":   "DB realtime",
  "cap.realtime.hook.name":      "Observability Hook",
  "cap.realtime.hook.desc":      "Client-side hook that subscribes to live infrastructure health events and surfaces them to dashboard components in real time.",
  "cap.realtime.hook.badge":     "Client hook",

  /* Capabilities - Game Platform */
  "cap.game.label":    "Game Platform",
  "cap.game.headline": "SDK generation, matchmaking configuration, and economy scaffolding for game backends",
  "cap.game.gameSdk.name":     "Game Engine SDK Export",
  "cap.game.gameSdk.desc":     "Generates production-ready SDK integration code for Unity, Unreal, Godot, and web engines targeting your deployed backend.",
  "cap.game.gameSdk.badge":    "SDK export",
  "cap.game.multiplayer.name": "Multiplayer Infrastructure Templates",
  "cap.game.multiplayer.desc": "Matchmaking algorithms, room management, spectator support, and reconnection handling generated as wirable backend configuration, not hardcoded.",
  "cap.game.multiplayer.badge": "Multiplayer",
  "cap.game.economy.name":     "Game Economy System",
  "cap.game.economy.desc":     "Virtual currency, item catalog, drop tables, and player inventory scaffolding. Economy definitions are generated and can be deployed to your game backend.",
  "cap.game.economy.badge":    "Economy",
  "cap.game.analytics.name":   "Game Analytics Engine",
  "cap.game.analytics.desc":   "Player behavior tracking, session analytics, funnel metrics, and retention cohorts generated as a wirable analytics backend for your game.",
  "cap.game.analytics.badge":  "Analytics",
  "cap.game.aiBalance.name":   "AI Balance Engine",
  "cap.game.aiBalance.desc":   "Game balancing recommendations based on win-rate analysis, economy flow, and retention data. Feeds configuration back into economy and matchmaking systems.",
  "cap.game.aiBalance.badge":  "Balancing AI",
  "cap.game.scale.name":       "Scalability Configuration Panel",
  "cap.game.scale.desc":       "Server scaling configuration for game deployments: player capacity targets, region distribution, auto-scaling thresholds, and load distribution policies.",
  "cap.game.scale.badge":      "Scale config",

  /* Capabilities - Releases */
  "cap.release.storeRelease.title":  "Release Infrastructure and Store Governance",
  "cap.release.storeRelease.desc":   "End-to-end store publishing with encrypted credential vault, authenticated store adapters, governed release pipeline, and immutable audit log.",
  "cap.release.storeRelease.tag":    "Release",
  "cap.release.mobileRelease.title": "Mobile Build System",
  "cap.release.mobileRelease.desc":  "Native Android and iOS build workers with real toolchain detection, build runtime governor, signing infrastructure, concurrent worker pool, and full build telemetry.",
  "cap.release.mobileRelease.tag":   "Mobile",
  "cap.release.asyncRelease.title":  "Async Execution Infrastructure",
  "cap.release.asyncRelease.desc":   "Async AI execution queue, durable retry scheduler, worker heartbeat monitoring, connection fault isolation, and cross-service event bridge.",
  "cap.release.asyncRelease.tag":    "Execution",
  "cap.release.mediaRelease.title":  "Media and Render Sandbox",
  "cap.release.mediaRelease.desc":   "Isolated render sandbox with network protection, content security enforcement, and bounded memory per render.",
  "cap.release.mediaRelease.tag":    "Media",
  "cap.release.deployRelease.title": "Deployment Self-Healing",
  "cap.release.deployRelease.desc":  "Deployment state machine with ordered transition enforcement, background reconciler, repair engine, atomic transactions, and durable retry infrastructure.",
  "cap.release.deployRelease.tag":   "Deployment",
  "cap.release.coreRelease.title":   "Core AI Generation and Auth",
  "cap.release.coreRelease.desc":    "Multi-agent code generation, redundant AI routing, agent consensus and arbitration systems, authentication, and real-time infrastructure.",
  "cap.release.coreRelease.tag":     "Foundation",

  /* LandingPage - Navbar */
  "landing.nav.features":         "Features",
  "landing.nav.howItWorks":       "How It Works",
  "landing.nav.useCases":         "Use Cases",
  "landing.nav.tryIt":            "Planner",
  "landing.nav.architecture":     "Architecture",
  "landing.nav.pricing":     "Pricing",
  "landing.nav.signIn":           "Sign in",

  /* LandingPage - Hero */
  "landing.hero.badge":           "Autonomous Software Engineering Platform",
  "landing.hero.h1a":             "Describe it.",
  "landing.hero.h1b":             "Nuvra builds and ships it.",
  "landing.hero.desc":            "Nuvra is an autonomous software engineering platform: it designs the architecture, builds the code, validates the system, and moves it through governed deployment from a single conversation.",
  "landing.hero.ctaPrimary":      "Start Building",
  "landing.hero.ctaSecondary":    "See It Live",

  "landing.hero.pipelineLabel":  "How Nuvra executes",
  "landing.hero.pipelineDesc":   "A concise view of the autonomous software pipeline.",
  "landing.hero.pipelineStatus": "Pipeline active",
  /* LandingPage - Product Explanation */
  "landing.explain.label":        "What Nuvra Does",
  "landing.explain.h2":           "Not assistance. Execution.",
  "landing.explain.desc":         "Describe what you want to build. Nuvra generates the full system and ships it, no manual steps.",

  "landing.hero.proof.architecture": "Architecture first",
  "landing.hero.proof.code":         "Real code",
  "landing.hero.proof.validation":   "Automated validation",
  "landing.hero.proof.control":      "Production control",

  "landing.capabilities.label": "Beyond the first build",
  "landing.capabilities.h2": "The factory keeps working after the prompt.",
  "landing.capabilities.desc": "Nuvra is more than generation. The platform carries the product through mobile builds, publishing, deployment, memory, and connected engineering work.",
  "landing.capability.mobile.title": "Mobile builds",
  "landing.capability.mobile.desc": "Generate mobile application output alongside the web product, with real project artifacts and build paths.",
  "landing.capability.publishing.title": "Store readiness",
  "landing.capability.publishing.desc": "Prepare the product for distribution with store metadata, policy documents, screenshots, and readiness checks.",
  "landing.capability.externalDeploy.title": "Deployment options",
  "landing.capability.externalDeploy.desc": "Move from preview to supported deployment targets without rebuilding the product around a new stack.",
  "landing.capability.workspace.title": "Engineering workspace",
  "landing.capability.workspace.desc": "Requirements, architecture, work orders, builds, environments, deployments, and integrations stay in one place.",
  "landing.capability.memory.title": "Memory and provenance",
  "landing.capability.memory.desc": "Project context, lineage, provenance, and durable engineering history keep future work connected to what came before.",
  "landing.capability.integrations.title": "Connected services",
  "landing.capability.integrations.desc": "Use governed integrations and stored credentials to connect the software factory to the services your product depends on.",

  /* LandingPage - Stats */
  "landing.live.builders":             "Builders",
  "landing.live.projects":             "Projects created",
  "landing.live.builds":               "Apps shipped",
  "landing.live.deployments":          "Deployments",
  "landing.proof.label":                "Platform activity",
  "landing.proof.desc":                 "Measured from Nuvra's public platform telemetry.",
  "landing.lifecycle.label":            "Nuvra build lifecycle",
  "landing.lifecycle.summary":          "Architecture → validation → deployment",
  "landing.lifecycle.note":             "Product workflow shown for illustration. The interactive planner below is the live, server-backed experience.",
  "landing.lifecycle.intent":            "Intent",
  "landing.lifecycle.architecture":      "Architecture",
  "landing.lifecycle.database":          "Database",
  "landing.lifecycle.backend":           "Backend",
  "landing.lifecycle.frontend":          "Frontend",
  "landing.lifecycle.validation":        "Validation",
  "landing.lifecycle.deployment":        "Deployment",
  "landing.lifecycle.production":        "Production",
  "landing.stat.architecture.value": "Autonomous",
  "landing.stat.architecture.label": "Architecture",
  "landing.stat.code.value":         "Generated",
  "landing.stat.code.label":         "Code",
  "landing.stat.deployment.value":   "Automated",
  "landing.stat.deployment.label":   "Deployment",
  "landing.stat.delivery.value":     "Production",
  "landing.stat.delivery.label":     "Delivery",

  /* LandingPage - Chips */
  "landing.chip.saas":            "SaaS",
  "landing.chip.fintech":         "Fintech",
  "landing.chip.market":          "Marketplace",
  "landing.chip.mobileApp":       "Mobile App",
  "landing.chip.enterprise":      "Enterprise",
  "landing.chip.aiTool":          "AI Tool",
  "landing.chip.gamePlatform":    "Game Platform",
  "landing.chip.dashboard":       "Dashboard",

  /* LandingPage - How It Works */
  "landing.howItWorks.label":     "Autonomous Build Pipeline",
  "landing.howItWorks.h2":        "One description. Full system.",
  "landing.howItWorks.desc":      "Every stage runs without you. Architecture, code, validation, and deployment are fully automated.",
  "landing.pipeline.sequential.label": "Sequential stage execution",
  "landing.pipeline.sequential.desc":  "Each stage validates before the next begins, so a build never advances on unverified work.",
  "landing.pipeline.repair.label":     "Autonomous repair",
  "landing.pipeline.repair.desc":      "Validation failures trigger targeted self-healing.",
  "landing.pipeline.zerotouch.label":  "Zero-touch deployment",
  "landing.pipeline.zerotouch.desc":   "Preview to production in a single orchestrated operation.",

  /* LandingPage - Try It */
  "landing.tryIt.label":          "Architecture Planner",
  "lpp.panelLabel":               "Nuvra architecture planner",
  "landing.tryIt.h2":             "Watch Nuvra Plan Your App",
  "landing.tryIt.desc":           "Type a prompt and Nuvra maps the real architecture from it: entities, database schema, APIs, and access control. Sign up to generate the full, working build.",

  /* LandingPage - System Scale */
  "landing.scale.label":          "Full-Stack Generation",
  "landing.scale.h2":             "Every Layer. Generated Autonomously.",
  "landing.scale.desc":           "Nuvra generates and deploys your application across a full platform stack, UI, schema design, testing, and deployment, all automated.",
  "landing.layer.frontend":       "Frontend Apps",
  "landing.layer.backend":        "Backend Services",
  "landing.layer.database":       "Databases",
  "landing.layer.infra":          "Infrastructure",
  "landing.layer.analytics":      "Analytics",
  "landing.layer.automation":     "Automation",

  /* LandingPage - Use Cases */
  "landing.useCases.label":       "Use Cases",
  "landing.useCases.h2":          "Build Anything",
  "landing.useCases.exploreTemplate": "See what Nuvra builds",
  "landing.useCase.saas.title":   "SaaS Platforms",
  "landing.useCase.saas.desc":    "Multi-tenant apps with auth, billing, and dashboards",
  "landing.useCase.market.title": "Marketplaces",
  "landing.useCase.market.desc":  "Buyer-seller platforms with payments and search",
  "landing.useCase.fintech.title": "Fintech Apps",
  "landing.useCase.fintech.desc": "Financial tools with secure transactions",
  "landing.useCase.health.title": "Healthcare",
  "landing.useCase.health.desc":  "Patient portals, scheduling, and health management apps",
  "landing.useCase.gov.title":    "Government Portals",
  "landing.useCase.gov.desc":     "Citizen service platforms at scale",
  "landing.useCase.mobile.title": "Mobile Apps",
  "landing.useCase.mobile.desc":  "Cross-platform mobile backends and APIs",
  "landing.useCase.game.title":   "3D Games",
  "landing.useCase.game.desc":    "Playable 3D worlds with physics, scenes, and saved progress",
  "landing.useCase.ai.title":     "AI Dashboards",
  "landing.useCase.ai.desc":      "Analytics and intelligence visualization",

  /* LandingPage - Features */
  "landing.features.label":       "Factory Capabilities",
  "landing.features.h2":          "How the Factory Operates",
  "landing.features.learnMore":   "Learn more",
  "landing.feature.intent.title":  "Intent Engine",
  "landing.feature.intent.desc":   "Describe it in plain language. Nuvra resolves your intent into a full system blueprint before anything runs.",
  "landing.feature.pipeline.title": "Autonomous Build Pipeline",
  "landing.feature.pipeline.desc":  "Every layer of your app is built autonomously through a sequential pipeline: intent analysis, code generation, security scanning, testing, and deployment.",
  "landing.feature.preview.title":  "Live Preview",
  "landing.feature.preview.desc":   "Every build gets an isolated preview environment. See it running and interact with it before you ship.",
  "landing.feature.deploy.title":   "One-Click Deploy",
  "landing.feature.deploy.desc":    "From build to a live production URL in a single operation. No configuration, no CI setup required.",
  "landing.feature.healing.title":  "Self-Healing Repair",
  "landing.feature.healing.desc":   "Recurring faults are detected automatically and turned into targeted fix proposals you approve with one click. Nothing is patched behind your back.",
  "landing.feature.monitor.title":  "Build Monitoring",
  "landing.feature.monitor.desc":   "Success rates, latency, and deployment state are tracked in real time so you always know what shipped.",

  /* LandingPage - Enterprise */
  "landing.enterprise.label":     "Enterprise Ready",
  "landing.enterprise.h2":        "Built for Production",
  "landing.enterprise.hint":      "Click to explore each capability",

  /* LandingPage - System Architecture */
  "landing.arch.label":           "Platform Depth",
  "landing.arch.h2":              "Production-grade. Every layer.",
  "landing.arch.desc":            "Core capabilities are production-ready and active.",
  "landing.arch.ctaAll":          "Explore All Capabilities",
  "landing.arch.ai.label":        "AI and Generation",
  "landing.arch.ai.desc":         "AI intent resolution, staged code generation, and autonomous self-repair.",
  "landing.arch.deploy.label":    "Deployment",
  "landing.arch.deploy.desc":     "Automated build pipeline from code to live production URL with health probe verification.",
  "landing.arch.mobile.label":    "Mobile Builds",
  "landing.arch.mobile.desc":     "Native Android and iOS packaging with signing support, ready for store submission.",
  "landing.arch.store.label":     "Store Publishing",
  "landing.arch.store.desc":      "Store-ready listings: generated privacy policy, terms, descriptions, screenshots, and submission readiness checks.",
  "landing.arch.exec.label":      "Execution Engine",
  "landing.arch.exec.desc":       "High-throughput job execution with priority scheduling, queuing, and live metrics.",
  "landing.arch.observ.label":    "Observability",
  "landing.arch.observ.desc":     "Build telemetry, release audit logs, system health, and incident tracking built in.",
  "landing.arch.security.label":  "Security",
  "landing.arch.security.desc":   "Encrypted credential vault, network protection, secure auth, and content policy.",
  "landing.arch.realtime.label":  "Real-time",
  "landing.arch.realtime.desc":   "Live build streaming, event-driven status updates, and real-time system monitoring.",

  /* LandingPage - Final CTA */
  "landing.cta.h2a":              "Describe it once.",
  "landing.cta.h2b":              "Ship it today.",
  "landing.cta.desc":             "No boilerplate. No configuration. No manual wiring. Nuvra generates, orchestrates, and deploys your system end-to-end.",
  "landing.cta.buildWithNuvra":   "Build with Nuvra",

  /* LandingPage - Product-led proof, control plane, pricing and FAQ */
  "landing.nav.menu": "Open menu",
  "landing.nav.close": "Close menu",
  "landing.difference.label": "The Nuvra difference",
  "landing.howItWorks.liveH2": "Watch the system move",
  "landing.principles.label": "Engineering principles",
  "landing.principles.h2": "Built like software, not a demo.",
  "landing.principles.desc": "Nuvra turns intent into structured engineering work, then keeps execution bounded, observable, and verifiable.",
  "landing.principle.intent.title": "Intent before code",
  "landing.principle.intent.desc": "Requirements are resolved into a system contract before generation starts.",
  "landing.principle.schema.title": "Schema-first execution",
  "landing.principle.schema.desc": "Architecture, data, services, and interfaces are planned as structured outputs instead of free-form code.",
  "landing.principle.verify.title": "Verification in the loop",
  "landing.principle.verify.desc": "Build stages are designed around validation, repair, and evidence rather than a single generation pass.",
  "landing.principle.govern.title": "Governed autonomy",
  "landing.principle.govern.desc": "Execution, permissions, infrastructure actions, and production changes stay inside explicit controls.",
  "landing.control.label": "The Nuvra control plane",
  "landing.control.h2": "Your software factory has a cockpit.",
  "landing.control.desc": "Requirements, architecture, work orders, builds, environments, deployments, memory, and integrations live in one engineering workspace.",
  "landing.control.cta": "Open Nuvra",
  "landing.control.window": "Nuvra engineering workspace",
  "landing.control.status": "Operational surface",
  "landing.control.note": "These are real Nuvra workspace surfaces, not a decorative dashboard.",
  "landing.control.surface.requirements.title": "Requirements",
  "landing.control.surface.requirements.desc": "Product contract",
  "landing.control.surface.architecture.title": "Architecture",
  "landing.control.surface.architecture.desc": "System design",
  "landing.control.surface.workOrders.title": "Work Orders",
  "landing.control.surface.workOrders.desc": "Permanent engineering requests",
  "landing.control.surface.builds.title": "Builds",
  "landing.control.surface.builds.desc": "Execution history",
  "landing.control.surface.environments.title": "Environments",
  "landing.control.surface.environments.desc": "Runtime targets",
  "landing.control.surface.deployments.title": "Deployments",
  "landing.control.surface.deployments.desc": "Release state",
  "landing.control.surface.memory.title": "Memory",
  "landing.control.surface.memory.desc": "Project context",
  "landing.control.surface.integrations.title": "Integrations",
  "landing.control.surface.integrations.desc": "Connected services",
  "landing.pricing.label": "Plans",
  "landing.pricing.h2": "Start small. Scale the factory.",
  "landing.pricing.desc": "Choose the level of engineering control that fits the software you are building. See the full feature matrix and current limits on pricing.",
  "landing.pricing.popular": "Popular",
  "landing.pricing.custom": "Custom",
  "landing.pricing.month": "/ month",
  "landing.pricing.noMonthly": "One-time / included",
  "landing.pricing.cta": "View full pricing",
  "landing.faq.label": "Questions",
  "landing.faq.h2": "Before you build",
  "landing.faq.desc": "The important answers, without marketing fog.",
  "landing.faq.what.q": "Is Nuvra just an AI coding chatbot?",
  "landing.faq.what.a": "No. Nuvra is designed as an autonomous software engineering platform: intent and requirements are resolved first, architecture is planned, execution is staged, and verification and deployment are part of the workflow.",
  "landing.faq.real.q": "Is the architecture planner actually live?",
  "landing.faq.real.a": "Yes. The planner in this page calls Nuvra's public architecture-preview service and returns a real architecture derived from your prompt. It does not require an account for the preview.",
  "landing.faq.control.q": "How much control do I keep?",
  "landing.faq.control.a": "Nuvra exposes requirements, architecture, work orders, builds, environments, deployments, memory, and integrations so you can inspect and govern the engineering process instead of handing everything to a black box.",
  "landing.faq.deploy.q": "Can Nuvra deploy software?",
  "landing.faq.deploy.a": "Nuvra has production deployment capabilities and provider integrations. What can be deployed depends on the project's architecture, connected infrastructure, permissions, and plan.",
  "landing.faq.ownership.q": "Do I own what Nuvra builds?",
  "landing.faq.ownership.a": "Nuvra's public plans include code ownership and export capabilities. Exact options vary by plan; the pricing page shows the current entitlement matrix.",

  /* OrgAdminShell */
  "orgAdmin.noOrg":               "No org specified.",
  "orgAdmin.verifying":           "Verifying access...",
  "orgAdmin.accessDeniedTitle":   "Access restricted",
  "orgAdmin.accessDeniedBody":    "You need org admin or owner access to view this page.",
  "orgAdmin.title":               "Organization admin",
  "orgAdmin.subtitle":            "Manage AI policy, members, SSO, compliance, service accounts, and deployment configuration.",
  "orgAdmin.tab.overview":        "Overview",
  "orgAdmin.tab.members":         "Members",
  "orgAdmin.tab.aiPolicy":        "AI policy",
  "orgAdmin.tab.audit":           "Audit log",
  "orgAdmin.tab.whiteLabel":      "White-label",
  "orgAdmin.tab.ssoCompliance":   "SSO and compliance",
  "orgAdmin.tab.serviceAccounts": "Service accounts",
  "orgAdmin.tab.sharedCredentials": "Shared credentials",
  "orgAdmin.tab.webhooks":        "Webhooks",
  "orgAdmin.tab.deployment":      "Deployment",

  /* VoiceNoteButton */
  "voice.micUnavailable":           "Microphone is not available in this browser.",
  "voice.notConfigured":            "Voice transcription is not configured yet.",
  "voice.noCredits":                "Not enough credits for transcription.",
  "voice.transcribeFailed":         "Could not transcribe the note.",
  "voice.micDenied":                "Microphone permission denied.",
  "voice.stopRecording":            "Stop recording",
  "voice.transcribing":             "Transcribing",
  "voice.record":                   "Record a voice note",

  /* NotificationPanel */
  "notif.title":                    "Notifications",
  "notif.markAllRead":              "Mark all read",
  "notif.empty":                    "No notifications yet",
  "notif.enablePush":               "Enable on this device",
  "notif.pushOn":                   "Push on for this device",
  "notif.pushDenied":               "Notifications blocked in your browser settings",
  "notif.pushEnabling":             "Enabling...",

  /* UpgradeModal */
  "upgrade.maybeLater":             "Maybe later",
  "upgrade.feature.unlimitedBuilds":"Unlimited builds",
  "upgrade.feature.aiAgents":       "AI Agents",
  "upgrade.feature.priorityQueue":  "Priority queue",
  "upgrade.feature.customDomains":  "Custom domains",

  /* CapabilityStrip */
  "capStrip.title":                 "Platform Capabilities",
  "capStrip.viewAll":               "Platform capabilities",
  "capStrip.aiGen.label":           "AI Generation",
  "capStrip.aiGen.desc":            "AI generation pipeline",
  "capStrip.webDeploy.label":       "Web Deploy",
  "capStrip.webDeploy.desc":        "Live preview and production URL",
  "capStrip.mobile.label":          "Mobile Builds",
  "capStrip.mobile.desc":           "AAB, APK, IPA, TWA",
  "capStrip.store.label":           "Store Publishing",
  "capStrip.store.desc":            "Google Play and App Store",
  "capStrip.rollout.label":         "Staged Rollout",
  "capStrip.rollout.desc":          "Gated percentage rollout",
  "capStrip.repair.label":          "AI Repair",
  "capStrip.repair.desc":           "Autonomous build recovery",
  "capStrip.multiAi.label":         "Multi-AI",
  "capStrip.multiAi.desc":          "3-provider AI cascade",
  "capStrip.integrity.label":       "Artifact Integrity",
  "capStrip.integrity.desc":        "SHA-256 and tamper detection",
  "capStrip.realtime.label":        "Real-time Events",
  "capStrip.realtime.desc":         "Event bus and job queues",
  "capStrip.rollback.label":        "Rollback",
  "capStrip.rollback.desc":         "Verified rollback to prior build",
  "capStrip.whatsNew.label":        "What's New",
  "capStrip.whatsNew.desc":         "Latest platform releases",

  /* LiveSystemStatus */
  "sys.runtimeHealth":              "Runtime Health",
  "sys.live":                       "Live",
  "sys.status.operational":         "Operational",
  "sys.status.degraded":            "Degraded",
  "sys.status.outage":              "Outage",

  /* ConnectorCard */
  "connector.badge.enabled":        "Enabled",
  "connector.badge.new":            "New",
  "connector.badge.popular":        "Popular",
  "connector.badge.required":       "Required by your app",
  "connector.configure":            "Configure",
  "connector.connect":              "Connect",

  /* FactoryProgressPanel */
  "factory.title":                  "Software Factory",
  "factory.stage.planning":         "Analyzing prompt",
  "factory.stage.architecture":     "Designing architecture",
  "factory.stage.models":           "Generating data models",
  "factory.stage.workflows":        "Creating workflows",
  "factory.stage.api":              "Configuring API routes",
  "factory.stage.components":       "Generating UI components",
  "factory.stage.design":           "Creating design system",
  "factory.stage.deployment":       "Preparing deployment",
  "factory.stage.mobile":           "Preparing mobile builds",
  "factory.stage.materializing":    "Materializing files",
  "factory.stage.complete":         "Complete",
  "factory.stage.failed":           "Failed",

  /* InteractiveProjectGrid */
  "projects.grid.title":            "Your Projects",
  "projects.grid.loading":          "Loading your engineering work...",
  "projects.grid.startNew":         "Start a new project",
  "projects.grid.emptyHint":        "Describe your idea above and your engineering team will start building it.",
  "projects.grid.inProgress":       "Engineering in progress...",
  "projects.grid.lastActivity":     "Last activity {t}",
  "projects.grid.notBuilt":         "Not built yet",
  "projects.grid.continueBuild":    "Continue building",
  "projects.grid.runtime":          "Runtime",
  "projects.grid.openRuntime":      "Open the live runtime",
  "projects.grid.status.building":  "Engineering",
  "projects.grid.status.live":      "Live",
  "projects.grid.status.error":     "Needs attention",
  "projects.grid.status.draft":     "Draft",

  /* ThreatTelemetryRail */
  "threat.title":                   "Threat Telemetry",
  "threat.noThreats":               "No threats detected",
  "threat.noThreatsHint":           "Security events appear here in real time",
  "threat.blocked":                 "blocked",
  "threat.kind.threat":             "Threat",
  "threat.kind.quarantine":         "Quarantine",
  "threat.kind.rate_limit":         "Rate limit",
  "threat.kind.validation_fail":    "Invalid op",
  "threat.kind.ai_threat":          "AI threat",
  "threat.metric.assessed":         "Assessed",
  "threat.metric.blocked":          "Blocked",
  "threat.metric.blockPct":         "Block %",

  /* WorkflowCanvas */
  "workflow.builder":               "Visual Builder",
  "workflow.emptyHint":             "Add nodes to build your workflow",
  "workflow.node.trigger":          "Trigger",
  "workflow.node.condition":        "Condition",
  "workflow.node.action":           "Action",
  "workflow.node.parallel":         "Parallel",
  "workflow.node.loop":             "Loop",
  "workflow.node.wait":             "Wait/Delay",
  "workflow.node.approval":         "Approval",

  /* AgentRunPanel */
  "agent.title":                    "Autonomous agent",
  "agent.locked.title":             "Autonomous agent",
  "agent.locked.body":              "The autonomous engineering agent (it reads, edits, runs commands, builds, tests, and repairs your project) is included from the Pro plan. Upgrade to put it to work.",
  "agent.locked.cta":               "See plans",
  "agent.placeholder":              "Describe what the agent should do (read, edit, run, build, test, commit)...",
  "agent.run":                      "Run agent",
  "agent.image":                    "Image",
  "agent.imageTitle":               "Attach an image (screenshot, sketch)",
  "agent.stop":                     "Stop",
  "agent.working":                  "Working",
  "agent.error.noKey":              "AI provider key not configured yet.",
  "agent.error.notEnabled":         "Agent runtime is not enabled.",
  "agent.reconnect":                   "Reconnect",
  "agent.resume":                      "Continue",
  "agent.resume.note":                 "Continuing resumes from the last saved checkpoint; completed steps are not run or charged again.",
  "agent.acuUsed":                     "ACU used",
  "agent.paused.approval":             "Paused: this step needs approval before it can run.",
  "agent.paused.budget":               "Paused: this session's credit allocation is used up.",
  "agent.cancelled":                   "Cancelled.",
  "agent.disconnected":                "Connection lost. The run is still going; reconnect to keep watching.",
  "agent.summary.pipeline":            "Pipeline",
  "agent.summary.stages":              "Stages",
  "agent.summary.capabilities":        "Capabilities completed",

  /* WorkspaceBusyState */
  "workspace.busy.errorTitle":      "Build did not finish",
  "workspace.busy.errorBody":       "This build stopped before it completed, and no credits were used. Run it again to continue.",
  "workspace.busy.runAgain":        "Run again",
  "workspace.busy.buildingTitle":   "Building your product",
  "workspace.busy.generating":      "Generating on Nuvra's servers. Live progress appears here.",

  /* SyncConflictBanner */
  "sync.conflict.ariaLabel":        "This project changed on another device",
  "sync.conflict.title":            "This project changed on another device",
  "sync.conflict.desc":             "Choose which version to keep. Nothing is overwritten until you decide.",
  "sync.conflict.keepMine":         "Keep mine",
  "sync.conflict.useTheirs":        "Use theirs",
  "sync.conflict.diffCount":        "{n} differences",
  "sync.conflict.keepAllMine":      "Keep all mine",
  "sync.conflict.useAllTheirs":     "Use all theirs",

  // DataExplorer
  "data.explorer.selectTable":      "Select table",
  "data.explorer.searchPlaceholder":"Search records...",
  "data.explorer.insert":           "Insert",
  "data.explorer.import":           "Import",
  "data.explorer.export":           "Export",
  "data.explorer.actionsCol":       "Actions",
  "data.explorer.noRecords":        "No records found",
  "data.explorer.recordCount":      "{n} records",
  "data.explorer.editHint":         "Double-click cell to edit inline",

  // ArchitectureConfidencePanel
  "arch.confidence.title":          "Architecture Confidence",
  "arch.confidence.score":          "{score}% confident",
  "arch.confidence.components":     "Components",
  "arch.confidence.services":       "Services",
  "arch.confidence.tables":         "Tables",
  "arch.confidence.endpoints":      "Endpoints",
  "arch.confidence.estCredits":     "Est. Credits",
  "arch.confidence.deployTarget":   "Deploy target:",
  "arch.confidence.confirmBuild":   "Confirm & Build",

  // ExecutionLogs
  "exec.filter.all":                "All",
  "exec.filter.completed":          "Completed",
  "exec.filter.failed":             "Failed",
  "exec.filter.running":            "Running",
  "exec.logs.noExecutions":         "No executions found",

  // AccessControlPanel
  "access.rls.title":               "Row-Level Security",
  "access.rls.desc":                "Define RLS conditions using natural language or SQL expressions",
  "access.rls.placeholder":         "e.g. \"Users can only read their own records\"",
  "access.rls.addRule":             "Add Rule",

  // BuildActivityStream
  "build.activity.onTeam":          "is on the team",

  // PlanExplanationPanel
  "plan.explain.toggle":            "Why Nuvra planned this",
  "plan.explain.summary":           "Summary",
  "plan.explain.keyDecisions":      "Key decisions",
  "plan.explain.assumptions":       "Assumptions",
  "plan.explain.confidence":        "{pct}% confidence",
  "plan.explain.pages":             "Pages ({n})",
  "plan.explain.capabilities":      "Capabilities ({n})",
  "plan.explain.noData":            "No explanation data available for this plan.",

  // UseCasePreviewPanel
  "usecase.tab.architecture":       "Architecture",
  "usecase.tab.data":               "Data Models",
  "usecase.tab.auth":               "Auth",
  "usecase.tab.deploy":             "Deploy",
  "usecase.startBuilding":          "Start Building",
  "usecase.close":                  "Close",

  // FeatureInteractionPanel
  "feature.howItWorks":             "How it works",
  "feature.highlights":             "Highlights",
  "feature.try":                    "Try {title}",
  "feature.close":                  "Close",

  // SaveStatusIndicator
  "save.status.ready":              "Ready",
  "save.status.saving":             "Saving...",
  "save.status.saved":              "Saved",
  "save.status.failed":             "Save failed",

  // CommentsMenu
  "comments.title":                 "Comments",
  "comments.empty":                 "No comments yet. Start the conversation.",
  "comments.placeholder":           "Add a comment...",
  "comments.post":                  "Post",

  // OAuthButtons
  "oauth.google":                   "Continue with Google",
  "oauth.github":                   "Continue with GitHub",
  "oauth.gitlab":                   "Continue with GitLab",
  "oauth.bitbucket":                "Continue with Bitbucket",
  "oauth.apple":                    "Continue with Apple",
  "oauth.microsoft":                "Continue with Microsoft",
  "oauth.signInFailed":             "Sign in failed",

  // WelcomeBanner
  "welcome.goodMorning":            "Good morning",
  "welcome.goodAfternoon":          "Good afternoon",
  "welcome.goodEvening":            "Good evening",
  "welcome.factory":                "Autonomous Software Engineering",
  "welcome.credits":                "credits",

  // IntentChips
  "intent.chip.saas":              "SaaS",
  "intent.chip.api":               "API / Backend",
  "intent.chip.music":             "Music",
  "intent.chip.books":             "Books & eBooks",
  "intent.chip.video":             "Video",
  "intent.chip.ecommerce":         "E-commerce",
  "intent.chip.ai":                "AI Product",
  "intent.chip.mobile":            "Mobile App",
  "intent.chip.game3d":            "3D Game",

  // ProjectSwitcher
  "proj.switcher.select":           "Select project",
  "proj.switcher.noProjects":       "No projects yet",
  "proj.switcher.pinned":           "Pinned",
  "proj.switcher.recent":           "Recent",
  "proj.switcher.create":           "Create new project",
  "proj.switcher.justNow":          "just now",
  "proj.switcher.mAgo":             "{m}m ago",
  "proj.switcher.hAgo":             "{h}h ago",
  "proj.switcher.dAgo":             "{d}d ago",

  // ProjectsSheet
  "proj.sheet.title":               "Projects",
  "proj.sheet.newProject":          "New project",
  "proj.sheet.noProjects":          "No projects yet. Create one to get started.",
  "proj.sheet.untitled":            "Untitled project",

  // BuilderEmptyState
  "builder.headline":               "Build your next software company.",
  "builder.subline":                "Describe your product and Nuvra engineers it end to end.",
  "builder.step.intent":            "Intent",
  "builder.step.planning":          "Planning",
  "builder.step.engineering":       "Engineering",
  "builder.step.operations":        "Operations",
  "builder.step.growth":            "Growth",
  "builder.tpl.saas":               "SaaS Dashboard",
  "builder.tpl.crm":                "CRM",
  "builder.tpl.marketplace":        "Marketplace",
  "builder.tpl.aiAgent":            "AI Agent Platform",
  "builder.tpl.adminPortal":        "Admin Portal",
  "builder.tpl.landingPage":        "Landing Page",
  "builder.tpl.ecommerce":          "E-Commerce Store",
  "builder.tpl.internalTool":       "Internal Tool",

  // ACUUsageNudges (ACUBalanceWarning)
  "acu.nudge.exhaustedTitle":       "Credits exhausted",
  "acu.nudge.exhaustedBody":        "Your compute credits are used up. Top up to keep building.",
  "acu.nudge.topUp":                "Top up",
  "acu.nudge.dismiss":              "Dismiss",
  "acu.nudge.almostTitle":          "Almost out of credits",
  "acu.nudge.remaining":            "{n} credits remaining.",
  "acu.nudge.approachingTitle":     "Approaching credit limit",
  "acu.nudge.buildingTitle":        "Building momentum",
  "acu.nudge.buildingBody":         "You are on a roll. Keep going.",

  // Builder Profile onboarding
  "onboarding.a11yTitle":           "Set up your Nuvra builder profile",
  "onboarding.stepOf":              "Step {n} of {total}",
  "onboarding.maybeLater":          "Maybe later",
  "onboarding.back":                "Back",
  "onboarding.continue":            "Continue",
  "onboarding.saveFailTitle":       "Your preferences were not saved",
  "onboarding.saveFailBody":        "Nuvra could not reach the server. You can keep going; your answers will be saved with your next step.",
  "onboarding.sessionExpiredTitle": "Your session expired",
  "onboarding.sessionExpiredBody":  "Sign in again to save your preferences. You can keep building either way.",
  "onboarding.profile.title":       "How will you use Nuvra?",
  "onboarding.profile.body":        "Tell us a little about how you build. Nuvra tailors your experience around it.",
  "onboarding.profile.founder":     "Founder / startup",
  "onboarding.profile.founderDesc": "Turn ideas into real products and ship faster.",
  "onboarding.profile.product_manager":     "Product manager",
  "onboarding.profile.product_managerDesc": "Turn product requirements into working software.",
  "onboarding.profile.developer":       "Developer / engineer",
  "onboarding.profile.developerDesc":   "Build, debug, and ship production software.",
  "onboarding.profile.business_owner":      "Business owner",
  "onboarding.profile.business_ownerDesc":  "Create tools and systems that run your business.",
  "onboarding.profile.freelancer":      "Freelancer / agency",
  "onboarding.profile.freelancerDesc":  "Build products and experiences for clients and teams.",
  "onboarding.profile.marketer":        "Marketer / creator",
  "onboarding.profile.marketerDesc":    "Create websites, digital experiences, and products.",
  "onboarding.profile.student":         "Student / educator",
  "onboarding.profile.studentDesc":     "Learn, experiment, and turn ideas into working projects.",
  "onboarding.profile.personal":        "Personal projects",
  "onboarding.profile.personalDesc":    "Build something useful just for yourself.",
  "onboarding.intent.title":        "What do you want to create first?",
  "onboarding.intent.body":         "Choose a starting point. You can change everything later.",
  "onboarding.intent.production_app":  "Production application",
  "onboarding.intent.saas":            "SaaS product",
  "onboarding.intent.website":         "Website / web experience",
  "onboarding.intent.internal_tool":   "Internal business tool",
  "onboarding.intent.mobile_app":      "Mobile application",
  "onboarding.intent.api_backend":     "API / backend system",
  "onboarding.intent.mvp":             "MVP / prototype",
  "onboarding.intent.ecommerce":       "E-commerce product",
  "onboarding.intent.client_project":  "Client project",
  "onboarding.intent.something_else":  "Something else",
  "onboarding.intent.ownIdea":      "I have another idea",
  "onboarding.priorities.title":    "What matters most to you?",
  "onboarding.priorities.body":     "Choose up to 3 priorities. Nuvra will use them to better understand how you want to build.",
  "onboarding.priorities.count":    "{n} of {max} selected",
  "onboarding.priority.production_readiness": "Production readiness",
  "onboarding.priority.ui_ux":            "Exceptional UI/UX",
  "onboarding.priority.speed":            "Speed to first working version",
  "onboarding.priority.architecture":     "Strong architecture",
  "onboarding.priority.business_logic":   "Business logic",
  "onboarding.priority.scalability":      "Scalability",
  "onboarding.priority.integrations":     "Integrations",
  "onboarding.priority.security":         "Security",
  "onboarding.priority.mobile":           "Mobile experience",
  "onboarding.priority.cost":             "Cost efficiency",
  "onboarding.ready.headline":      "What are we building?",
  "onboarding.ready.body":          "Describe what you want to build. Nuvra turns your idea into an engineered project you can build, test, and ship.",
  "onboarding.ready.placeholder":   "Describe your product: what it does, who uses it, and what it needs to handle.",
  "onboarding.ready.examplesLabel": "Examples",
  "onboarding.ready.build":         "Build with Nuvra",
  "onboarding.ready.workspace":     "Continue to workspace",
  "onboarding.example.store":            "Build an online store with product catalog, checkout, and order tracking",
  "onboarding.example.clientPortal":     "Build a client portal with authentication, invoices, documents, and messaging",
  "onboarding.example.fitnessPlatform":  "Build a fitness platform with memberships, workout plans, payments, and progress tracking",

  // ACUTopUpGrid
  "topup.title":                    "Top Up Credits",
  "topup.body":                     "Running low? Add credits anytime without changing your plan.",
  "topup.info":                     "Buying credits does not change your subscription. Credits never expire. Max outstanding: {n} credits.",
  "topup.creditsUnit":              "credits",
  "topup.bestValue":                "Best Value",
  "topup.processing":               "Processing...",
  "topup.buy":                      "Buy {label}",
  "topup.footer":                   "Top-up credits never expire and carry over every billing cycle.",
  "topup.upgrade":                  "Need significantly more? Upgrade your plan",
  "topup.viewPlans":                "View Plans",

  // BillingUsagePanel
  "usage.loading":                  "Loading usage details...",
  "usage.noData":                   "No usage data.",
  "usage.month":                    "This month ({period})",
  "usage.spentSoFar":               "Spent so far",
  "usage.projected":                "Projected by month end",
  "usage.dayOf":                    "day {elapsed} of {total}",
  "usage.planLimits":               "Plan limits ({tier})",
  "usage.unlimited":                "Unlimited on this plan.",
  "usage.notInPlan":                "Not in your plan",
  "usage.unlockUpgrade":            "Unlock these with an upgrade",
  "usage.breakdown":                "Usage breakdown",
  "usage.byProvider":               "By AI provider",
  "usage.byActivity":               "By activity",
  "usage.noActivity":               "No recorded usage yet this month.",
  "usage.recentLimits":             "Recent limits hit",
  "usage.rateLimit":                "rate limit",
  "usage.planLimit":                "plan limit",

  // SSOComplianceTab
  "sso.loading":                    "Loading SSO and compliance config...",
  "sso.noConfig":                   "No config loaded",
  "sso.title":                      "Single sign-on",
  "sso.saved":                      "Saved",
  "sso.saving":                     "Saving...",
  "sso.saveSso":                    "Save SSO",
  "sso.enabled":                    "SSO enabled",
  "sso.type":                       "SSO type",
  "sso.issuerUrl":                  "Issuer URL",
  "sso.clientId":                   "Client ID",
  "sso.scope":                      "Scope",
  "sso.scopeHint":                  "Space-separated OIDC scopes.",
  "sso.entityId":                   "Entity ID",
  "sso.ssoUrl":                     "SSO URL",
  "sso.certificate":                "X.509 Certificate",
  "sso.defaultRole":                "Default member role",
  "sso.defaultRoleHint":            "Role assigned to new members provisioned via SSO.",
  "sso.scim":                       "SCIM provisioning",
  "sso.scimDesc":                   "Enables /api/org/{orgId}/scim/v2/Users endpoint for IdP provisioning.",
  "sso.scimToken":                  "SCIM token",
  "sso.scimTokenHint":              "Store this now. It will not be shown again after you leave this page.",
  "sso.copied":                     "Copied",
  "sso.complianceTitle":            "Data residency and compliance",
  "sso.saveRegion":                 "Save region policy",
  "sso.dataClass":                  "Data classification",
  "sso.piiRedaction":               "PII redaction required",
  "sso.piiDesc":                    "Flags PII field names in export README. Does not auto-redact content.",
  "sso.legalHold":                  "Legal hold",
  "sso.legalHoldActive":            "ACTIVE: all project exports are blocked.",
  "sso.legalHoldDesc":              "When active: project exports are hard-blocked.",
  "sso.frameworks":                 "Compliance frameworks",
  "sso.dataRegions":                "Allowed data regions",
  "sso.dataRegionsHint":            "Comma-separated region codes. Empty = any region allowed.",
  "sso.aiRegions":                  "AI provider regions",
  "sso.aiRegionsHint":              "Comma-separated. Empty = any provider region allowed.",

  // AIGovernanceTab
  "ai.gov.loading":                 "Loading AI policy...",
  "ai.gov.noPolicy":                "No policy loaded",
  "ai.gov.title":                   "AI governance policy",
  "ai.gov.saved":                   "Saved",
  "ai.gov.saving":                  "Saving...",
  "ai.gov.save":                    "Save",
  "ai.gov.approvedModels":          "Approved models",
  "ai.gov.approvedModelsHint":      "Empty list = any model allowed. Select to restrict.",
  "ai.gov.restrictionActive":       "Restriction active: only selected models permitted for this org.",
  "ai.gov.redactionPatterns":       "Redaction patterns",
  "ai.gov.redactionHint":           "One regex per line. Applied to prompts before they reach any provider.",
  "ai.gov.retention":               "Prompt retention (days)",
  "ai.gov.retentionHint":           "0 = do not retain",
  "ai.gov.costCap":                 "Monthly cost cap (cents)",
  "ai.gov.noCap":                   "No cap",
  "ai.gov.month":                   "month",
  "ai.gov.logPrompts":              "Log prompts",
  "ai.gov.logPromptsHint":          "When off, prompt content is never stored (only metadata).",
  "ai.gov.trainingOptOut":          "Training opt-out",
  "ai.gov.trainingOptOutHint":      "Signals to AI providers that org data must not be used for training.",

  // WhiteLabelTab
  "wl.loading":                     "Loading white-label config...",
  "wl.noConfig":                    "No config loaded",
  "wl.title":                       "White-label configuration",
  "wl.saved":                       "Saved",
  "wl.saving":                      "Saving...",
  "wl.save":                        "Save",
  "wl.domain":                      "Custom domain",
  "wl.domainHint":                  "The domain where your white-labelled Nuvra instance is served.",
  "wl.platformName":                "Platform name",
  "wl.platformNameHint":            "Replaces 'Nuvra' everywhere in the UI.",
  "wl.primaryColor":                "Primary color",
  "wl.logoUrl":                     "Logo URL",
  "wl.faviconUrl":                  "Favicon URL",
  "wl.supportEmail":                "Support email",
  "wl.footerText":                  "Footer text",
  "wl.hidePoweredBy":               "Hide 'Powered by Nuvra'",
  "wl.customCss":                   "Custom CSS",
  "wl.customCssHint":               "Scoped to your tenant. Dangerous patterns are stripped server-side (no @import, javascript:, expressions). Max 8 KB.",

  // MembersTab
  "members.loading":                "Loading members...",
  "members.title":                  "Members",
  "members.total":                  "{n} total",
  "members.empty":                  "No members found",
  "members.col.member":             "Member",
  "members.col.role":               "Role",
  "members.col.joined":             "Joined",
  "members.col.lastActive":         "Last active",
  "members.never":                  "Never",
  "members.remove":                 "Remove member",

  // OrgDashboardTab
  "org.loading":                    "Loading dashboard...",
  "org.health":                     "Organization health",
  "org.refresh":                    "Refresh",
  "org.metric.totalInferences":     "Total inferences",
  "org.metric.denied":              "Denied",
  "org.metric.redacted":            "Redacted",
  "org.metric.policyChanges":       "Policy changes (24h)",
  "org.alerts":                     "Active alerts ({n})",
  "org.noAlerts":                   "No active alerts",
  "org.members":                    "Members ({n})",
  "org.inactive":                   "inactive",
  "org.noMembers":                  "No members found",

  // AuditLogTab
  "audit.tab.prompts":              "Prompt log",
  "audit.tab.policy":               "Policy changes",
  "audit.loading":                  "Loading...",
  "audit.col.time":                 "Time",
  "audit.col.model":                "Model",
  "audit.col.outcome":              "Outcome",
  "audit.col.redacted":             "Redacted",
  "audit.col.action":               "Action",
  "audit.col.changedBy":            "Changed by",
  "audit.unknown":                  "unknown",
  "audit.field":                    "field",
  "audit.fields":                   "fields",
  "audit.noRedaction":              "No",
  "audit.blockReason":              "Block reason: {reason}",
  "audit.noPromptEntries":          "No prompt audit entries",
  "audit.system":                   "system",
  "audit.noPolicyEntries":          "No policy change entries",

  // WatermarkModal
  "wm.close":                       "Close",
  "wm.logoAlt":                     "Nuvra logo",
  "wm.title":                       "Built with Nuvra",
  "wm.tagline":                     "The fastest way to ship apps with AI.",
  "wm.descBefore":                  "This app was built on",
  "wm.descAfter":                   "an AI-native platform for shipping production apps in minutes, not months.",
  "wm.buildMyApp":                  "Build my app",
  "wm.exploreTemplates":            "Explore templates",
  "wm.viewPlatform":                "View the platform",
  "wm.joinLine":                    "Join thousands building with Nuvra",

  // ProjectCodeView
  "code.title":                     "Project code",
  "code.edited":                    "edited",
  "code.editedTooltip":             "Edited",
  "code.pushGithub":                "Push to GitHub",
  "code.mobileApp":                 "Mobile app",
  "code.mobileAppTitle":            "Download the native mobile app (Expo / React Native)",
  "code.downloadZip":               "Download ZIP",
  "code.buildCi":                   "Build app (CI)",
  "code.buildCiTitle":              "Build a signed AAB/IPA on CI (runners with Android SDK / macOS + Xcode)",
  "code.loading":                   "Loading",
  "code.empty":                     "No code generated yet. Build the project first.",
  "code.edit":                      "Edit",
  "code.reset":                     "Reset",
  "code.save":                      "Save",
  "code.cancel":                    "Cancel",
  "code.flash.downloadStarted":     "Download started.",
  "code.flash.noCredits":           "Not enough credits to export (needs {acu} credits).",
  "code.flash.more":                "more",
  "code.flash.exportFailed":        "Export failed (HTTP {status}).",
  "code.flash.needGithub":          "Connect your GitHub first, then build on CI.",
  "code.flash.ciFailed":            "Build handoff failed (HTTP {status}).",
  "code.flash.pushed":              "Pushed. Add signing secrets, then run the workflow.",
  "code.flash.ciReady":             "Build handoff prepared.",
  "code.flash.saved":               "Saved.",
  "code.flash.reset":               "Reset to the generated original.",
  "code.flash.filesTo":             "Pushed {n} files to {branch}.",

  // NuvraActionsMenu
  "actions.button":                 "Actions",
  "actions.title":                  "Nuvra Actions",
  "actions.templates":              "Templates",
  "actions.connect":                "Connect",
  "actions.manageConnectors":       "Manage Connectors",
  "actions.cat.saas":               "SaaS / Startup",
  "actions.cat.marketplace":        "Marketplace / E-Commerce",
  "actions.cat.internal":           "Internal Tools",
  "actions.cat.ai_agents":          "AI and Agents",
  "actions.cat.marketing":          "Marketing",
  "actions.cat.other":              "Other",
  "actions.tpl.analytics_dash":     "Analytics dashboard",
  "actions.tpl.sub_billing":        "Subscription billing app",
  "actions.tpl.two_sided":          "Two-sided marketplace",
  "actions.tpl.ecommerce":          "E-commerce store",
  "actions.tpl.admin_portal":       "Admin portal",
  "actions.tpl.ops_tool":           "Operations tool",
  "actions.tpl.ai_platform":        "AI agent platform",
  "actions.tpl.ai_chat":            "AI chat app",
  "actions.tpl.landing":            "SaaS landing page",
  "actions.tpl.crm":                "CRM",
  "actions.tpl.community":          "Community platform",
  "actions.tpl.project_mgmt":       "Project management tool",

  // BuildHistory
  "bh.title":                       "Build History",
  "bh.count":                       "{n} builds",
  "bh.noBuilds":                    "No builds match your filters.",
  "bh.filter.all":                  "All",
  "bh.filter.success":              "Success",
  "bh.filter.failed":               "Failed",
  "bh.filter.inProgress":           "In Progress",
  "bh.filter.allCost":              "All Cost",
  "bh.filter.highCost":             "High Cost",
  "bh.used":                        "Used: {n} credits",
  "bh.reserved":                    "Reserved: {n} credits",
  "bh.refunded":                    "Refunded: {n} credits",
  "bh.logsTitle":                   "Logs - {id}",
  "bh.logsExported":                "Logs exported",
  "bh.downloading":                 "Downloading {target} - {mb}MB",
  "bh.shareCopied":                 "Share link copied to clipboard",
  "bh.idCopied":                    "Build ID copied",

  // MobileGovernanceDashboard
  "mgov.loading":                   "Loading mobile governance data",
  "mgov.loadError":                 "Could not load mobile governance data.",
  "mgov.loadAllFailed":             "We could not load build health for this project. This is usually temporary.",
  "mgov.loadPartial":               "Some build health data did not load. Retry to complete the picture.",
  "mgov.retry":                     "Retry",
  "mgov.profileSaved":              "Default profile saved. New builds use it unless overridden.",
  "mgov.profileError":              "Could not save the default profile.",
  "mgov.observability":             "Build observability",
  "mgov.stat.builds":               "Builds",
  "mgov.stat.successRate":          "Success rate",
  "mgov.stat.p50":                  "p50 duration",
  "mgov.stat.p95":                  "p95 duration",
  "mgov.stat.completed":            "Completed",
  "mgov.stat.failed":               "Failed",
  "mgov.stat.running":              "Running",
  "mgov.stat.queued":               "Queued",
  "mgov.failuresByCause":           "Failures by cause",
  "mgov.of":                        "of",
  "mgov.noBuilds":                  "No builds recorded for this project yet.",
  "mgov.versionHistory":            "Version history",
  "mgov.storeReady":                "store-ready",
  "mgov.caps":                      "caps",
  "mgov.noVersions":                "No versioned builds yet.",
  "mgov.profiles":                  "Governance profiles",
  "mgov.profilesExplainer":         "The selected profile runs its security, encryption, audit, and data-residency checks before every mobile build. A failed check blocks the build.",
  "mgov.projectDefault":            "Project default",
  "mgov.selectDefault":             "Select a default",
  "mgov.default":                   "default",
  "mgov.tag.encryption":            "encryption",
  "mgov.tag.audit":                 "audit",
  "mgov.tag.residency":             "residency: {r}",
  "mgov.tag.security":              "security: {s}",
  "mgov.tag.blocks":                "blocks {n}",

  // HostDeploySection
  "host.title":                     "Deploy to your host",
  "host.subtitle":                  "Ship the current build to your own Vercel or Netlify account.",
  "host.connectBtn":                "Connect host",
  "host.open":                      "Open",
  "host.deploy":                    "Deploy",
  "host.disconnect":                "Disconnect",
  "host.noConnection":              "No host connected yet. Connect Vercel or Netlify to deploy to your own account.",
  "host.tokenRequired":             "Token and target are required.",
  "host.connectError":              "Could not connect.",
  "host.noCredits":                 "Not enough credits to deploy (needs {acu} credits).",
  "host.more":                      "more",
  "host.deployFailed":              "Deploy failed (HTTP {status}).",
  "host.tokenPlaceholder":          "{provider} access token",
  "host.vercelProjectId":           "Vercel project ID",
  "host.netlifySiteId":             "Netlify site ID",
  "host.vercelTeamId":              "Vercel team ID (optional)",
  "host.connectProvider":           "Connect {provider}",

  // StoreSubmitSection
  "store.google":                   "Google Play Store",
  "store.apple":                    "Apple App Store",
  "store.ciRun":                    "CI run",
  "store.checking":                 "Checking build",
  "store.needGithub":               "Connect GitHub and run a {target} build to submit.",
  "store.noBuild":                  "No {target} build yet. Start one from Mobile Builds, then submit here.",
  "store.buildRunning":             "Build {status}. Submission unlocks when it finishes.",
  "store.buildFailed":              "Last build failed. Fix and rebuild before submitting.",
  "store.noArtifact":               "Build succeeded but produced no artifact to submit.",
  "store.packagePlaceholder":       "Android application id (e.g. com.acme.app)",
  "store.submit":                   "Submit",
  "store.submitted":                "Submitted ({id})",
  "store.release":                  "release",
  "store.submitFailed":             "Submission failed",
  "store.statusError":              "Could not read build status",

  // VersionHistoryMenu
  "ver.title":        "Version history",
  "ver.titleBtn":     "Version history and revert",
  "ver.btnLabel":     "History",
  "ver.loading":      "Loading versions",
  "ver.loadError":    "Could not load version history.",
  "ver.empty":        "No completed builds yet. Your versions appear here after a build finishes.",
  "ver.untitled":     "Untitled build",
  "ver.name":         "Name this version",
  "ver.rename":       "Rename this version",
  "ver.namePlaceholder": "Name this version (e.g. Before payments)",
  "ver.labelError":   "Could not save the name. Try again.",
  "ver.current":      "Current",
  "ver.confirm":      "Confirm",
  "ver.cancel":       "Cancel",
  "ver.revert":       "Revert",
  "ver.revertFailed": "Revert failed. Please try again.",
  "ver.search":       "Search builds",
  "ver.noMatch":      "No builds match your search.",

  // DomainsMenu
  "domain.status.live":     "Live",
  "domain.status.progress": "Verified, SSL provisioning",
  "domain.status.failed":   "Verification failed",
  "domain.status.pending":  "Pending DNS",
  "domain.title":           "Custom domain",
  "domain.subtitle":        "Connect your own domain and take the app live with managed SSL.",
  "domain.add":             "Add",
  "domain.dnsInstructions": "Add these DNS records at your registrar, then verify.",
  "domain.copy":            "Copy",
  "domain.verify":          "Verify and go live",
  "domain.remove":          "Remove",
  "domain.registerError":   "Could not register the domain",
  "domain.dnsError":        "DNS verification failed. Ensure both records have propagated.",
  "domain.verifyFailed":    "Verification failed",
  "domain.record.host":     "Host:",
  "domain.record.value":    "Value:",

  // SupabaseConnectMenu
  "sb.title":                  "Connect your Supabase",
  "sb.subtitle":               "Your app will use your own Supabase. Find these in your project's API settings.",
  "sb.anonOnly":               "Anon key stored (encrypted). Add a service role key for server features.",
  "sb.anonService":            "Anon + service role key stored (encrypted).",
  "sb.connectBtn":             "Connect and verify",
  "sb.disconnect":             "Disconnect",
  "sb.urlRequired":            "Project URL and anon key are required.",
  "sb.connectError":           "Could not connect Supabase.",
  "sb.connected":              "Supabase connected and verified.",
  "sb.anonPlaceholder":        "anon public key",
  "sb.serviceRolePlaceholder": "service role key (optional)",

  // HostDeployMenu (workspace variant)
  "hdm.title":            "Deploy to your host",
  "hdm.connectTitle":     "Connect a host",
  "hdm.deploy":           "Deploy current build",
  "hdm.open":             "Open",
  "hdm.disconnect":       "Disconnect",
  "hdm.tokenRequired":    "Token and target are required.",
  "hdm.connectError":     "Could not connect.",
  "hdm.deployFailed":     "Deploy failed (HTTP {status}).",
  "hdm.deployFailed2":    "Deploy failed.",
  "hdm.noCredits":        "Not enough credits to deploy (needs {acu} credits).",
  "hdm.more":             "more",
  "hdm.connected":        "{provider} connected.",
  "hdm.liveon":           "Live on {provider}.",
  "hdm.tokenPlaceholder": "{provider} access token",
  "hdm.vercelProjectId":  "Vercel project ID",
  "hdm.netlifySiteId":    "Netlify site ID",
  "hdm.vercelTeamId":     "Vercel team ID (optional)",
  "hdm.connect":          "Connect {provider}",

  // ImportRepoMenu
  "imp.title":            "Import an existing repository",
  "imp.subtitle":         "Bring your code in from GitHub, GitLab, or Bitbucket. Nuvra adopts it and keeps building, no fresh start required.",
  "imp.btnLabel":         "Import repo",
  "imp.preview":          "Preview",
  "imp.import":           "Import into this project",
  "imp.tokenPlaceholder": "Access token (only for private GitLab / Bitbucket repos)",
  "imp.previewError":     "Could not read that repository.",
  "imp.importError":      "Could not import that repository.",
  "imp.models":           "Models:",
  "imp.routes":           "Routes:",
  "imp.done":             "Imported {files} files. Recovered {models} models and {pages} routes.",

  // StoreReadinessMenu
  "sr.docsTitle":      "Store submission docs",
  "sr.dataDisclosure": "Generated from your data model. Privacy policy discloses: {data}.",
  "sr.minimalData":    "minimal technical data",
  "sr.privacy":        "Privacy Policy",
  "sr.terms":          "Terms of Service",
  "sr.listing":        "App store listing",
  "sr.screenshots":    "Capture store screenshots",
  "sr.genError":       "Could not generate documents.",
  "sr.shotsError":     "Could not capture screenshots.",
  "sr.shotsError2":    "Screenshot capture failed.",

  // SchemaErdView
  "erd.title":      "Database diagram",
  "erd.fromTable":  "from table",
  "erd.references": "references",
  "erd.colName":    "column name",
  "erd.addRel":     "Relationship",
  "erd.noTables":   "No tables to diagram yet.",
  "erd.pending":    "pending",
  "erd.relError":   "Could not add the relationship.",

  // TableDataView
  "tdv.search":       "Search text columns, Enter",
  "tdv.loading":      "Loading",
  "tdv.noRows":       "No rows yet.",
  "tdv.noRowsSearch": "No rows match your search.",
  "tdv.clickEdit":    "Click to edit",
  "tdv.loadError":    "Could not load rows.",
  "tdv.saveError":    "Could not save the cell.",

  // WorkflowTestPanel
  "wft.testTitle":    "Test: {name}",
  "wft.dryRun":       "Dry Run",
  "wft.payloadLabel": "Test Payload (JSON)",
  "wft.invalidJson":  "Invalid JSON payload",
  "wft.executing":    "Executing...",
  "wft.runTest":      "Execute Test",
  "wft.liveBadge":    "Live",
  "wft.steps":        "{n} steps",

  // WorkflowVersionPanel
  "wfv.saveTitle":    "Save Version",
  "wfv.currentBadge": "Current: v{n}",
  "wfv.placeholder":  "Version message...",
  "wfv.save":         "Save",
  "wfv.historyTitle": "Version History",
  "wfv.empty":        "No saved versions yet",
  "wfv.restore":      "Restore",

  // batch 10 - env.*
  "env.title":          "Environments",
  "env.notReleased":    "not released",
  "env.build":          "build {id}",
  "env.promoteStaging": "Promote staging",
  "env.rollBack":       "Roll back",
  "env.rollbackTitle":  "Roll back to build {id}",
  "env.config":         "Config",
  "env.add":            "Add",
  "env.actionFailed":   "Action failed",
  "env.description":    "New builds release to staging. Promote to production when ready. Config is for non-secret settings; keep secrets in your deploy platform.",

  "assets.open":        "Assets",
  "assets.title":       "Assets",
  "assets.subtitle":    "Upload media and place it in your app",
  "assets.empty":       "No assets yet. Upload an image, audio, or video.",
  "assets.drop":        "Drag files here, or click to upload",
  "assets.uploading":   "Uploading {pct}%",
  "assets.retry":       "Retry",
  "assets.delete":      "Delete",
  "assets.replace":     "Replace",
  "assets.selected":    "{count} selected",
  "assets.placeHint":   "Say where it goes, for example: put this in the hero",
  "assets.place":       "Place in app",
  "assets.placing":     "Placing",
  "assets.placed":      "Placed in {target}",
  "assets.inUse":       "In use",
  "assets.close":       "Close",
  "assets.failed":      "Something went wrong. Try again.",
  "assets.loadFailed":  "Could not load assets.",
  "assets.removed":     "Deleted",

  // batch 10 - msm.*
  "msm.title":          "Generate media",
  "msm.label":          "Media",
  "msm.placeholder":    "Describe an image, or paste text to speak...",
  "msm.image":          "Image",
  "msm.speech":         "Speech",
  "msm.music":          "Music",
  "msm.download":       "Download",
  "msm.imageAlt":       "Generated",
  "msm.imageGen":       "Image generation",
  "msm.speechGen":      "Speech generation",
  "msm.musicGen":       "Music generation",
  "msm.notConfigured":  "{what} is not configured yet.",
  "msm.noCredits":      "Not enough credits (needs {acu} credits).",
  "msm.more":           "more",
  "msm.failedHttp":     "{what} failed (HTTP {status}).",
  "msm.imgFailed":      "Image generation failed.",
  "msm.speechFailed":   "Speech generation failed.",
  "msm.musicFailed":    "Music generation failed.",
  "msm.length":         "Length",
  "msm.instrumental":   "Instrumental",
  "msm.library":        "Your tracks",
  "msm.titleField":     "Title (optional)",
  "msm.genreField":     "Style (optional)",
  "msm.lyricsField":    "Lyrics (optional)",
  "msm.musicTooLong":   "The configured music provider reaches {max} minutes.",
  "msm.musicStillRunning": "Still generating. The track will appear in your library when it finishes.",
  "msm.musicCancelled": "That generation is no longer running.",
  "msm.mfail.unsupported": "That combination is not available. Try different settings.",
  "msm.mfail.unavailable": "Music is not available right now. You were not charged.",
  "msm.mfail.timeout": "This took too long and was stopped. You were not charged.",
  "msm.mfail.validation": "The track that came back did not match your request, so it was discarded. You were not charged.",
  "msm.mfail.storage": "The track could not be saved. You were not charged.",
  "msm.mfail.provider": "The music service did not finish this one. You were not charged.",
  "msm.cancel":         "Cancel",
  "msm.stage.queued":     "Queued",
  "msm.stage.running":    "Generating",
  "msm.stage.generating": "Generating",
  "msm.stage.validating": "Checking the audio",
  "msm.stage.illustrating": "Creating the cover",
  "msm.coverUnavailable": "Song generated. Cover artwork is unavailable for this track.",
  "msm.play":           "Play",
  "msm.downloadAudio":  "Download audio",
  "msm.downloadCover":  "Download cover",
  "msm.coverAlt":       "Cover artwork for {title}",
  "msm.noCover":        "No cover artwork",
  "msm.stage.saving":     "Saving",
  "msm.video":          "Video",
  "msm.videoGen":       "Video generation",
  "msm.videoFailed":    "Video generation failed.",
  "msm.videoRefused":   "That request was refused by the video provider.",
  "msm.videoLength":    "Clip",
  "msm.videoTier":         "Quality",
  "msm.videoTier.fast":    "Fast",
  "msm.videoTier.quality": "Best quality",
  "msm.videoAudio":     "With sound",
  "msm.vfail.unsupported": "That combination is not available. Try a different length, size or quality.",
  "msm.vfail.unavailable": "Video is not available right now. You were not charged.",
  "msm.vfail.timeout":     "This took too long and was stopped. You were not charged.",
  "msm.vfail.validation":  "The clip that came back did not match your request, so it was discarded. You were not charged.",
  "msm.vfail.storage":     "The clip could not be saved. You were not charged.",
  "msm.vfail.provider":    "The video service did not finish this one. You were not charged.",
  "msm.videoLibrary":   "Your clips",
  "msm.videoHasAudio":  "with sound",
  "msm.videoOption":    "{seconds}s ({acu} credits)",
  "msm.videoLengths":   "This provider generates clips of {lengths} seconds.",
  "msm.videoStillRunning": "Still generating. The clip will appear in your library when it finishes.",
  "msm.videoCancelled": "That generation is no longer running.",
  "msm.vstage.submitting": "Sending your request",
  "msm.vstage.queued":     "Queued",
  "msm.vstage.generating": "Generating",
  "msm.vstage.retrieving": "Collecting the file",
  "msm.vstage.validating": "Checking the video",
  "msm.vstage.saving":     "Saving",

  // batch 10 - wb.*
  "wb.dismiss":         "Dismiss briefing",
  "wb.teamHere":        "Your team is here",
  "wb.welcomeBack":     "Welcome back to {name}.",
  "wb.lastWorked":      "We last worked on this {ago}.",
  "wb.productStands":   "Here is where your product stands.",
  "wb.buildingToward":  "Building toward",
  "wb.whatNext":        "What I would do next",
  "wb.buildThis":       "Build",

  // batch 10 - wcm.*
  "wcm.title":          "Connect GitHub, your database, and integrations",
  "wcm.ariaLabel":      "Connect integrations",
  "wcm.connectLabel":   "Connect",
  "wcm.sectionTitle":   "Connections",
  "wcm.database":       "Database & data",
  "wcm.integrations":   "All integrations",

  // batch 10 - erec.*
  "erec.title":         "What your engineering team recommends next",
  "erec.subtitle":      "Real next steps from your project's current state. One tap builds it.",
  "erec.buildThis":     "Build this",

  // batch 10 - bal.*
  // batch 11 - bst.* (durable build execution timeline)
  "bst.heading":        "Engineering record",
  "bst.historical":     "Earlier in this build",
  "bst.live":           "Happening now",
  "bst.interrupted":    "Did not finish",
  "bst.running":        "Running",
  "bst.completed":      "Completed",
  "bst.failed":         "Failed",
  "bst.attempts":       "Attempted {count} times",
  "bst.unavailable":    "No engineering record was found for this build.",
  "bst.unreachable":    "Nuvra could not reach the engineering record. This does not mean the build failed.",
  "bst.forbidden":      "This build is not available to you.",
  "bst.loading":        "Reading the engineering record",
  "bst.noPhases":       "This build recorded no phases.",
  "bst.retry":          "Try again",
  "bst.startedAt":      "Started {time}",
  "bst.duration":       "Ran for {duration}",

  "bal.builtProduct":    "Built your product",
  "bal.buildingProduct": "Building your product",
  "bal.builtData":       "Built your {nouns}",
  "bal.assembling":      "Assembling your product",
  "bal.openInFiles":     "Open in Files",

  // batch 10 - pp.*
  "pp.title":           "Orchestration Pulse",
  "pp.releaseEvents":   "Release Events",
  "pp.mobileBuilds":    "Mobile Builds",
  "pp.lastEvent":       "Last Event",
  "pp.standingBy":      "Pipeline standing by - no events yet.",
  "pp.viewFull":        "View full Platform Intelligence",
  "pp.justNow":         "Just now",
  "pp.minsAgo":         "{n}m ago",
  "pp.hoursAgo":        "{n}h ago",
  "pp.daysAgo":         "{n}d ago",

  // batch 10 - wqa.*
  "wqa.heading":        "Build & Ship",
  "wqa.generateApp":    "Generate App",
  "wqa.shipBuild":      "Ship Build",
  "wqa.myProjects":     "My Projects",
  "wqa.buyCredits":     "Buy Credits",
  "wqa.credits":        "{n} credits",

  // batch 10 - eaf.*
  "eaf.earlierStep":    "{n} earlier step",
  "eaf.earlierSteps":   "{n} earlier steps",
  "eaf.now":            "now",

  // batch 10 - etl.*
  "etl.now":            "now",

  // batch 11 - dt.*
  "dt.title":           "Deployment Pipeline",
  "dt.loading":         "Loading build history...",
  "dt.empty":           "No builds yet. Start your first build from the Dashboard.",
  "dt.preview":         "Preview",
  "dt.statusLive":      "Live",
  "dt.statusFailed":    "Failed",
  "dt.statusGenerating": "Generating",
  "dt.justNow":         "Just now",
  "dt.minsAgo":         "{n}m ago",
  "dt.hoursAgo":        "{n}h ago",
  "dt.daysAgo":         "{n}d ago",

  // batch 11 - iaf.*
  "iaf.title":          "Orchestration Events",
  "iaf.loading":        "Loading activity...",
  "iaf.empty":          "No builds yet",
  "iaf.emptyDesc":      "AI agents are standing by. Describe what you want to build.",
  "iaf.shipped":        "Application shipped",
  "iaf.failed":         "Generation failed",
  "iaf.generating":     "AI agents generating...",
  "iaf.inQueue":        "In queue",
  "iaf.justNow":        "Just now",
  "iaf.minsAgo":        "{n}m ago",
  "iaf.hoursAgo":       "{n}h ago",
  "iaf.daysAgo":        "{n}d ago",

  // batch 11 - aqa.*
  "aqa.platformStatus": "Platform Status",
  "aqa.platformDesc":   "View real-time orchestration, release, and build infrastructure",
  "aqa.debugAi":        "Debug with AI",
  "aqa.debugDesc":      "Open a project workspace to diagnose a build or deployment failure",

  // batch 11 - uqa.*
  "uqa.generateApp":    "Generate App",
  "uqa.deploy":         "Deploy",
  "uqa.mobileBuild":    "Mobile Build",
  "uqa.platform":       "Platform",
  "uqa.buyCredits":     "Buy Credits",
  "uqa.credits":        "{n} credits",

  // batch 11 - iap.*
  "iap.title":          "Alerts",
  "iap.healthy":        "All systems healthy. No active alerts.",
  "iap.resolve":        "Resolve incident",
  "iap.justNow":        "just now",
  "iap.minsAgo":        "{n}m ago",
  "iap.hoursAgo":       "{n}h ago",

  // batch 11 - cgb.*
  "cgb.disconnect":     "Disconnect",
  "cgb.installApp":     "Install GitHub App",
  "cgb.connect":        "Connect GitHub",
  "cgb.notConfigured":  "GitHub is not configured on the server",

  // batch 11 - rcp.*
  "rcp.running":        "Running your code",
  "rcp.rerunTitle":     "Re-run with the latest code and edits",
  "rcp.rerun":          "Re-run",
  "rcp.loadError":      "Could not run the code.",
  "rcp.noCode":         "No runnable code yet. Build the project first.",

  // batch 11 - bih.*
  "bih.notSignedIn":    "Not signed in",
  "bih.notSignedInDesc": "Please sign in and try again.",
  "bih.noCredits":      "No credits remaining",
  "bih.noCreditsDesc":  "Add credits or upgrade your plan to keep building with Nuvra.",
  "bih.createError":    "Could not create project",
  "bih.docCreateError": "Could not create the document",
  "bih.docCreated":     "Document created",
  "bih.docCreatedFallback": "Your document",
  "bih.docPages":       "pages",
  "bih.unexpectedError": "Unexpected error. Please try again.",
  "bih.permissionError": "Your account may not have permission or enough credits. Please check your billing.",
  "bih.wentWrong":      "Something went wrong",
  "bih.tryAgain":       "Please try again.",
  // Composer failure copy. Product language for the customer; the engineering detail stays in the logs
  // and the durable record. Each one says plainly that the work did not happen.
  "composerFail.engineUnavailable": "Nuvra's engineering service is unavailable right now, so nothing was built and no credits were used. Please try again shortly.",
  "composerFail.needCredits":       "You do not have enough credits for this build. No credits were used.",
  "composerFail.needPlan":          "This kind of product is not included in your current plan.",
  "composerFail.describeProduct":   "Describe the product itself: what it should do, and who uses it.",
  "composerFail.signedOut":         "Your session has expired. Sign in and try again.",
  "composerFail.notAllowed":        "Your account does not have access to this.",
  "composerFail.busy":              "Nuvra is handling too many builds right now. Please try again in a moment.",
  "composerFail.failed":            "This could not be created. Nothing was built and no credits were used. Please try again.",
  "bih.placeholder":    "Describe the product to engineer...",
  "bih.ariaLabel":      "Describe your app idea",
  "bih.hint":           "Enter to send",
  "bih.attachImageInWorkspace":     "Images attach inside the project workspace. Start the build and add them there.",
  "bih.ex.saasMultiTenant":       "Build a multi-tenant SaaS platform with authentication, subscriptions, teams, and role-based access",
  "bih.ex.inventory":             "Build an inventory and sales platform with stock tracking, orders, and reporting",
  "bih.ex.customerPortal":        "Build a customer portal with authentication, payments, documents, and messaging",
  "bih.ex.marketplace":           "Build a marketplace with vendors, product listings, orders, payments, and reviews",
  "bih.ex.aiProduct":             "Build an AI-powered productivity platform with workspaces, projects, and intelligent workflows",
  "bih.ex.booking":               "Build a booking platform with customer accounts, availability, payments, and notifications",
  "bih.ex.internalOps":           "Build an internal operations platform with approvals, reporting, roles, and audit history",
  "bih.ex.learning":              "Build a learning platform with courses, student accounts, progress tracking, and subscriptions",
  "bih.ex.ecommerce":             "Build an online store with a product catalog, cart, checkout, and order management",
  "bih.ex.apiBackend":            "Build a REST API with authentication, rate limiting, webhooks, and documentation",
  "bih.ex.dashboard":             "Build an analytics dashboard with connected data, charts, filters, and scheduled reports",
  "bih.ex.mobileApp":             "Build a mobile app with accounts, offline data, push notifications, and in-app payments",
  "bih.ex.game3d":               "Build a 3D adventure game with physics, a third-person camera, enemies, collectibles, and saved progress",

  // batch 12 - rv.*
  "rv.title":           "Receipt",
  "rv.subtitle":        "Download or open your receipt PDF.",
  "rv.downloadPdf":     "Download PDF",
  "rv.openTab":         "Open in new tab",

  // batch 12 - th.*
  "th.loading":         "Loading transactions...",
  "th.empty":           "No transactions yet",
  "th.creditsAdded":    "{n} credits added",
  "th.view":            "View",
  "th.download":        "Download",

  // batch 12 - rm.*
  "rm.title":           "Receipt",
  "rm.creditsAdded":    "{n} credits added",
  "rm.reference":       "Reference",
  "rm.date":            "Date",
  "rm.plan":            "Plan",
  "rm.secureNote":      "This transaction was securely processed.",
  "rm.print":           "Print",
  "rm.copyRef":         "Copy Ref",
  "rm.refCopied":       "Reference copied",

  // batch 12 - acu.*
  "acu.title":          "Compute Usage",
  "acu.creditsUsed":    "Credits used",
  "acu.credits":        "{used} / {total} credits",
  "acu.percentUsed":    "{n}% used",
  "acu.remaining":      "{n} remaining",
  "acu.daysRemaining":  "Days remaining",
  "acu.dailyRate":      "credits / day avg",
  "acu.recommendations": "Recommendations",
  "acu.byProject":      "Usage by project",
  "acu.general":        "General",
  "acu.high":           "High",

  // batch 12 - scc.*
  "scc.heading":        "Stripe payouts",
  "scc.enabled":        "Enabled",
  "scc.connected":      "Your Stripe account is connected and ready to receive payouts.",
  "scc.description":    "Connect a Stripe account to receive payouts via Stripe. You will complete a short, secure setup on Stripe.",
  "scc.notEnabled":     "Stripe is not enabled yet.",
  "scc.startFailed":    "Could not start onboarding.",
  "scc.continueSetup":  "Continue Stripe setup",
  "scc.connect":        "Connect Stripe",

  // batch 12 - cd.*
  "cd.loading":         "Loading earnings...",
  "cd.empty":           "No marketplace earnings yet",
  "cd.payoutHold":      "Payout on hold",
  "cd.loadError":       "Failed to load earnings",
  "cd.totalEarned":     "Total earned",
  "cd.pending":         "Pending (held)",
  "cd.available":       "Available",
  "cd.nextPayout":      "Next payout: {date}. {notice}",

  // batch 12 - pmf.*
  "pmf.heading":        "Payout method",
  "pmf.update":         "Update",
  "pmf.accountEnding":  "Account ending {last4}",
  "pmf.bankCodeDisplay": "Bank code {code}",
  "pmf.saved":          "Saved.",
  "pmf.description":    "Cleared earnings are sent here automatically on each payout day. Details are encrypted.",
  "pmf.accountNumber":  "Account number",
  "pmf.bankCodeLabel":  "Bank code",
  "pmf.beneficiaryName": "Beneficiary name",
  "pmf.country":        "Country (2-letter code, optional)",
  "pmf.saving":         "Saving...",
  "pmf.saveMethod":     "Save payout method",
  "pmf.cancel":         "Cancel",
  "pmf.signInError":    "Sign in to save a payout method",
  "pmf.saveError":      "Could not save payout method",

  // batch 12 - mp.*
  "mp.applied":         "Applied",
  "mp.pending":         "Pending",
  "mp.runPending":      "Run Pending",
  "mp.dryRun":          "Dry Run",
  "mp.up":              "UP",
  "mp.down":            "DOWN",
  "mp.apply":           "Apply",
  "mp.rollback":        "Rollback",

  // batch 12 - svp.*
  "svp.createSnapshot": "Create Snapshot",
  "svp.description":    "Snapshot description",
  "svp.dev":            "Dev",
  "svp.staging":        "Staging",
  "svp.prod":           "Prod",
  "svp.save":           "Save",
  "svp.modelsInSnapshot": "{n} models in snapshot",
  "svp.restore":        "Restore",
  "svp.viewDiff":       "View Diff",

  // batch 12 - qb.*
  "qb.visual":          "Visual",
  "qb.sql":             "SQL",
  "qb.sortBy":          "Sort by",
  "qb.noSort":          "No sort",
  "qb.asc":             "ASC",
  "qb.desc":            "DESC",
  "qb.filters":         "Filters",
  "qb.addFilter":       "Add Filter",
  "qb.value":           "Value",
  "qb.execute":         "Execute",
  "qb.results":         "Results",
  "qb.rows":            "{n} rows",
  "qb.sqlPlaceholder":  "SELECT * FROM...",

  // batch 13 - ddt.*
  "ddt.loading":               "Loading deployment declaration...",
  "ddt.loadError":             "Failed to load",
  "ddt.saveError":             "Save failed",
  "ddt.noDecl":                "No declaration loaded",
  "ddt.title":                 "Deployment declaration",
  "ddt.lastUpdated":           "Last updated {date}",
  "ddt.saved":                 "Saved",
  "ddt.saving":                "Saving...",
  "ddt.save":                  "Save",
  "ddt.airGappedWarning":      "Air-gapped deployment: project exports are hard-blocked. Contact your infrastructure team before enabling.",
  "ddt.deploymentTarget":      "Deployment target",
  "ddt.deploymentTargetHint":  "Controls which infrastructure requirements and export policies apply.",
  "ddt.keyMgmt":               "Key management",
  "ddt.keyMgmtHint":           "How encryption keys are managed for this org's data.",
  "ddt.hsmProvider":           "HSM provider",
  "ddt.hsmProviderHint":       "Name or identifier of your HSM vendor.",
  "ddt.strictResidency":       "Strict data residency",
  "ddt.strictResidencyHint":   "Enforces that all data stays in the declared primary region.",
  "ddt.primaryRegion":         "Primary region",
  "ddt.primaryRegionHint":     "ISO region code where all org data must reside.",
  "ddt.complianceFrameworks":  "Compliance frameworks",
  "ddt.infraTitle":            "Infrastructure requirements",
  "ddt.sovereignBadge":        "Sovereign context",
  "ddt.airGappedBadge":        "Air-gapped",
  "ddt.hsmBadge":              "HSM required",
  "ddt.byokBadge":             "BYOK",
  "ddt.strictResidencyBadge":  "Strict residency",
  "ddt.networkIsolation":      "Network isolation",
  "ddt.onPremInfra":           "On-prem infra",
  "ddt.provisioning":          "Provisioning required before go-live:",
  "ddt.opNotes":               "Operational notes:",
  "ddt.targetNuvraCloud":      "Nuvra Cloud",
  "ddt.targetPrivateCloud":    "Private Cloud",
  "ddt.targetOnPremise":       "On-Premise",
  "ddt.targetSovereign":       "Sovereign",
  "ddt.targetAirGapped":       "Air-Gapped",
  "ddt.kmPlatform":            "Platform managed",
  "ddt.kmCustomerManaged":     "Customer managed (BYOK)",
  "ddt.kmHsm":                 "Hardware Security Module",

  // batch 13 - sat.*
  "sat.loading":         "Loading service accounts...",
  "sat.loadError":       "Failed to load",
  "sat.createError":     "Create failed",
  "sat.revokeError":     "Revoke failed",
  "sat.title":           "Service accounts",
  "sat.newAccount":      "New account",
  "sat.keyCreated":      "Service account created. Store this key now.",
  "sat.keyOnce":         "It will not be shown again.",
  "sat.copied":          "Copied",
  "sat.dismiss":         "Dismiss",
  "sat.newAccountTitle": "New service account",
  "sat.name":            "Name",
  "sat.scopes":          "Scopes",
  "sat.expiresAt":       "Expires at (optional)",
  "sat.cancel":          "Cancel",
  "sat.creating":        "Creating...",
  "sat.create":          "Create",
  "sat.empty":           "No service accounts yet",
  "sat.revoked":         "revoked",
  "sat.created":         "Created {date}",
  "sat.lastUsed":        "Last used {date}",
  "sat.expires":         "Expires {date}",
  "sat.revokeTitle":     "Revoke",

  // batch 13 - swt.*
  "swt.loading":         "Loading webhooks...",
  "swt.loadError":       "Failed to load",
  "swt.createError":     "Create failed",
  "swt.deleteError":     "Delete failed",
  "swt.title":           "Seller webhooks",
  "swt.addEndpoint":     "Add endpoint",
  "swt.webhookCreated":  "Webhook created. Store the signing secret now.",
  "swt.secretOnce":      "It will not be shown again.",
  "swt.dismiss":         "Dismiss",
  "swt.newEndpoint":     "New webhook endpoint",
  "swt.url":             "URL",
  "swt.events":          "Events",
  "swt.allEventsHint":   "Empty = receive all events.",
  "swt.cancel":          "Cancel",
  "swt.creating":        "Creating...",
  "swt.create":          "Create",
  "swt.empty":           "No webhook endpoints yet",
  "swt.disabled":        "disabled",
  "swt.receivingAll":    "Receiving all events",
  "swt.created":         "Created {date}",
  "swt.lastCalled":      "Last called {date}",
  "swt.testTitle":       "Send test event",
  "swt.deleteTitle":     "Delete",

  // batch 13 - eep.*
  "eep.prodArchTitle":         "Production-ready architecture",
  "eep.prodArch1":             "CDN-backed static asset delivery via Cloudflare Pages",
  "eep.prodArch2":             "SHA-256 checksum integrity on every deployed artifact",
  "eep.prodArch3":             "Security scanning gates every build. Critical findings block deployment",
  "eep.prodArch4":             "Admission control: validation score and security scan must pass before deployment",
  "eep.prodArch5":             "Build certification: signed certificate issued for every successful deployment",
  "eep.secureInfraTitle":      "Secure infrastructure",
  "eep.secureInfra1":          "End-to-end encryption for data in transit and at rest",
  "eep.secureInfra2":          "Hosted on SOC 2 Type II certified infrastructure (Cloudflare Pages + Supabase)",
  "eep.secureInfra3":          "Static security analysis on every build: SQL injection, XSS, eval(), and hardcoded credential detection",
  "eep.secureInfra4":          "Row-level security policies on all data access",
  "eep.secureInfra5":          "Secret management with encrypted vault storage",
  "eep.enterpriseDeployTitle": "Enterprise-grade deployment",
  "eep.enterpriseDeploy1":     "HTTP health probe with retries validates every deployment before marking live",
  "eep.enterpriseDeploy2":     "Automatic rollback on failed health checks",
  "eep.enterpriseDeploy3":     "Deployment governance gate: consecutive failures block new deployments automatically",
  "eep.enterpriseDeploy4":     "Live fingerprint verification: deployed checksum compared against build manifest",
  "eep.enterpriseDeploy5":     "Build artifact storage on global CDN with shareable preview URLs",
  "eep.aiAutomationTitle":     "AI-driven engineering automation",
  "eep.aiAutomation1":         "AI generates and validates all application code in the build pipeline",
  "eep.aiAutomation2":         "Automated test generation for UI, API, data, and workflow coverage",
  "eep.aiAutomation3":         "Autonomous repair engine: validation failures trigger targeted code repair before deployment",
  "eep.aiAutomation4":         "Intelligent error classification and root cause analysis",

  // batch 13 - lpp.*
  "lpp.describe":         "Describe what you want to build",
  "lpp.generate":         "Generate",
  "lpp.analyzing":        "Analyzing your prompt and planning the architecture...",
  "lpp.secStandard":      "Standard",
  "lpp.secElevated":      "Elevated",
  "lpp.secCritical":      "Critical",
  "lpp.securityBadge":    "Security: {level}",
  "lpp.confidenceMatch":  "{n}% match",
  "lpp.coreWorkflows":    "Core workflows:",
  "lpp.buildApp":         "Build This App",
  "lpp.tryAnother":       "Try Another",
  "lpp.footer":           "This is Nuvra's real architecture planner. Sign up to generate the full, working application.",
  "lpp.timeout":          "That took too long. Try again.",
  "lpp.previewError":     "Could not build a preview.",
  "lpp.prompt1":          "Build a project management SaaS with team collaboration",
  "lpp.prompt2":          "Create a fintech dashboard with real-time analytics",
  "lpp.prompt3":          "Design an e-commerce marketplace with seller tools",

  // batch 13 - alp.*
  "alp.title":   "Audit Trail",
  "alp.entries": "{n} entries",
  "alp.by":      "by {user}",

  // batch 14 - auth.*
  "auth.preparing": "Preparing your experience...",
  "auth.checking":  "Checking authentication...",

  // batch 14 - wm.*
  "wm.ariaLabel": "Built with Nuvra - open details",
  "wm.builtWith": "Built with",

  // batch 14 - agp.*
  "agp.endpoints":    "{n} endpoints",
  "agp.graphqlSchema": "GraphQL Schema",

  // batch 14 - sfd.*
  "sfd.empty": "Submit a prompt to generate system flows",

  // batch 14 - prc.*
  "prc.loadError":  "Could not load payout rail status. Reload to retry.",
  "prc.noRail":     "No payout rail configured. Add FLUTTERWAVE_SECRET_KEY or a Stripe key.",
  "prc.webhookUrl": "Flutterwave webhook URL",
  "prc.title":         "Payout rails",
  "prc.statusActive":  "Active",
  "prc.statusReady":   "Configured",
  "prc.statusMissing": "Not configured",
  "prc.webhookHint":   "Point your Flutterwave dashboard webhook here. It handles both payment charges and transfer (payout) settlement.",
  "prc.copyTitle":  "Copy",

  // batch 14 - rn.*
  "rn.aiCredits":    "+{n} AI Credits",
  "rn.upgradeTitle": "Upgrade to keep building",
  "rn.viewPlans":    "View Paid Plans",
  "rn.maybeLater":   "Maybe later",

  // batch 14 - sd.*
  "sd.creditsRemaining": "credits remaining",
  "sd.trial":            "Trial",
  "sd.creditsUsed":      "Trial credits used",
  "sd.upgrade":          "Upgrade to keep building",
  "sd.creditsCompact":   "{n} credits",

  // batch 14 - psb.*
  "psb.dismiss": "Dismiss",

  // batch 14 - dep.*
  "dep.title":         "Global AI System",
  "dep.ai":            "AI",
  "dep.fixing":        "Fixing...",
  "dep.forceFix":      "Force Fix",
  "dep.runtimeActive": "Runtime Active",

  // batch 14 - ip.*
  "ip.search":    "Search integrations...",
  "ip.configure": "Configure",
  "ip.connect":   "Connect",

  // batch 14 - wap.*
  "wap.totalRuns":      "Total Runs",
  "wap.avgSuccess":     "Avg Success",
  "wap.avgDuration":    "Avg Duration",
  "wap.activeWorkflows": "Active Workflows",
  "wap.runs":           "runs",
  "wap.success":        "success",
  "wap.avgMs":          "Avg {n}ms",
  "wap.fail":           "fail",
  "wap.last":           "Last: {date}",

  // batch 15 - acp.*
  "acp.heading":           "AI Project Context",
  "acp.routes":            "Routes",
  "acp.footer":            "This context is sent with every AI request so the assistant understands your full project.",
  "acp.labelPages":        "Pages",
  "acp.labelComponents":   "Components",
  "acp.labelDataModels":   "Data Models",
  "acp.labelWorkflows":    "Workflows",
  "acp.labelDesignTokens": "Design Tokens",

  // batch 15 - aip.*
  "aip.analyzing":        "Analyzing...",
  "aip.score":            "Architecture Score",
  "aip.systems":          "Systems ({n}/{m})",
  "aip.issues":           "Issues ({n})",
  "aip.recommendations":  "Recommendations ({n})",
  "aip.applyFix":         "Apply Fix",
  "aip.applied":          "Recommendation applied",
  "aip.failedApply":      "Failed to apply",
  "aip.autoFixFailed":    "Auto-fix failed",

  // batch 15 - cpp.*
  "cpp.heading":      "Code Preview",
  "cpp.generate":     "Generate",
  "cpp.materialize":  "Materialize",
  "cpp.emptyHeading": "No code generated yet",
  "cpp.emptyDesc":    "Click Generate for context-based code or Materialize for architecture-based files.",

  // batch 15 - ssp.*
  "ssp.platformStatus": "Platform Status",
  "ssp.projectHealth":  "Project Health",
  "ssp.backgroundTasks": "Background Tasks",
  "ssp.recentLogs":     "Recent Logs",
  "ssp.checkingHealth": "Checking service health...",
  "ssp.noIssues":       "No issues detected",
  "ssp.noLogs":         "No logs yet",
  "ssp.outage":         "Outage",
  "ssp.degraded":       "Degraded",
  "ssp.operational":    "Operational",

  // batch 15 - vhp.*
  "vhp.noProject":   "No project loaded",
  "vhp.heading":     "Version History ({n})",
  "vhp.noVersions":  "No versions yet",
  "vhp.restored":    "Version restored",
  "vhp.description": "{n} blocks · {m} pages",
  "vhp.scope":       "Saved in this browser only. Clearing site data or switching device loses it.",

  // batch 15 - ce.*
  "ce.loading": "Loading editor...",

  // batch 15 - aop.*
  "aop.idle":        "Idle",
  "aop.thinking":    "Analyzing",
  "aop.editing":     "Editing",
  "aop.reviewing":   "Reviewing",
  "aop.waiting":     "Awaiting input",
  "aop.heading":     "AI Orchestration",
  "aop.emptyTitle":  "No AI agents active",
  "aop.emptyDesc":   "AI agents appear here when you trigger generation or repair tasks",
  "aop.active":      "{n} active",
  "aop.inSession":   "{n} agents in session",
  "aop.idleSection": "Idle",

  // batch 15 - rnp.*
  "rnp.heading":      "Release History",
  "rnp.loading":      "Loading release history...",
  "rnp.empty":        "No release events yet",
  "rnp.failedLoad":   "Failed to load release notes",
  "rnp.events":       "{n} events",
  "rnp.deployment":   "deployment",
  "rnp.deployments":  "deployments",
  "rnp.build":        "build",
  "rnp.builds":       "builds",
  "rnp.submission":   "submission",
  "rnp.submissions":  "submissions",

  // batch 15 - sr.*
  "sr.title":       "Store Readiness",
  "sr.button":      "Store",
  "sr.ready":       "Ready",
  "sr.partial":     "Partial",
  "sr.notReady":    "Not Ready",
  "sr.submit":      "Submit",
  "sr.optimize":    "Optimize",
  "sr.optimizing":  "Optimizing...",
  "sr.highRisk":    "High risk - fix issues before submitting",
  "sr.fixIssues":   "Fix issues before submitting",

  // batch 15 - cm.*
  "cm.credentials":   "Credentials",
  "cm.noCredentials": "This connector requires no credentials.",
  "cm.required":      "required",
  "cm.alreadySet":    "already set",
  "cm.viewDocs":      "View documentation",
  "cm.update":        "Update",
  "cm.saveEnable":    "Save & Enable",
  "cm.test":          "Test",
  "cm.disconnect":    "Disconnect",

  // batch 15 - ldp.*
  "ldp.heading":           "Legal Documents",
  "ldp.privacyPolicy":     "Privacy Policy",
  "ldp.termsOfService":    "Terms of Service",
  "ldp.generatingPrivacy": "Generating privacy policy...",
  "ldp.generatingTerms":   "Generating terms of service...",
  "ldp.dataCollected":     "Data Collected",
  "ldp.thirdParty":        "Third-Party Services",
  "ldp.governing":         "Governing Capabilities",

  // batch 15 - lav.*
  "lav.phaseIdle":         "Waiting",
  "lav.phaseAnalyzing":    "Analyzing Prompt",
  "lav.phaseArchitecting": "Designing Architecture",
  "lav.phaseGenerating":   "Generating Components",
  "lav.phaseConnecting":   "Establishing Connections",
  "lav.phaseDeploying":    "Preparing Deployment",
  "lav.phaseComplete":     "Architecture Ready",
  "lav.configuration":     "Configuration",
  "lav.endpoints":         "Endpoints",
  "lav.tables":            "Tables",
  "lav.files":             "Files",
  "lav.building":          "building...",
  "lav.tableCount":        "{n} tables",
  "lav.routeCount":        "{n} routes",

  // batch 15 - awg.*
  "awg.heading":          "AI Workflow Generator",
  "awg.placeholder":      "Describe your workflow in natural language...",
  "awg.generating":       "Generating...",
  "awg.generateWorkflow": "Generate Workflow",
  "awg.quickPrompts":     "Quick Prompts",
  "awg.generated":        "Generated: {name}",
  "awg.nodesCreated":     "{n} nodes created",
  "awg.suggestion1":      "Send welcome email after user signup",
  "awg.suggestion2":      "Process payment and generate invoice",
  "awg.suggestion3":      "Daily analytics digest to admin team",
  "awg.suggestion4":      "Notify Slack on deployment failure",
  "awg.suggestion5":      "User approval flow for content publishing",

  // batch 16 - aarp.*
  "aarp.heading":        "AI Agents",
  "aarp.scan":           "Scan",
  "aarp.scanning":       "Scanning...",
  "aarp.scanFailed":     "Scan failed",
  "aarp.approved":       "Approved",
  "aarp.rejected":       "Rejected",
  "aarp.executed":       "Executed",
  "aarp.failed":         "Failed",
  "aarp.emptyTitle":     "No scan results yet",
  "aarp.emptyDesc":      "Run a scan to get AI-powered suggestions for refactoring, bugs, optimization, compliance, and migration.",
  "aarp.runFullScan":    "Run Full Scan",
  "aarp.scanningAgents": "Scanning with 5 agents...",

  /* Pre-run credit estimate: what an action costs, what you have, what would remain. */
  "acp.upgrade": "Upgrade",
  "acp.cost": "This uses {n} credits.",
  "acp.balance": "You have {n}.",
  "acp.remaining": "You would have {n} left.",
  "acp.short": "This needs {n} credits and you have {have}. Top up or upgrade to run it.",
  "acp.unknown": "Could not check your credit balance right now.",
  "acp.action.full_app_gen": "Build or edit an app",
  "acp.action.rebuild": "Targeted rebuild",
  "acp.action.page_gen": "Generate a page",
  "acp.action.component_gen": "Generate a component",
  "acp.action.targeted_fix_build": "Apply a targeted fix",
  "acp.action.auto_fix": "Auto-fix an issue",
  "acp.action.documentation": "Generate documentation",
  "acp.action.ai_completion": "Ask about your project",
  "acp.action.agent_execution": "Run an autonomous agent",
  "acp.action.image_generation": "Generate an image",
  "acp.action.music_generation": "Generate music",
  "acp.action.speech_generation": "Generate speech",
  "acp.action.voice_transcription": "Transcribe a voice note",
  "acp.action.deployment": "Deploy to a host",
  "acp.action.project_export": "Export project",
  "acp.action.aab": "Android app bundle (AAB) build",
  "acp.action.twa": "Android TWA build",
  "acp.action.ipa": "iOS app (IPA) build",
  "acp.action.refactor": "Refactor a component",
  "acp.action.build_plan_preview": "Preview a build plan",
  "acp.action.document_generated": "Generate a document (standard length)",
  "acp.action.document_chapter": "Generate a document chapter",
  "acp.action.document_render": "Render a document to PDF",
  "acp.action.document_image": "Generate a document illustration",
  "acp.action.document_export": "Export a document (EPUB/DOCX/ZIP)",
  "aarp.creditCost":     "Estimated credits:",
  "aarp.breakingChange": "Breaking change",
  "aarp.approve":        "Approve",
  "aarp.reject":         "Reject",
  "aarp.execute":        "Execute",
  "aarp.appliedSuccess": "Applied successfully",
  "aarp.allClear":       "All clear - no issues found.",

  // batch 16 - op.*
  "op.stageArchitect":    "Architecture",
  "op.stageSchema":       "Database",
  "op.stageBackend":      "Backend",
  "op.stageFrontend":     "Frontend",
  "op.stagePreview":      "Preview",
  "op.stageValidation":   "Validation",
  "op.stageDeployment":   "Deployment",
  "op.stagePublishing":   "Publishing",
  "op.captionArchitect":  "Intent to system blueprint",
  "op.captionSchema":     "Schema generation",
  "op.captionBackend":    "API routes and services",
  "op.captionFrontend":   "UI components and pages",
  "op.captionPreview":    "Live container",
  "op.captionValidation": "Auto-repair on failure",
  "op.captionDeployment": "Production push",
  "op.captionPublishing": "Store submission",

  // batch 16 - car.*
  "car.heading":      "Activity",
  "car.reconnecting": "Reconnecting to server...",
  "car.disconnected": "Disconnected - events paused",
  "car.emptyTitle":   "No activity yet",
  "car.emptyDesc":    "Events will appear here as collaborators work",

  // batch 16 - cpr.*
  "cpr.heading":       "Session",
  "cpr.collaborators": "Collaborators",
  "cpr.aiAgents":      "AI Agents",
  "cpr.emptyTitle":    "No active participants",
  "cpr.emptyWaiting":  "Waiting for collaborators",
  "cpr.notConnected":  "Not connected",

  // batch 16 - csp.*
  "csp.heading":              "Security",
  "csp.blockRate":            "Block rate",
  "csp.coordinatorLoading":   "Coordinator loading...",
  "csp.crossNodeSync":        "Cross-node sync",
  "csp.active":               "active",
  "csp.offline":              "offline",
  "csp.recentHighSeverity":   "Recent high-severity",
  "csp.operations":           "Operations",
  "csp.assessed":             "Assessed",
  "csp.blocked":              "Blocked",
  "csp.rateLimited":          "Rate limited",
  "csp.validationFail":       "Validation fail",
  "csp.quarantined":          "Quarantined",
  "csp.aiBlocked":            "AI blocked",
  "csp.loadingMetrics":       "Loading metrics...",
  "csp.detectorState":        "Detector state",
  "csp.users":                "users",
  "csp.agents":               "agents",
  "csp.replayTracker":        "Replay tracker",
  "csp.changeIds":            "changeIds",
  "csp.distribution":         "Distribution",

  // batch 16 - cpl.*
  "cpl.heading":              "Compliance",
  "cpl.passed":               "passed",
  "cpl.nBlockers":            "{n} blockers",
  "cpl.evaluating":           "Evaluating compliance...",
  "cpl.evaluated":            "Evaluated",
  "cpl.detectedCapabilities": "Detected Capabilities",
  "cpl.blockers":             "Blockers ({n})",
  "cpl.requiresApproval":     "Requires Approval ({n})",
  "cpl.warnings":             "Warnings ({n})",
  "cpl.allClear":             "No compliance blockers detected",
  "cpl.errSignIn":            "Sign in to evaluate compliance for this project.",
  "cpl.errBuildFirst":        "Build this project first to evaluate compliance.",
  "cpl.errGeneric":           "Compliance could not be evaluated right now. Try again shortly.",

  // batch 16 - smp.*
  "smp.heading":          "Store Metadata",
  "smp.tabAppStore":      "App Store",
  "smp.tabPlayStore":     "Play Store",
  "smp.tabSeo":           "SEO",
  "smp.error":            "error",
  "smp.errors":           "errors",
  "smp.warning":          "warning",
  "smp.warnings":         "warnings",
  "smp.ready":            "ready",
  "smp.loading":          "Building store metadata...",
  "smp.validationsPassed": "All validations passed",
  "smp.validation":       "Validation ({n})",
  "smp.fieldTitle":       "Title",
  "smp.fieldSubtitle":    "Subtitle",
  "smp.fieldKeywords":    "Keywords",
  "smp.fieldDescription": "Description",
  "smp.fieldShortDesc":   "Short Description",
  "smp.fieldFullDesc":    "Full Description",
  "smp.fieldMetaTitle":   "Meta Title",
  "smp.fieldMetaDesc":    "Meta Description",
  "smp.fieldOgDesc":      "OG Description",

  // batch 17 - ppc.*
  "ppc.heading":             "Nuvra Privacy Policy",
  "ppc.effectiveDate":       "Effective Date:",
  "ppc.lastUpdated":         "Last Updated:",
  "ppc.s1Title":             "1. Introduction",
  "ppc.s1Intro":             "Nuvra (\"Nuvra\", \"we\", \"our\", or \"us\") operates an AI-powered software creation platform that allows users to design, generate, deploy, and manage applications using artificial intelligence.",
  "ppc.s1Explains":          "This Privacy Policy explains how we collect, use, disclose, and protect information when you use:",
  "ppc.s1Li1":               "the Nuvra website",
  "ppc.s1Li2":               "the Nuvra software platform",
  "ppc.s1Li3":               "associated services and APIs",
  "ppc.s1Li4":               "any related applications, tools, or integrations",
  "ppc.s1Agree":             "By using Nuvra, you agree to the collection and use of information in accordance with this policy.",
  "ppc.s2Title":             "2. Information We Collect",
  "ppc.s2Intro":             "We collect information necessary to operate the platform and provide services.",
  "ppc.s2AccountTitle":      "2.1 Account Information",
  "ppc.s2AccountIntro":      "When you create an account, we may collect:",
  "ppc.s2AccountLi1":        "name",
  "ppc.s2AccountLi2":        "email address",
  "ppc.s2AccountLi3":        "account credentials",
  "ppc.s2AccountLi4":        "profile information",
  "ppc.s2AccountLi5":        "billing details",
  "ppc.s2AccountLi6":        "organization information",
  "ppc.s2PlatformTitle":     "2.2 Platform Usage Data",
  "ppc.s2PlatformIntro":     "When you use the Nuvra platform we may collect:",
  "ppc.s2PlatformLi1":       "project metadata",
  "ppc.s2PlatformLi2":       "build logs",
  "ppc.s2PlatformLi3":       "deployment information",
  "ppc.s2PlatformLi4":       "application configuration",
  "ppc.s2PlatformLi5":       "API requests",
  "ppc.s2PlatformLi6":       "feature usage analytics",
  "ppc.s2PlatformOutro":     "This helps us improve reliability and performance.",
  "ppc.s2AiTitle":           "2.3 AI Interaction Data",
  "ppc.s2AiIntro":           "When using AI features such as:",
  "ppc.s2AiLi1":             "Software Builder",
  "ppc.s2AiLi2":             "Autonomous Build Mode",
  "ppc.s2AiLi3":             "AI Assistant",
  "ppc.s2AiLi4":             "Code Generation",
  "ppc.s2AiLi5":             "Architecture Planning",
  "ppc.s2AiProcess":         "we may process:",
  "ppc.s2AiPLi1":            "prompts",
  "ppc.s2AiPLi2":            "generated outputs",
  "ppc.s2AiPLi3":            "build instructions",
  "ppc.s2AiPLi4":            "system feedback",
  "ppc.s2AiOutro":           "This data helps Nuvra generate and improve applications.",
  "ppc.s2TechTitle":         "2.4 Technical Data",
  "ppc.s2TechIntro":         "We automatically collect certain technical information:",
  "ppc.s2TechLi1":           "IP address",
  "ppc.s2TechLi2":           "browser type",
  "ppc.s2TechLi3":           "device type",
  "ppc.s2TechLi4":           "operating system",
  "ppc.s2TechLi5":           "performance metrics",
  "ppc.s2TechLi6":           "diagnostic logs",
  "ppc.s3Title":             "3. How We Use Information",
  "ppc.s3Intro":             "We use collected information to:",
  "ppc.s3Li1":               "operate and maintain the Nuvra platform",
  "ppc.s3Li2":               "generate software applications using AI",
  "ppc.s3Li3":               "provide infrastructure and deployment services",
  "ppc.s3Li4":               "process payments and subscriptions",
  "ppc.s3Li5":               "improve platform functionality",
  "ppc.s3Li6":               "ensure system security and reliability",
  "ppc.s3Li7":               "prevent fraud or abuse",
  "ppc.s3Li8":               "comply with legal obligations",
  "ppc.s3NoSell":            "We do not sell personal data to third parties.",
  "ppc.s4Title":             "4. AI-Generated Content and Projects",
  "ppc.s4Intro":             "Applications generated using Nuvra may include:",
  "ppc.s4Li1":               "frontend interfaces",
  "ppc.s4Li2":               "backend APIs",
  "ppc.s4Li3":               "databases",
  "ppc.s4Li4":               "infrastructure configurations",
  "ppc.s4Ownership":         "Ownership of generated project code belongs to the user who created the project, except where restricted by applicable law or third-party licensing.",
  "ppc.s4Process":           "Nuvra may process project data solely to provide platform functionality and improve services.",
  "ppc.s5Title":             "5. Data Storage and Security",
  "ppc.s5Intro":             "We implement industry-standard security measures including:",
  "ppc.s5Li1":               "encrypted data transmission",
  "ppc.s5Li2":               "access controls",
  "ppc.s5Li3":               "authentication protection",
  "ppc.s5Li4":               "infrastructure monitoring",
  "ppc.s5Li5":               "vulnerability scanning",
  "ppc.s5Caveat":            "However, no internet system can guarantee absolute security.",
  "ppc.s6Title":             "6. Data Retention",
  "ppc.s6Intro":             "We retain data only as long as necessary to:",
  "ppc.s6Li1":               "provide services",
  "ppc.s6Li2":               "maintain system integrity",
  "ppc.s6Li3":               "comply with legal obligations",
  "ppc.s6Request":           "Users may request deletion of their accounts and associated data.",
  "ppc.s7Title":             "7. International Data Transfers",
  "ppc.s7Intro":             "Nuvra may process data in multiple jurisdictions.",
  "ppc.s7SafeguardsIntro":   "Where required, we use safeguards such as:",
  "ppc.s7Li1":               "Standard Contractual Clauses",
  "ppc.s7Li2":               "lawful data transfer mechanisms",
  "ppc.s7Li3":               "regional hosting options",
  "ppc.s7Outro":             "to protect user information across borders.",
  "ppc.s8Title":             "8. User Rights",
  "ppc.s8Intro":             "Depending on your jurisdiction, you may have rights including:",
  "ppc.s8Li1":               "access to your data",
  "ppc.s8Li2":               "correction of inaccurate information",
  "ppc.s8Li3":               "deletion of personal information",
  "ppc.s8Li4":               "restriction of processing",
  "ppc.s8Li5":               "data portability",
  "ppc.s8Li6":               "withdrawal of consent",
  "ppc.s8Contact":           "Requests may be submitted through the platform or by contacting support.",
  "ppc.s9Title":             "9. Cookies and Tracking Technologies",
  "ppc.s9Intro":             "Nuvra may use cookies and similar technologies to:",
  "ppc.s9Li1":               "maintain login sessions",
  "ppc.s9Li2":               "improve platform functionality",
  "ppc.s9Li3":               "analyze usage patterns",
  "ppc.s9Control":           "Users may control cookies through browser settings.",
  "ppc.s10Title":            "10. Third-Party Services",
  "ppc.s10Intro":            "Nuvra may integrate with third-party services including:",
  "ppc.s10Li1":              "cloud infrastructure providers",
  "ppc.s10Li2":              "payment gateways",
  "ppc.s10Li3":              "AI model providers",
  "ppc.s10Li4":              "developer tools",
  "ppc.s10Note":             "These services operate under their own privacy policies.",
  "ppc.s11Title":            "11. Children's Privacy",
  "ppc.s11NotFor":           "Nuvra services are not intended for children under 13 years of age.",
  "ppc.s11NoCollect":        "We do not knowingly collect personal information from children.",
  "ppc.s12Title":            "12. Changes to This Privacy Policy",
  "ppc.s12Update":           "We may update this policy from time to time to reflect legal or operational changes.",
  "ppc.s12Posted":           "Updated versions will be posted with a revised effective date.",
  "ppc.s13Title":            "13. Contact Information",
  "ppc.s13Intro":            "For privacy inquiries or requests:",
  "ppc.s13Email":            "Email:",
  "ppc.s13EmailLink":        "hello@nuvrahq.com",

  // batch 18 - cmp.*, of.*, sg.*
  "cmp.placeholder":      "Describe the product to engineer...",
  "cmp.submitLabel":      "Send to engineering",
  "cmp.ariaLabel":        "Message composer",
  "cmp.stopEngineering":  "Stop engineering",
  "cmp.engineering":      "Engineering",
  "of.nodeAi":            "AI Engine",
  "of.nodeCodegen":       "Code Gen",
  "of.nodeArch":          "Architect",
  "of.nodeFrontend":      "Frontend",
  "of.nodeApi":           "API Gateway",
  "of.nodeDb":            "Database",
  "of.nodeDeploy":        "Deploy",
  "of.nodeAuth":          "Auth",
  "of.nodeMonitor":       "Monitor",
  "of.nucleus":           "NUCLEUS",
  "sg.fields":            "fields",
  "sg.rels":              "rels",

  // Extensions page - error states (replaces raw API error codes)
  "ext.error.unauthenticated": "Sign in to browse and install extensions.",
  "ext.error.forbidden":       "You don't have permission to manage extensions for this project.",
  "ext.error.notFound":        "Extensions registry not available. Try again later.",
  "ext.error.server":          "Extensions service unavailable. Try again in a moment.",

  // Extensions page - no-project state (enhanced)
  "ext.noProjectTitle": "Open this page from a project to install and manage its extensions.",
  "ext.noProjectHint":  "You can browse the registry below.",

  // Webhooks page
  "wh.title":             "Webhooks",
  "wh.subtitle":          "Send real-time HTTP notifications to your endpoints when events occur in your workspace.",
  "wh.endpoints":         "Endpoints",
  "wh.endpointsEmpty":    "No webhook endpoints configured. Add one to start receiving events.",
  "wh.add":               "Add endpoint",
  "wh.url":               "Endpoint URL",
  "wh.urlPlaceholder":    "https://your-server.com/webhook",
  "wh.name":              "Name",
  "wh.namePlaceholder":   "My webhook",
  "wh.events":            "Event subscriptions",
  "wh.create":            "Create endpoint",
  "wh.cancel":            "Cancel",
  "wh.creating":          "Creating",
  "wh.delete":            "Delete",
  "wh.deleting":          "Deleting",
  "wh.enable":            "Enable",
  "wh.disable":           "Disable",
  "wh.enabled":           "Active",
  "wh.disabled":          "Paused",
  "wh.deliveries":        "Delivery history",
  "wh.deliveriesEmpty":   "No deliveries yet for this endpoint.",
  "wh.deliverySuccess":   "Delivered",
  "wh.deliveryFailed":    "Failed",
  "wh.deliveryStatus":    "Status",
  "wh.deliveryAt":        "Delivered at",
  "wh.deliveryEvent":     "Event",
  "wh.secretTitle":       "Signing secret",
  "wh.secretDesc":        "Use this secret to verify webhook signatures. It is shown only once.",
  "wh.secretCopy":        "Copy secret",
  "wh.secretCopied":      "Copied",
  "wh.secretClose":       "I've saved the secret",
  "wh.testSend":          "Send test event",
  "wh.testing":           "Sending",
  "wh.testSent":          "Test event sent",
  "wh.loadError":         "Could not load webhook endpoints.",
  "wh.createError":       "Could not create endpoint.",
  "wh.deleteError":       "Could not delete endpoint.",
  "wh.noOrg":             "Webhooks require a workspace. Create or join a workspace to manage webhook endpoints.",
  "wh.eventTypes.build.completed":   "build.completed",
  "wh.eventTypes.build.failed":      "build.failed",
  "wh.eventTypes.deploy.completed":  "deploy.completed",
  "wh.eventTypes.deploy.failed":     "deploy.failed",
  "wh.eventTypes.project.created":   "project.created",
  "wh.eventTypes.project.deleted":   "project.deleted",
  "wh.eventTypes.member.joined":     "member.joined",
  "wh.eventTypes.member.removed":    "member.removed",
  "wh.loading":           "Loading endpoints",

  // Team page
  "team.title":              "Team",
  "team.subtitle":           "Manage workspace members, roles, and invitations.",
  "team.members":            "Members",
  "team.membersEmpty":       "No team members yet.",
  "team.invite":             "Invite member",
  "team.inviteEmail":        "Email address",
  "team.inviteRole":         "Role",
  "team.inviteSend":         "Send invite",
  "team.inviting":           "Sending",
  "team.invitations":        "Pending invitations",
  "team.invitationsEmpty":   "No pending invitations.",
  "team.role.admin":         "Admin",
  "team.role.member":        "Member",
  "team.role.viewer":        "Viewer",
  "team.role.billing":       "Billing",
  "team.remove":             "Remove",
  "team.removing":           "Removing",
  "team.cancel":             "Cancel",
  "team.revokeInvite":       "Revoke",
  "team.loadError":          "Could not load team members.",
  "team.inviteError":        "Could not send invitation.",
  "team.removeError":        "Could not remove member.",
  "team.noOrg":              "You do not have a workspace yet.",
  "team.createHint":         "Name your workspace to start inviting teammates.",
  "team.workspaceNamePlaceholder": "Workspace name",
  "team.createWorkspace":    "Create workspace",
  "team.workspaceNameShort": "Workspace name must be at least 2 characters.",
  "team.createError":        "Could not create the workspace. Check your connection and try again.",
  "team.retry":              "Retry",
  "team.you":                "You",
  "team.owner":              "Owner",
  "team.joinedAt":           "Joined",
  "team.loading":            "Loading team",
  "team.inviteSuccess":      "Invitation sent.",
  "team.removeSuccess":      "Member removed.",
  "team.revokeSuccess":      "Invitation revoked.",
  "team.seats":              "Seats used",

  // Security panel - sessions + history
  "settings.security.sessions.title":    "Active sessions",
  "settings.security.sessions.empty":    "No other active sessions.",
  "settings.security.sessions.current":  "Current session",
  "settings.security.sessions.revoke":   "Sign out",
  "settings.security.sessions.revoking": "Signing out",
  "settings.security.sessions.browser":  "Browser",
  "settings.security.sessions.device":   "Device",
  "settings.security.sessions.lastSeen": "Last seen",
  "settings.security.sessions.location": "Location",
  "settings.security.devices.title":     "Trusted devices",
  "settings.passkeys.title":       "Passkeys & biometrics",
  "settings.security.posture.title":    "Security posture",
  "settings.security.posture.mfa":      "Two-factor",
  "settings.security.posture.on":       "On",
  "settings.security.posture.off":      "Off",
  "settings.security.posture.passkeys": "Passkeys",
  "settings.security.posture.devices":  "Active devices",
  "settings.security.posture.signIns":  "Sign-ins (30d)",
  "settings.security.posture.warnings": "Warnings (30d)",
  "settings.security.posture.window":   "Aggregated from your real login events, devices, passkeys, and security timeline over the last 30 days.",
  "settings.passkeys.intro":       "Sign-in verification with a passkey stored on your device. Face ID, Windows Hello, and fingerprint sensors enroll through the same prompt when your device supports them.",
  "settings.passkeys.unsupported": "This browser does not support passkeys.",
  "settings.passkeys.namePlaceholder": "Name this passkey (e.g. My phone)",
  "settings.passkeys.add":         "Add passkey",
  "settings.passkeys.test":        "Verify now",
  "settings.passkeys.added":       "Passkey added.",
  "settings.passkeys.verified":    "Identity verified with your passkey.",
  "settings.passkeys.failed":      "The passkey ceremony did not complete.",
  "settings.passkeys.empty":       "No passkeys yet. Add one to verify sensitive actions with your device.",
  "settings.passkeys.unnamed":     "Unnamed passkey",
  "settings.passkeys.addedOn":     "Added",
  "settings.passkeys.lastUsed":    "last used",
  "settings.passkeys.remove":      "Remove passkey",
  "settings.security.devices.intro":     "Devices that have signed in to your account. Trust the ones you own; revoke anything you do not recognize.",
  "settings.security.devices.empty":     "No devices recorded yet. Devices appear here after your next sign-in.",
  "settings.security.devices.trusted":   "Trusted",
  "settings.security.devices.revoked":   "Revoked",
  "settings.security.devices.lastSeen":  "Last seen",
  "settings.security.devices.trust":     "Trust",
  "settings.security.devices.untrust":   "Untrust",
  "settings.security.devices.revoke":    "Revoke",
  "settings.security.timeline.title":    "Security events",
  "settings.security.timeline.empty":    "No security events recorded yet.",
  "settings.security.strength.label":    "Password strength",
  "settings.security.strength.weak":     "Weak",
  "settings.security.strength.fair":     "Fair",
  "settings.security.strength.strong":   "Strong",
  "settings.security.strength.very_strong": "Very strong",

  // Settings - Security: connected providers
  "settings.security.providers.title":   "Sign-in methods",
  "settings.security.providers.desc":    "Manage how you sign in. At least one method must remain active.",
  "settings.security.providers.linked":  "Linked",
  "settings.security.providers.unlink":  "Unlink",
  "settings.security.providers.unlinking": "Unlinking",
  "settings.security.providers.unlinkError": "Could not unlink. Keep at least one sign-in method active.",
  "settings.security.providers.linkedAt": "Linked",
  "settings.security.providers.addGoogle": "Connect Google",
  "settings.security.providers.addGithub": "Connect GitHub",

  // Settings - Security: email verification
  "settings.security.email.title":       "Email address",
  "settings.security.email.verified":    "Verified",
  "settings.security.email.unverified":  "Unverified",
  "settings.security.email.resend":      "Resend verification",
  "settings.security.email.resent":      "Verification email sent.",
  "settings.security.email.resentError": "Could not send verification email.",

  // Settings - Security: recovery email
  "settings.security.recovery.title":   "Recovery email",
  "settings.security.recovery.desc":    "Backup email to recover your account if you lose access to your primary address.",
  "settings.security.recovery.placeholder": "backup@example.com",
  "settings.security.recovery.save":    "Save recovery email",
  "settings.security.recovery.saving":  "Saving",
  "settings.security.recovery.saved":   "Recovery email saved.",
  "settings.security.recovery.error":   "Could not save recovery email.",

  // Settings - Security: recommendations
  "settings.security.recs.title":       "Security recommendations",
  "settings.security.recs.mfa":         "Two-factor authentication enabled",
  "settings.security.recs.email":       "Primary email verified",
  "settings.security.recs.recovery":    "Recovery email configured",
  "settings.security.recs.allGood":     "Your account meets all security recommendations.",
  "settings.security.recs.fix":         "Fix",

  // Privacy panel - consent controls
  "settings.privacy.controls.title":     "Privacy & data controls",
  "settings.privacy.controls.subtitle":  "Control optional data use, communications, and diagnostics. Changes are saved to your Nuvra account when account storage is available.",
  "settings.privacy.ai.title":           "AI improvement data",
  "settings.privacy.ai.desc":            "Allow Nuvra to use anonymized build data to improve the AI engine.",
  "settings.privacy.analytics.title":    "Product analytics",
  "settings.privacy.analytics.desc":     "Allow Nuvra to collect anonymized usage analytics to improve the product.",
  "settings.privacy.marketing.title":    "Marketing communications",
  "settings.privacy.marketing.desc":     "Receive product updates, tips, and announcements by email.",
  "settings.privacy.crash.title":        "Crash and error reporting",
  "settings.privacy.crash.desc":         "Send error reports automatically to help fix bugs faster.",
  "settings.privacy.saved":             "Privacy preferences saved.",
  "settings.privacy.saving":            "Saving",
  "settings.privacy.saveError":         "Could not save preferences. Try again.",
  "settings.privacy.export.title":       "Personal data export",
  "settings.privacy.export.desc":        "Use Nuvra’s Data Export center to download the data available to your account.",
  "settings.privacy.export.btn":         "Open Data Export",
  "settings.privacy.regional.title":     "Privacy frameworks",
  "settings.privacy.regional.gdpr":      "GDPR (EU/EEA)",
  "settings.privacy.regional.ccpa":      "CCPA (California)",
  "settings.privacy.regional.ndpr":      "NDPR (Nigeria)",
  "settings.privacy.policyLink":         "View full privacy policy",

  // Appearance panel
  "settings.appearance.title":           "Appearance",
  "settings.appearance.theme.title":     "Theme",
  "settings.appearance.theme.dark":      "Dark",
  "settings.appearance.theme.dark.desc": "Nuvra's native dark palette. Best for extended builds.",
  "settings.appearance.theme.light":     "Light",
  "settings.appearance.theme.light.desc": "A bright palette for well-lit spaces.",
  "settings.appearance.theme.system":    "System",
  "settings.appearance.theme.system.desc": "Follows your OS light/dark preference.",
  "settings.appearance.density.title":   "Interface density",
  "settings.appearance.density.default": "Default",
  "settings.appearance.density.default.desc": "Standard spacing for most screens.",
  "settings.appearance.density.compact": "Compact",
  "settings.appearance.density.compact.desc": "More information, less padding.",
  "settings.appearance.saved":           "Appearance updated.",
  // Deployment Center (MANDATE 15)
  "dc.providers":          "Choose where to deploy",
  "dc.soon":               "Soon",
  "dc.preview":            "Preview",
  "dc.ship.liveAt":        "Live at",
  "dc.ship.notLive":       "The deployment did not go live.",
  "dc.ship.signed":        "Signed via {provider}.",
  "dc.ship.unsigned":      "Not code-signed (not required for web).",
  "dc.ship.rollbackOk":    "Rollback available.",
  "dc.railway.title":      "Railway",
  "dc.railway.connected":  "Connected as {account}",
  "dc.railway.disconnect": "Disconnect",
  "dc.railway.token":      "Railway API token",
  "dc.railway.service":    "Service ID (optional)",
  "dc.railway.env":        "Environment ID (optional)",
  "dc.railway.connect":    "Connect Railway",
  "dc.cloud.title":        "Nuvra Cloud",
  "dc.cloud.prod":         "Production",
  "dc.cloud.live":         "live",
  "dc.cloud.notLive":      "not live",
  "dc.cloud.empty":        "No deployments yet. Ship your latest build to see it here.",
  "dc.cloud.active":       "active",
  "dc.cloud.rollback":     "Roll back to this deployment",
  "dc.cloud.restart":      "Restart this deployment",
  "dc.cloud.logs":         "Deployment logs",
  "dc.env.title":          "Environment variables",
  "dc.env.desc":           "Encrypted at rest. Values are never shown after saving.",
  "dc.env.remove":         "Remove",
  "dc.env.name":           "NAME",
  "dc.env.value":          "value",
  "dc.env.add":            "Add",
  "dc.error.generic":      "Something went wrong. Try again.",
  "dc.history.title":      "Deployment history",
  "dc.history.charged":    "{acu} credits",
  "dc.history.note":       "Every deploy across all providers, with the credits it used. Failed deploys are refunded.",
  "dc.history.status.failed":              "Failed (refunded)",
  "dc.history.status.blocked_entitlement": "Plan limit reached",
  "dc.history.status.blocked_acu":         "Not enough credits",
  "dc.history.status.refused":             "Refused",

  // Execution authorization: what Nuvra is permitted to spend and change on this project.
  "auth.title":            "Execution authorization",
  "auth.subtitle":         "Nuvra cannot spend your credits or change this project without a recorded authorization for that exact operation. This is what is authorized right now, what it has actually used, and how to stop it.",
  "auth.mode.enforce":     "Enforcing",
  "auth.mode.observe":     "Observing only",
  "auth.ledger.durable":   "Durable ledger",
  "auth.ledger.memory":    "In-memory ledger (this process only)",
  "auth.active.title":     "Authorized now",
  "auth.active.none":      "Nothing is authorized to spend on this project. Starting a build, deploy or analysis creates an authorization for that operation.",
  "auth.history.title":    "Recent authorizations",
  "auth.history.none":     "No authorizations recorded for this project yet.",
  "auth.class.analysis":   "AI analysis",
  "auth.class.execution":  "Build and change",
  "auth.class.deployment": "Deploy",
  "auth.class.rollback":   "Rollback",
  "auth.class.destructive": "Destructive change",
  "auth.badge.production": "Production approved",
  "auth.badge.destructive": "Destructive approved",
  "auth.remaining":        "{remaining} of {total} credits left",
  "auth.used":             "{used}/{total} credits",
  "auth.revoke":           "Revoke",
  "auth.draws.none":       "Nothing has been drawn against this authorization.",
  "auth.draw.open":        "{acu} held",
  "auth.draw.settled":     "{acu} used of {held} held",
  "auth.draw.released":    "{acu} returned",
  "auth.error.load":       "Could not load authorizations.",
  "auth.error.revoke":     "Could not revoke that authorization.",

  // Nuvra Support (customer surface)
  "support.title":                "Nuvra Support",
  "support.subtitle":             "Describe what is going wrong. Nuvra Support investigates it, resolves what it safely can, and brings a support engineer into the same conversation when it cannot.",
  "support.newConversation":      "New conversation",
  "support.startTopic":           "Start a conversation about {topic}",
  "support.subjectPlaceholder":   "What is this about?",
  "support.describePlaceholder":  "Describe the issue. What were you doing, what did you expect, and what happened instead?",
  "support.start":                "Start conversation",
  "support.starting":             "Starting",
  "support.cancel":               "Cancel",
  "support.emptyTitle":           "How can we help?",
  "support.emptyBody":            "Start a conversation and Nuvra Support will look into it. If it cannot resolve the issue safely, a support engineer joins the same conversation and can see everything you have already said.",
  "support.loading":              "Loading",
  "support.composerPlaceholder":  "Message Nuvra Support…",
  "support.send":                 "Send",
  "support.sending":              "Sending",
  "support.working":              "Nuvra Support is looking into this",
  "support.sendFailed":           "Not sent",
  "support.retry":                "Retry",
  "support.reconnecting":         "Reconnecting",
  "support.backToList":           "Back to conversations",
  "support.attachFiles":          "Attach files",
  "support.attachRejected.type":    "that file type is not accepted. Send a PNG, JPEG, GIF, WebP or PDF.",
  "support.attachRejected.size":    "that file is larger than 10 MB.",
  "support.attachRejected.session": "your session expired. Sign in again to attach files.",
  "support.attachRejected.failed":  "that file could not be attached. Try again.",
  "support.removeAttachment":     "Remove",
  "support.uploading":            "Uploading",
  "support.attachmentLimit":      "You can attach up to 5 files to a message.",
  "support.attachmentUnavailable": "could not be loaded",
  "support.withEngineer":         "A support engineer is on this",
  "support.listWithEngineer":     "with a support engineer",
  "support.resolvedNote":         "This conversation is resolved. Reply below any time to reopen it.",
  "support.openFailed":           "Could not start the conversation",
  "support.status.open":          "Open",
  "support.status.answered":      "Answered",
  "support.status.escalated":     "With an engineer",
  "support.status.resolved":      "Resolved",
  "nav.item.support":             "Support",
  "support.homeHeading": "Engineering support that understands your workspace.",
  "support.homeBody": "Tell Nuvra what you are trying to do or what is not working. We investigate, resolve what can be resolved safely, and bring in a support engineer when human help is needed.",
  "support.topicsHeading": "Common topics",
  "support.topic.builds": "Builds",
  "support.topic.deployments": "Deployments",
  "support.topic.projects": "Projects",
  "support.topic.billing": "Billing",
  "support.topic.account": "Account",
  "support.starter.account": "I need help with my account: ",
  "support.starter.billing": "I have a question about billing: ",
  "support.starter.projects": "I am having a problem with a project: ",
  "support.starter.deployments": "I am having a problem with a deployment: ",
  "support.starter.builds": "I am having a problem with a build: ",
  "support.searchPlaceholder": "Search conversations",
  "support.filterLabel": "Filter conversations",
  "support.filter.all": "All",
  "support.filter.open": "Open",
  "support.filter.waiting": "Waiting for you",
  "support.filter.resolved": "Resolved",
  "support.state.open": "Open",
  "support.state.waiting": "Waiting for you",
  "support.state.with_engineer": "With an engineer",
  "support.state.resolved": "Resolved",
  "support.listEmpty": "No conversations yet.",
  "support.listNoMatch": "No conversations match that.",
  "support.untitled": "Support request",
  "support.markResolved": "Mark resolved",
  "support.threadLoadFailed": "We could not load this conversation.",
  "support.tryAgain": "Try again",
  "support.withEngineerNote": "A Nuvra support engineer is on this conversation. They can see everything you have already shared, so you do not need to repeat yourself.",
  "support.category.how_to": "How to",
  "support.category.bug": "Issue",
  "support.category.billing": "Billing",
  "support.category.account": "Account",
  "support.category.policy_appeal": "Policy",
  "support.category.payout": "Payout",
  "support.category.dispute": "Dispute",
  "support.voice.you":            "You",
  "support.voice.ai":             "Nuvra Support",
  "support.voice.engineer":       "Support Engineer",
  "support.voice.system":         "Nuvra",
  "help.supportPitch":            "Open a Nuvra Support conversation. Support investigates the issue and brings a support engineer into the same conversation when it cannot resolve it safely.",
  "help.openSupport":             "Open Nuvra Support",

  // Project infrastructure panel (what Nuvra can actually do with connected services).
  // The server sends stable ids and enums; every sentence below is the client's own copy,
  // which is why the panel needs no fallback to the server's English.
  "infra.triggerLabel": "Infrastructure",
  "infra.triggerTitle": "What Nuvra can do with your connected services",
  "infra.triggerAria": "Project infrastructure",
  "infra.heading": "Infrastructure",
  "infra.unreadable": "Nuvra could not read this project's connections just now. Nothing has changed.",
  "infra.needsApproval": "(asks you first)",
  "infra.ready": "This project has somewhere to build into.",
  "infra.notReady": "Connect a repository to give this project somewhere to build into.",
  "infra.autonomy.assisted": "Nuvra asks before it changes anything.",
  "infra.autonomy.autonomous": "Nuvra works on its own, including on your live product.",
  "infra.autonomy.autonomous_with_production_approval": "Nuvra works on its own, and asks before it changes your live product.",
  "infra.cap.github.create_repo": "Create a new repository",
  "infra.cap.github.read_code": "Read your source code",
  "infra.cap.github.branch": "Create branches",
  "infra.cap.github.push": "Commit and push changes",
  "infra.cap.github.pull_request": "Open pull requests",
  "infra.cap.github.ci": "Run CI and read the results",
  "infra.cap.supabase.inspect": "Inspect your schema",
  "infra.cap.supabase.migrate": "Write migrations",
  "infra.cap.supabase.apply": "Apply changes to your live database",
  "infra.cap.vercel.preview": "Create preview deployments",
  "infra.cap.vercel.production": "Deploy to your live site",
  "infra.cap.vercel.logs": "Read deployment logs",
  "infra.cap.railway.deploy": "Deploy your backend services",
  "infra.cap.railway.logs": "Read service logs",
  "infra.state.github.not_configured": "GitHub is not available on this Nuvra deployment yet.",
  "infra.state.github.upgrade_required": "Upgrade your plan to let Nuvra work with your repositories.",
  "infra.state.github.not_connected": "Connect GitHub so Nuvra can store and update your code.",
  "infra.state.github.no_resource_selected": "GitHub is connected. Choose a repository for this project, or let Nuvra create one.",
  "infra.state.github.paused": "Nuvra is paused for this repository. Resume it when you want Nuvra to continue.",
  "infra.state.github.connected": "Nuvra is working in this repository.",
  "infra.state.supabase.not_connected": "Connect Supabase so Nuvra can manage your database.",
  "infra.state.supabase.paused": "Nuvra is paused for this database. Resume it when you want Nuvra to continue.",
  "infra.state.supabase.connected": "Nuvra is working with this database.",
  "infra.state.vercel.not_connected": "Connect Vercel so Nuvra can deploy your front end.",
  "infra.state.vercel.paused": "Nuvra is paused for this site. Resume it when you want Nuvra to continue.",
  "infra.state.vercel.connected": "Nuvra is deploying to this site.",
  "infra.state.railway.not_connected": "Connect Railway so Nuvra can run your backend services.",
  "infra.state.railway.paused": "Nuvra is paused for this service. Resume it when you want Nuvra to continue.",
  "infra.state.railway.connected": "Nuvra is running this service.",
  "settings.privacy.loading": "Loading privacy preferences...",
  "settings.privacy.synced": "Synced to your Nuvra account",
  "settings.privacy.localFallback": "Saved on this device; account sync is unavailable",
  "settings.privacy.notSynced": "Account sync status unavailable",
  "settings.privacy.data.title": "Your data",
  "settings.privacy.data.desc": "Review the data Nuvra makes available to your account and use the dedicated export controls when you need a copy.",
  "settings.privacy.regional.subtitle": "Privacy requirements vary by location. These references help you understand the frameworks that may apply to your use of Nuvra.",
  "settings.privacy.rightsHint": "Your privacy choices can be changed at any time",
  "settings.systemStatus.subtitle": "Live health signals from Nuvra’s API, AI providers, build infrastructure, database, and incident service.",
  "settings.systemStatus.refresh": "Refresh",
  "settings.systemStatus.monitoring": "Live monitoring enabled",
  "settings.systemStatus.lastChecked": "Checked {time}",
  "settings.systemStatus.incidentsSubtitle": "Active incidents are updated in real time when available.",
  "settings.systemStatus.noIncidentsHint": "No active incidents are currently recorded by Nuvra.",
  "settings.systemStatus.recentIncidents": "Recent incidents",
  "settings.systemStatus.pageChecked": "Page checked {time}",
  "settings.systemStatus.autoRefresh": "Automatically refreshes every 30 seconds",
  "settings.privacy.regional.gdpr.desc": "EU/EEA privacy rights and data-protection framework.",
  "settings.privacy.regional.ccpa.desc": "California privacy rights and consumer-data controls.",
  "settings.privacy.regional.ndpr.desc": "Nigeria privacy rights and data-protection framework.",
  // Phase 6 build journey spine
  "bj.heading": "Build journey",
  "bj.understand": "Understand",
  "bj.plan": "Plan",
  "bj.build": "Build",
  "bj.verify": "Verify",
  "bj.live": "Live",
  "bj.complete": "Complete",
  "bj.active": "In progress",
  "bj.failed": "Needs attention",
  "bj.pending": "Not started",
  "bj.liveHint": "Live status is based on work Nuvra has actually reported, not elapsed time.",

  "landing.showcase.eyebrow": "SEE NUVRA BUILD",
  "landing.showcase.h2a": "From idea",
  "landing.showcase.h2b": "to production",
  "landing.showcase.desc": "Watch the engineering path Nuvra is designed to execute: resolve intent, plan the system, build in stages, verify the result, and move the product toward production.",
  "landing.showcase.cta": "Start building your product",
  "landing.showcase.step1.label": "Start with an idea",
  "landing.showcase.step1.title": "Describe the system you want.",
  "landing.showcase.step1.desc": "Start with plain language. Nuvra's public planner turns the idea into a concrete architecture preview before an authenticated build begins.",
  "landing.showcase.step2.label": "Architecture first",
  "landing.showcase.step2.title": "Turn intent into a system blueprint.",
  "landing.showcase.step2.desc": "Requirements, services, data, interfaces, roles, and security boundaries are structured before code execution moves forward.",
  "landing.showcase.step3.label": "Watch it get built",
  "landing.showcase.step3.title": "Build the layers in a controlled sequence.",
  "landing.showcase.step3.desc": "Frontend, backend, data, and security work advance through bounded stages instead of one opaque generation pass.",
  "landing.showcase.step4.label": "Verify and repair",
  "landing.showcase.step4.title": "Validation stays inside the loop.",
  "landing.showcase.step4.desc": "Checks provide evidence for the next stage. When a supported failure is detected, targeted repair can close the loop before progression.",
  "landing.showcase.step5.label": "Move toward production",
  "landing.showcase.step5.title": "Preview, govern, and ship.",
  "landing.showcase.step5.desc": "The end state is not a code dump. Nuvra is designed to carry the product through preview, deployment, monitoring, and governed production control.",
};